<template>
  <AppShell
    active="map"
    :workspace="workspace"
    title="Opportunity map"
    subtitle="Inspect acquisition sites with parcel, demographic, power, gas, and environmental context."
    max-width-class="max-w-none"
    data-testid="opportunity-map-page"
  >
    <template #actions>
      <a href="/opportunities" class="inline-flex h-10 items-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover">
        <Plus class="size-4" aria-hidden="true" />
        Add plotted opportunity
      </a>
    </template>

    <div v-if="notice" class="mb-4 rounded-md border border-land-border bg-land-primary-soft px-4 py-3 text-sm font-medium text-land-primary" role="status">{{ notice }}</div>
    <div v-if="errors.length > 0" class="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
      <p v-for="error in errors" :key="error">{{ error }}</p>
    </div>

    <section class="grid items-start gap-4" :class="mapGridClass">
      <aside v-if="!leftMapPanelCollapsed" class="order-2 space-y-4 min-[1500px]:order-1 min-[1500px]:sticky min-[1500px]:top-20 min-[1500px]:max-h-[calc(100vh-6rem)] min-[1500px]:overflow-auto min-[1500px]:pr-1" data-testid="map-left-panel">
        <section class="rounded-md border border-land-border bg-land-surface shadow-sm">
          <div class="border-b border-land-border-soft p-4">
            <div class="flex items-start justify-between gap-3">
              <div>
                <p class="text-xs font-semibold uppercase text-land-ink-soft">Map layers</p>
                <div class="mt-1 flex flex-wrap items-center gap-2">
                  <h2 class="text-lg font-semibold tracking-normal">Site context</h2>
                  <StateBadge :label="`${activeRenderableLayers.length}/${renderableLayers.length} visible`" :tone="activeRenderableLayers.length > 0 ? 'green' : 'slate'" />
                </div>
              </div>
              <button type="button" class="inline-flex h-8 items-center gap-2 rounded-md border border-land-border bg-white px-2 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted" data-testid="collapse-site-context-panel" @click="toggleLeftMapPanel">
                <PanelLeftClose class="size-4" aria-hidden="true" />
                Collapse
              </button>
            </div>
            <div class="mt-4 grid grid-cols-2 gap-2">
              <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="renderableLayers.length === 0" @click="setAllLayersVisible(true)">Show all</button>
              <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="activeRenderableLayers.length === 0" @click="setAllLayersVisible(false)">Hide all</button>
              <button type="button" class="col-span-2 h-8 rounded-md border border-land-border bg-white px-2 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="renderableLayers.length === 0" aria-label="Show all line and point layers, hide polygons" title="Show all line and point layers, hide polygons" data-testid="map-layers-lines-points" @click="setNonPolygonLayersVisible">Lines + points</button>
              <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted" @click="setDefaultLayersVisible">Defaults</button>
              <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted" @click="setLayerCategoryExpansion(expandedLayerCategoryKeys.size !== layerCategories.length)">
                {{ expandedLayerCategoryKeys.size === layerCategories.length ? 'Collapse' : 'Expand' }}
              </button>
            </div>
            <div class="mt-4">
              <p class="text-xs font-semibold uppercase text-land-ink-soft">Diligence recipes</p>
              <div class="mt-2 grid gap-2 sm:grid-cols-2 min-[1500px]:grid-cols-1">
                <button
                  v-for="recipe in layerRecipes"
                  :key="recipe.key"
                  type="button"
                  class="rounded-md border border-land-border bg-white px-2.5 py-2 text-left hover:border-land-primary/40 hover:bg-land-primary-soft"
                  :title="recipe.description"
                  @click="applyLayerRecipe(recipe)"
                >
                  <span class="block text-sm font-semibold text-land-ink">{{ recipe.label }}</span>
                  <span class="mt-1 block truncate text-xs text-land-ink-muted">{{ recipe.description }}</span>
                </button>
              </div>
            </div>
            <label class="mt-4 block">
              <span class="text-xs font-semibold uppercase text-land-ink-soft">Find a layer</span>
              <input
                v-model.trim="layerSearchQuery"
                type="search"
                class="mt-2 h-10 w-full rounded-md border border-land-border bg-white px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20"
                placeholder="Search power, gas, parcels, flood..."
              >
            </label>
            <div v-if="favoriteLayers.length > 0" class="mt-4">
              <p class="text-xs font-semibold uppercase text-land-ink-soft">Pinned layers</p>
              <div class="mt-2 flex flex-wrap gap-1.5">
                <button
                  v-for="layer in favoriteLayers"
                  :key="`favorite-${layer.key}`"
                  type="button"
                  class="inline-flex h-8 items-center gap-1.5 rounded-md border px-2 text-xs font-semibold"
                  :class="isLayerActive(layer.key) ? 'border-land-primary bg-land-primary-soft text-land-primary' : 'border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted'"
                  @click="toggleLayer(layer.key)"
                >
                  <span class="size-2 rounded-full" :style="{ backgroundColor: layerColor(layer) }" aria-hidden="true" />
                  {{ layer.name }}
                </button>
              </div>
            </div>
          </div>

          <div class="divide-y divide-land-border-soft">
            <div v-for="category in visibleLayerCategories" :key="category.key" class="p-3">
              <div class="flex items-center justify-between gap-2">
                <button
                  type="button"
                  class="flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-1 text-left hover:bg-land-surface-muted"
                  :aria-expanded="layerCategoryExpandedForDisplay(category.key)"
                  @click="toggleLayerCategory(category.key)"
                >
                  <component :is="layerCategoryExpandedForDisplay(category.key) ? ChevronDown : ChevronRight" class="size-4 shrink-0 text-land-ink-soft" aria-hidden="true" />
                  <component :is="categoryIcon(category.key)" class="size-4 shrink-0 text-land-ink-soft" aria-hidden="true" />
                  <span class="truncate text-sm font-semibold">{{ category.label }}</span>
                </button>
                <div class="flex shrink-0 items-center gap-1.5">
                  <span class="rounded bg-land-surface-muted px-2 py-0.5 text-xs font-semibold text-land-ink-soft">{{ categoryActiveCount(category) }}/{{ categoryRenderableCount(category) }}</span>
                  <button type="button" class="rounded border border-land-border bg-white px-2 py-0.5 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="categoryRenderableCount(category) === 0" @click.stop="setCategoryLayersVisible(category, true)">All</button>
                  <button type="button" class="rounded border border-land-border bg-white px-2 py-0.5 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="categoryActiveCount(category) === 0" @click.stop="setCategoryLayersVisible(category, false)">None</button>
                </div>
              </div>

              <div v-if="layerCategoryExpandedForDisplay(category.key)" class="mt-3 space-y-2">
                <label v-for="layer in category.layers" :key="layer.key" class="block rounded-md border border-land-border-soft bg-white p-2.5 transition-colors" :class="isLayerActive(layer.key) ? 'border-land-primary/30 bg-land-primary-soft/30' : ''">
                  <div class="flex items-start gap-3">
                    <input
                      :checked="isLayerActive(layer.key)"
                      :disabled="!canRenderLayer(layer)"
                      type="checkbox"
                      class="mt-1 size-4 rounded border-land-border text-land-primary focus:ring-land-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
                      @change="toggleLayer(layer.key)"
                    >
                    <span class="min-w-0 flex-1">
                      <span class="flex items-center justify-between gap-2">
                        <span class="min-w-0 inline-flex items-center gap-2">
                          <span class="size-3 shrink-0 rounded-sm border border-white shadow-sm" :style="{ backgroundColor: layerColor(layer) }" aria-hidden="true" />
                          <span class="truncate text-sm font-semibold">{{ layer.name }}</span>
                        </span>
                        <span class="inline-flex shrink-0 items-center gap-1.5">
                          <button
                            type="button"
                            class="grid size-7 place-items-center rounded-md text-land-ink-soft hover:bg-land-surface-muted hover:text-land-primary"
                            :aria-label="`About ${layer.name}`"
                            :aria-expanded="infoLayerKey === layer.key"
                            @click.stop.prevent
                            @mouseenter="openLayerInfo(layer, $event)"
                            @mouseleave="scheduleCloseLayerInfo"
                            @focus="openLayerInfo(layer, $event)"
                            @blur="scheduleCloseLayerInfo"
                            @keydown.esc="closeLayerInfo"
                          >
                            <Info class="size-3.5" aria-hidden="true" />
                          </button>
                          <button type="button" class="grid size-7 place-items-center rounded-md text-land-ink-soft hover:bg-land-surface-muted hover:text-land-primary" :aria-label="`${isFavoriteLayer(layer.key) ? 'Unpin' : 'Pin'} ${layer.name}`" @click.stop.prevent="toggleFavoriteLayer(layer.key)">
                            <Star class="size-3.5" :class="isFavoriteLayer(layer.key) ? 'fill-amber-400 text-amber-500' : ''" aria-hidden="true" />
                          </button>
                          <StateBadge :label="layerStatusLabel(layer)" :tone="layerStatusTone(layer)" />
                        </span>
                      </span>
                      <span class="mt-1 block text-xs leading-5 text-land-ink-muted">{{ layerDataSummary(layer) }}</span>
                      <span v-if="layerErrors[layer.key]" class="mt-2 block text-xs font-medium text-red-700">{{ layerErrors[layer.key] }}</span>
                      <span v-else-if="layerHints[layer.key]" class="mt-2 block text-xs font-medium leading-5 text-amber-700">{{ layerHints[layer.key] }}</span>
                      <span v-else-if="isLayerActive(layer.key)" class="mt-1 block text-xs leading-5 text-land-ink-soft">{{ layer.description }}</span>
                      <span v-else-if="!canRenderLayer(layer)" class="mt-1 block text-xs leading-5 text-land-ink-soft">{{ layer.description }}</span>
                      <span v-if="layer.metadata?.caveat" class="mt-1 block text-xs leading-5 text-amber-700">{{ layer.metadata.caveat }}</span>
                      <a
                        v-if="layer.sourceUrl"
                        :href="layer.sourceUrl"
                        target="_blank"
                        rel="noreferrer"
                        class="mt-1 inline-flex text-xs font-semibold text-land-primary hover:text-land-primary-hover"
                        @click.stop
                      >
                        Open source
                      </a>
                    </span>
                  </div>

                  <div v-if="isLayerActive(layer.key) && canRenderLayer(layer)" class="mt-3 flex items-center gap-2">
                    <SlidersHorizontal class="size-3.5 text-land-ink-soft" aria-hidden="true" />
                    <span class="text-xs font-semibold uppercase text-land-ink-soft">Opacity</span>
                    <input
                      :value="layerOpacity[layer.key]"
                      type="range"
                      min="0.1"
                      max="1"
                      step="0.05"
                      class="h-2 min-w-0 flex-1 accent-land-primary"
                      :aria-label="`${layer.name} opacity`"
                      @input="setLayerOpacity(layer.key, $event.target.value)"
                    >
                    <span class="w-9 text-right text-xs font-semibold text-land-ink-soft">{{ Math.round((layerOpacity[layer.key] || layer.defaultOpacity || 0.7) * 100) }}%</span>
                  </div>
                </label>
              </div>
            </div>
            <div v-if="visibleLayerCategories.length === 0" class="p-8 text-center text-sm text-land-ink-muted">
              No layers match “{{ layerSearchQuery }}”.
            </div>
          </div>
        </section>

        <section v-if="parcelSources.length > 0" class="rounded-md border border-land-border bg-land-surface shadow-sm">
          <div class="border-b border-land-border-soft p-4">
            <button
              type="button"
              class="flex w-full items-start justify-between gap-3 text-left"
              :aria-expanded="isContextSectionExpanded('parcel_sources')"
              @click="toggleContextSection('parcel_sources')"
            >
              <span>
                <span class="text-xs font-semibold uppercase text-land-ink-soft">Property Parcels</span>
                <span class="mt-1 flex flex-wrap items-center gap-2">
                  <span class="text-lg font-semibold tracking-normal">Public source layers</span>
                  <StateBadge :label="`${activeRenderableParcelSources.length}/${enabledParcelSources.length} visible`" :tone="activeRenderableParcelSources.length > 0 ? 'green' : 'slate'" />
                </span>
              </span>
              <span class="inline-flex items-center gap-2">
                <MapPinned class="size-5 text-land-primary" aria-hidden="true" />
                <component :is="isContextSectionExpanded('parcel_sources') ? ChevronDown : ChevronRight" class="size-4 text-land-ink-soft" aria-hidden="true" />
              </span>
            </button>
            <div v-if="isContextSectionExpanded('parcel_sources')" class="mt-4 grid grid-cols-2 gap-2">
              <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="enabledParcelSources.length === 0" @click="setAllParcelSourcesVisible(true)">Show all</button>
              <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="activeRenderableParcelSources.length === 0" @click="setAllParcelSourcesVisible(false)">Hide all</button>
            </div>
          </div>
          <div v-if="isContextSectionExpanded('parcel_sources')" class="space-y-3 p-4">
            <label v-for="source in renderableParcelSources" :key="source.key" class="block rounded-md border border-land-border-soft bg-white p-3">
              <div class="flex items-start gap-3">
                <input
                  :checked="isParcelSourceActive(source.key)"
                  :disabled="!source.enabled"
                  type="checkbox"
                  class="mt-1 size-4 rounded border-land-border text-land-primary focus:ring-land-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
                  @change="toggleParcelSource(source.key)"
                >
                <span class="min-w-0 flex-1">
                  <span class="flex items-center justify-between gap-2">
                    <span class="truncate text-sm font-semibold">{{ source.name }}</span>
                    <StateBadge :label="parcelSourceStatusLabel(source)" :tone="parcelSourceStatusTone(source)" />
                  </span>
                  <span class="mt-1 block text-xs leading-5 text-land-ink-muted">{{ source.provider }} · zoom {{ source.minZoom }}+</span>
                  <span v-if="parcelSourceErrors[source.key]" class="mt-2 block text-xs font-medium text-red-700">{{ parcelSourceErrors[source.key] }}</span>
                  <span v-else-if="parcelSourceHints[source.key]" class="mt-2 block text-xs font-medium text-amber-700">{{ parcelSourceHints[source.key] }}</span>
                  <span v-else-if="isParcelSourceActive(source.key)" class="mt-2 block text-xs leading-5 text-land-ink-soft">{{ source.licenseText }}</span>
                </span>
              </div>

              <div v-if="isParcelSourceActive(source.key) && source.enabled" class="mt-3 flex items-center gap-2">
                <SlidersHorizontal class="size-3.5 text-land-ink-soft" aria-hidden="true" />
                <span class="text-xs font-semibold uppercase text-land-ink-soft">Opacity</span>
                <input
                  :value="parcelSourceOpacity[source.key]"
                  type="range"
                  min="0.05"
                  max="0.75"
                  step="0.05"
                  class="h-2 min-w-0 flex-1 accent-land-primary"
                  :aria-label="`${source.name} opacity`"
                  @input="setParcelSourceOpacity(source.key, $event.target.value)"
                >
                <span class="w-9 text-right text-xs font-semibold text-land-ink-soft">{{ Math.round((parcelSourceOpacity[source.key] || source.defaultOpacity || 0.22) * 100) }}%</span>
              </div>
            </label>

            <div v-if="manualParcelSources.length > 0" class="rounded-md border border-purple-200 bg-purple-50 p-3">
              <div class="flex items-start justify-between gap-3">
                <div>
                  <p class="text-sm font-semibold text-purple-950">Paid owner lookup</p>
                  <p class="mt-1 text-xs leading-5 text-purple-900">Manual only. Use this around a clicked substation, infrastructure feature, or the current map center.</p>
                </div>
                <StateBadge label="Manual" tone="slate" />
              </div>
              <div class="mt-3 grid gap-2">
                <div v-for="source in manualParcelSources" :key="source.key" class="rounded-md border border-purple-200 bg-white p-3">
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                      <p class="truncate text-sm font-semibold">{{ source.name }}</p>
                      <p class="mt-1 text-xs text-land-ink-soft">{{ source.provider }} · {{ source.metadata?.quotaPolicy || 'Explicit lookup only' }}</p>
                    </div>
                    <StateBadge :label="manualParcelLayerKeys.has(source.key) ? 'Loaded' : 'Ready'" :tone="manualParcelLayerKeys.has(source.key) ? 'green' : 'blue'" />
                  </div>
                  <div class="mt-3 rounded-md border border-land-border-soft bg-land-surface-muted p-2">
                    <div class="grid grid-cols-2 gap-1 sm:grid-cols-4">
                      <button v-for="mode in manualParcelLookupModes" :key="mode.value" type="button" class="h-8 rounded px-2 text-xs font-semibold" :class="manualParcelLookupMode === mode.value ? 'bg-land-primary text-white' : 'text-land-ink-muted hover:bg-white'" @click="manualParcelLookupMode = mode.value">
                        {{ mode.label }}
                      </button>
                    </div>
                    <label v-if="manualParcelLookupMode === 'nearby'" class="mt-2 flex items-center justify-between gap-2 text-xs font-semibold text-land-ink-muted">
                      Radius
                      <select v-model.number="manualParcelLookupRadiusMiles" class="h-8 rounded-md border border-land-border bg-white px-2 text-xs focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                        <option v-for="radius in manualParcelRadiusOptions(source)" :key="radius" :value="radius">{{ radius }} mi</option>
                      </select>
                    </label>
                    <div v-if="manualParcelLookupMode === 'draw'" class="mt-2 rounded-md border border-purple-100 bg-white p-2">
                      <p class="text-xs leading-5 text-purple-900">{{ manualParcelDrawStatus }}</p>
                      <div class="mt-2 grid grid-cols-2 gap-1">
                        <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted" @click="startManualParcelDraw">{{ manualParcelDrawActive ? 'Drawing' : 'Start draw' }}</button>
                        <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="manualParcelDrawVertices.length === 0" @click="undoManualParcelDrawVertex">Undo point</button>
                        <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="!manualParcelDrawReady" @click="finishManualParcelDraw">Finish</button>
                        <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="manualParcelDrawVertices.length === 0" @click="clearManualParcelDraw">Clear</button>
                      </div>
                    </div>
                  </div>
                  <div class="mt-3 grid gap-2">
                    <button type="button" class="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover disabled:cursor-not-allowed disabled:opacity-60" :disabled="manualParcelLookupDisabledForSelectedFeature(source)" @click="lookupPaidParcelsAtSelectedFeature(source)">
                      <LoaderCircle v-if="loadingParcelSourceKeys.has(source.key)" class="size-4 animate-spin" aria-hidden="true" />
                      <MapPinned v-else class="size-4" aria-hidden="true" />
                      {{ manualParcelLookupMode === 'draw' ? 'Lookup drawn area' : 'Lookup selected feature' }}
                    </button>
                    <button type="button" class="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-semibold text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-60" :disabled="manualParcelLookupDisabledForMap(source)" @click="lookupPaidParcelsAtMapCenter(source)">
                      <LocateFixed class="size-4" aria-hidden="true" />
                      {{ manualParcelLookupMode === 'draw' ? 'Lookup drawn area' : 'Lookup map center' }}
                    </button>
                    <button v-if="manualParcelLayerKeys.has(source.key)" type="button" class="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted" @click="clearManualParcelLookup(source)">
                      <X class="size-4" aria-hidden="true" />
                      Clear lookup
                    </button>
                  </div>
                  <p v-if="manualParcelLookupNotices[source.key]" class="mt-2 text-xs font-semibold text-purple-900">{{ manualParcelLookupNotices[source.key] }}</p>
                  <p v-if="parcelSourceErrors[source.key]" class="mt-2 text-xs font-medium text-red-700">{{ parcelSourceErrors[source.key] }}</p>
                  <div v-if="parcelLookupResultsFor(source).length > 0" class="mt-3 rounded-md border border-purple-100 bg-purple-50/70">
                    <div class="flex items-center justify-between border-b border-purple-100 px-3 py-2">
                      <p class="text-xs font-semibold uppercase text-purple-900">Owner results</p>
                      <StateBadge :label="`${parcelLookupResultsFor(source).length}`" tone="slate" />
                    </div>
                    <button v-for="parcel in parcelLookupResultsFor(source).slice(0, 6)" :key="parcel.id" type="button" class="block w-full border-b border-purple-100 px-3 py-2 text-left last:border-b-0 hover:bg-white" @click="selectParcelLookupResult(source, parcel)">
                      <span class="block truncate text-sm font-semibold text-land-ink">{{ parcel.ownerName || parcel.label }}</span>
                      <span class="mt-0.5 block truncate text-xs text-land-ink-soft">{{ parcel.apn || 'No APN' }} · {{ parcel.acreage ? `${parcel.acreage} ac` : 'acreage n/a' }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </aside>

      <div class="order-1 rounded-md border border-land-border bg-land-surface shadow-sm min-[1500px]:order-2">
        <div class="border-b border-land-border-soft p-4">
          <div class="flex flex-col gap-4 2xl:flex-row 2xl:items-start 2xl:justify-between">
            <div class="min-w-0">
              <h2 class="text-lg font-semibold tracking-normal">Mapped acquisition view</h2>
              <div class="mt-2 flex flex-wrap gap-2">
                <StateBadge :label="`${visibleOpportunityPoints.length} opportunities`" tone="blue" />
                <StateBadge :label="`${visibleParcels.length} parcels`" tone="green" />
                <StateBadge :label="`${activeMapOverlayCount} active overlays`" :tone="activeMapOverlayCount > 0 ? 'amber' : 'slate'" />
                <StateBadge v-if="comparisonRecords.length > 0" :label="`${comparisonRecords.length} comparing`" tone="violet" />
              </div>
            </div>

            <div class="flex shrink-0 items-center gap-2">
              <button
                type="button"
                class="inline-flex size-10 items-center justify-center rounded-md border border-land-border bg-white text-land-ink-muted transition-colors hover:bg-land-surface-muted hover:text-land-ink"
                data-testid="map-left-panel-toggle"
                :aria-label="leftMapPanelCollapsed ? 'Show context panel' : 'Hide context panel'"
                :title="leftMapPanelCollapsed ? 'Show context panel' : 'Hide context panel'"
                @click="toggleLeftMapPanel"
              >
                <component :is="leftMapPanelCollapsed ? PanelLeftOpen : PanelLeftClose" class="size-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                class="inline-flex size-10 items-center justify-center rounded-md border border-land-border bg-white text-land-ink-muted transition-colors hover:bg-land-surface-muted hover:text-land-ink"
                data-testid="map-right-panel-toggle"
                :aria-label="rightMapPanelCollapsed ? 'Show selected site panel' : 'Hide selected site panel'"
                :title="rightMapPanelCollapsed ? 'Show selected site panel' : 'Hide selected site panel'"
                @click="toggleRightMapPanel"
              >
                <component :is="rightMapPanelCollapsed ? PanelRightOpen : PanelRightClose" class="size-4" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div class="mt-4 grid items-end gap-3 rounded-md border border-land-border-soft bg-land-surface-muted p-3 sm:grid-cols-2 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,.9fr)]" data-testid="map-filter-bar">
            <div class="min-w-0" data-testid="map-location-search">
              <p class="mb-1.5 text-xs font-semibold uppercase tracking-wide text-land-ink-soft">Location</p>
              <div class="flex h-10 items-center gap-2 rounded-md border border-land-border bg-white px-2">
                <Search class="size-4 shrink-0 text-land-ink-soft" aria-hidden="true" />
                <input
                  v-model="locationSearchQuery"
                  type="search"
                  autocomplete="off"
                  aria-label="Search by city, county, address, or GPS coordinates"
                  class="h-8 min-w-0 flex-1 border-0 bg-transparent px-1 text-sm outline-none focus:ring-0"
                  placeholder="City, county, address, or GPS"
                  @keyup.enter="searchMapLocation"
                  @keydown.esc="clearLocationSearch"
                >
                <button
                  type="button"
                  class="grid size-8 shrink-0 place-items-center rounded-md bg-land-primary text-white transition-colors hover:bg-land-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="locationSearchDisabled"
                  aria-label="Search map location"
                  title="Search map location"
                  @click="searchMapLocation"
                >
                  <LoaderCircle v-if="locationSearchLoading" class="size-4 animate-spin" aria-hidden="true" />
                  <Search v-else class="size-4" aria-hidden="true" />
                </button>
                <button
                  v-if="locationSearchResult || locationSearchError"
                  type="button"
                  class="grid size-8 shrink-0 place-items-center rounded-md border border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted"
                  aria-label="Clear location search"
                  title="Clear location search"
                  @click="clearLocationSearch"
                >
                  <X class="size-4" aria-hidden="true" />
                </button>
              </div>
              <p v-if="locationSearchError" class="mt-1.5 truncate text-xs font-medium text-red-700" role="alert">{{ locationSearchError }}</p>
              <p v-else-if="locationSearchResult" class="mt-1.5 truncate text-xs font-semibold text-land-primary" role="status">{{ locationSearchResult.label }}</p>
            </div>

            <div class="min-w-0">
              <p class="mb-1.5 text-xs font-semibold uppercase tracking-wide text-land-ink-soft">Records</p>
              <div class="grid h-10 grid-cols-3 gap-1 rounded-md border border-land-border bg-white p-1">
                <button
                  v-for="mode in layerModes"
                  :key="mode.value"
                  type="button"
                  class="inline-flex h-full items-center justify-center rounded px-2 text-sm font-medium whitespace-nowrap transition-colors"
                  :class="selectedRecordLayer === mode.value ? 'bg-land-primary text-white shadow-sm' : 'text-land-ink-muted hover:bg-land-surface-muted'"
                  @click="selectedRecordLayer = mode.value"
                >
                  {{ mode.label }}
                </button>
              </div>
            </div>

            <label class="block min-w-0">
              <span class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-land-ink-soft">Stage</span>
              <select v-model="selectedStage" aria-label="Filter mapped opportunities by stage" class="h-10 w-full rounded-md border border-land-border bg-white px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                <option value="all">All stages</option>
                <option v-for="stage in stages" :key="stage.key" :value="stage.key">{{ stage.label }}</option>
              </select>
            </label>

            <div class="min-w-0">
              <p class="mb-1.5 text-xs font-semibold uppercase tracking-wide text-land-ink-soft">Attention</p>
              <button
                type="button"
                class="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md border px-3 text-sm font-semibold transition-colors"
                :class="needsActionOnly ? 'border-land-primary bg-land-primary-soft text-land-primary' : 'border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted'"
                @click="needsActionOnly = !needsActionOnly"
              >
                <span class="size-2 rounded-full" :class="needsActionOnly ? 'bg-land-primary' : 'bg-slate-300'" aria-hidden="true" />
                Issues only
              </button>
            </div>
          </div>
        </div>

        <div class="p-4">
              <div class="relative h-[min(72vh,760px)] min-h-[520px] overflow-hidden rounded-md border border-land-border bg-land-surface-soft min-[1500px]:h-[calc(100vh-250px)] min-[1500px]:min-h-[620px]" data-testid="opportunity-map-canvas">
            <div class="absolute inset-0">
              <div ref="mapElement" class="h-full w-full" />
            </div>

            <Transition name="map-loader-fade">
              <MapLoadingOverlay v-if="showMapLoader" class="z-30" />
            </Transition>

            <div v-if="!apiKey || styleError" class="absolute inset-0 grid place-items-center px-6 text-center">
              <div>
                <MapPinned class="mx-auto size-10 text-land-primary" aria-hidden="true" />
                <p class="mt-3 text-sm font-semibold">Map tiles unavailable</p>
                <p class="mt-2 text-sm text-land-ink-muted">Check MAPTILER_API_KEY to render the live parcel map.</p>
              </div>
            </div>

            <div v-else-if="visibleOpportunityPoints.length === 0 && visibleParcels.length === 0" class="absolute inset-0 grid place-items-center px-6 text-center">
              <div>
                <MapPinned class="mx-auto size-10 text-land-primary" aria-hidden="true" />
                <p class="mt-3 text-sm font-semibold">No plotted records</p>
                <p class="mt-2 text-sm text-land-ink-muted">Add coordinates to an opportunity parcel to plot it here.</p>
              </div>
            </div>

            <div class="absolute left-3 top-3 max-w-[calc(100%-1.5rem)] rounded-md border border-land-border bg-white/95 px-3 py-2 shadow-sm backdrop-blur">
              <div class="flex flex-wrap items-center gap-2 text-xs font-semibold text-land-ink-muted">
                <span class="inline-flex items-center gap-1.5"><Satellite class="size-3.5 text-land-primary" aria-hidden="true" /> {{ mapStatusLabel }}</span>
                <span v-if="isAnyLayerLoading" class="inline-flex items-center gap-1.5 text-land-primary"><LoaderCircle class="size-3.5 animate-spin" aria-hidden="true" /> Loading layer data</span>
              </div>
            </div>

            <div class="absolute right-14 top-3 max-w-[calc(100%-5rem)] rounded-md border border-land-border bg-white/95 p-2 shadow-sm backdrop-blur">
              <div class="flex flex-wrap items-center gap-2">
                <button type="button" class="inline-flex h-8 items-center gap-1.5 rounded-md border border-land-border bg-white px-2 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted" @click.stop="fitToVisibleRecords">
                  <LocateFixed class="size-3.5" aria-hidden="true" />
                  Fit records
                </button>
                <button
                  v-if="manualParcelSources.length > 0"
                  type="button"
                  class="inline-flex h-8 items-center gap-1.5 rounded-md border px-2 text-xs font-semibold"
                  :class="manualParcelLookupMode === 'draw' ? 'border-purple-300 bg-purple-50 text-purple-900' : 'border-land-border bg-white text-land-ink-muted hover:bg-land-surface-muted'"
                  @click.stop="activateManualParcelDraw"
                >
                  <MapPinned class="size-3.5" aria-hidden="true" />
                  Draw parcel area
                </button>
                <button
                  v-if="manualParcelSources.length > 0"
                  type="button"
                  class="inline-flex h-8 items-center gap-1.5 rounded-md bg-land-primary px-2 text-xs font-semibold text-white hover:bg-land-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="manualParcelLookupDisabledForMap(manualParcelSources[0])"
                  @click.stop="lookupPaidParcelsAtMapCenter(manualParcelSources[0])"
                >
                  <MapPinned class="size-3.5" aria-hidden="true" />
                  {{ manualParcelLookupMode === 'draw' ? 'Lookup drawn area' : 'Lookup center' }}
                </button>
              </div>
            </div>

            <div v-if="manualParcelLookupMode === 'draw'" class="absolute left-3 top-14 max-w-sm rounded-md border border-purple-200 bg-white/95 p-3 shadow-sm backdrop-blur">
              <div class="flex items-start justify-between gap-3">
                <div>
                  <p class="text-xs font-semibold uppercase text-purple-900">Parcel search area</p>
                  <p class="mt-1 text-xs leading-5 text-land-ink-muted">{{ manualParcelDrawStatus }}</p>
                </div>
                <StateBadge :label="manualParcelDrawLookupReady ? 'Ready' : manualParcelDrawReady ? 'Too large' : `${manualParcelDrawVertices.length}/3`" :tone="manualParcelDrawLookupReady ? 'green' : manualParcelDrawReady ? 'amber' : 'slate'" />
              </div>
              <div class="mt-2 flex flex-wrap gap-1.5">
                <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted" @click.stop="startManualParcelDraw">{{ manualParcelDrawActive ? 'Drawing' : 'Start draw' }}</button>
                <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="manualParcelDrawVertices.length === 0" @click.stop="undoManualParcelDrawVertex">Undo</button>
                <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="!manualParcelDrawReady" @click.stop="finishManualParcelDraw">Finish</button>
                <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="manualParcelDrawVertices.length === 0" @click.stop="clearManualParcelDraw">Clear</button>
              </div>
            </div>

            <div class="absolute bottom-3 left-3 min-w-44 rounded-md border border-land-border bg-white/95 p-2 shadow-sm backdrop-blur">
              <button
                type="button"
                class="flex w-full items-center justify-between gap-3 rounded px-1.5 py-1 text-left hover:bg-land-surface-muted"
                :aria-expanded="!recordHealthLegendCollapsed"
                @click.stop="toggleRecordHealthLegend"
              >
                <span class="text-xs font-semibold uppercase text-land-ink-soft">Record Health</span>
                <component :is="recordHealthLegendCollapsed ? ChevronRight : ChevronDown" class="size-3.5 shrink-0 text-land-ink-soft" aria-hidden="true" />
              </button>
              <div v-if="!recordHealthLegendCollapsed" class="px-1.5 pb-1">
                <div class="mt-2 grid gap-1.5 text-xs font-medium text-land-ink-muted">
                  <span v-for="item in legendItems" :key="item.label" class="inline-flex items-center gap-2">
                    <span class="size-2.5 rounded-full" :class="item.class" aria-hidden="true" />
                    {{ item.label }}
                  </span>
                </div>
                <div v-if="voltageLegendVisible" class="mt-3 border-t border-land-border-soft pt-3">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">Voltage</p>
                  <div class="mt-2 grid gap-1.5 text-xs font-medium text-land-ink-muted">
                    <span v-for="tier in voltageLegendItems" :key="tier.key" class="inline-flex items-center gap-2">
                      <span class="h-1.5 w-5 rounded-full" :style="{ backgroundColor: tier.color }" aria-hidden="true" />
                      {{ tier.label }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="activeMapOverlayCount > 0" class="absolute bottom-3 right-3 w-[min(22rem,calc(100%-1.5rem))] rounded-md border border-land-border bg-white/95 p-2 shadow-sm backdrop-blur">
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="flex min-w-0 flex-1 items-center justify-between gap-3 rounded px-1.5 py-1 text-left hover:bg-land-surface-muted"
                  :aria-expanded="!visibleLayersLegendCollapsed"
                  @click.stop="toggleVisibleLayersLegend"
                >
                  <span class="min-w-0">
                    <span class="block text-xs font-semibold uppercase text-land-ink-soft">Visible Layers</span>
                    <span class="mt-0.5 block text-xs font-medium text-land-ink-muted">{{ activeMapOverlayCount }} active</span>
                  </span>
                  <component :is="visibleLayersLegendCollapsed ? ChevronRight : ChevronDown" class="size-3.5 shrink-0 text-land-ink-soft" aria-hidden="true" />
                </button>
                <button
                  v-if="!visibleLayersLegendCollapsed"
                  type="button"
                  class="grid size-7 shrink-0 place-items-center rounded-md text-land-ink-soft hover:bg-land-surface-muted hover:text-red-600"
                  aria-label="Hide all visible overlays"
                  title="Hide all visible overlays"
                  @click.stop="hideVisibleMapOverlays"
                >
                  <X class="size-3.5" aria-hidden="true" />
                </button>
              </div>
              <div v-if="!visibleLayersLegendCollapsed" class="mt-2 max-h-[calc(100vh-12rem)] overflow-y-auto px-1.5 pb-1 pr-2 sm:max-h-80">
                <div class="grid gap-1.5">
                  <button
                    v-for="layer in activeRenderableLayers"
                    :key="`layer-${layer.key}`"
                    type="button"
                    class="flex w-full min-w-0 items-center gap-2 rounded bg-land-surface-muted px-2 py-1.5 text-left text-xs font-semibold text-land-ink-muted hover:bg-land-primary-soft hover:text-land-primary"
                    :aria-label="`Hide ${layer.name}`"
                    :title="`Hide ${layer.name}`"
                    @click.stop="toggleLayer(layer.key)"
                  >
                    <span class="size-2 shrink-0 rounded-full" :style="{ backgroundColor: layerColor(layer) }" aria-hidden="true" />
                    <span class="min-w-0 flex-1 truncate">{{ layer.name }}</span>
                    <X class="size-3 shrink-0" aria-hidden="true" />
                  </button>
                  <button
                    v-for="source in activeRenderableParcelSources"
                    :key="`parcel-source-${source.key}`"
                    type="button"
                    class="flex w-full min-w-0 items-center gap-2 rounded bg-land-surface-muted px-2 py-1.5 text-left text-xs font-semibold text-land-ink-muted hover:bg-land-primary-soft hover:text-land-primary"
                    :aria-label="`Hide ${source.name}`"
                    :title="`Hide ${source.name}`"
                    @click.stop="toggleParcelSource(source.key)"
                  >
                    <span class="size-2 shrink-0 rounded-full" :style="{ backgroundColor: source.style?.fillColor || '#f59e0b' }" aria-hidden="true" />
                    <span class="min-w-0 flex-1 truncate">{{ source.name }}</span>
                    <X class="size-3 shrink-0" aria-hidden="true" />
                  </button>
                  <button
                    v-for="source in visibleManualParcelSources"
                    :key="`manual-parcel-source-${source.key}`"
                    type="button"
                    class="flex w-full min-w-0 items-center gap-2 rounded bg-purple-50 px-2 py-1.5 text-left text-xs font-semibold text-purple-900 hover:bg-purple-100"
                    :aria-label="`Clear ${source.name}`"
                    :title="`Clear ${source.name}`"
                    @click.stop="clearManualParcelLookup(source)"
                  >
                    <span class="size-2 shrink-0 rounded-full" :style="{ backgroundColor: source.style?.fillColor || '#a855f7' }" aria-hidden="true" />
                    <span class="min-w-0 flex-1 truncate">{{ source.name }}</span>
                    <X class="size-3 shrink-0" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Teleport to="body">
        <div v-if="selectedInfrastructureFeature" class="fixed inset-0 z-40 bg-land-ink/20" data-testid="infrastructure-slideout-backdrop" @click.self="closeInfrastructureFeature">
          <aside class="ml-auto flex h-full w-full max-w-xl flex-col border-l border-land-border bg-white shadow-2xl" data-testid="infrastructure-slideout" :aria-label="`${selectedInfrastructureLabel} details`">
            <header class="border-b border-land-border-soft p-5">
              <div class="flex items-start justify-between gap-4">
                <div class="min-w-0">
                  <p class="flex items-center gap-2 text-xs font-semibold uppercase text-land-primary">
                    <component :is="selectedInfrastructureIcon" class="size-4" aria-hidden="true" />
                    {{ selectedInfrastructureLabel }}
                  </p>
                  <h2 class="mt-2 truncate text-xl font-semibold tracking-normal">{{ selectedInfrastructureFeature.name }}</h2>
                  <p class="mt-1 text-sm text-land-ink-muted">{{ selectedInfrastructureFeature.subtitle }}</p>
                </div>
                <button type="button" class="grid size-9 shrink-0 place-items-center rounded-md border border-land-border text-land-ink-muted hover:bg-land-surface-muted" :aria-label="`Close ${selectedInfrastructureLabel.toLowerCase()} details`" @click="closeInfrastructureFeature">
                  <X class="size-4" aria-hidden="true" />
                </button>
              </div>
            </header>

            <div class="flex-1 overflow-auto p-5">
              <section>
                <div class="grid gap-2 sm:grid-cols-2">
                  <div v-for="fact in selectedInfrastructureFacts" :key="fact.label" class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                    <p class="text-xs font-medium uppercase text-land-ink-soft">{{ fact.label }}</p>
                    <p class="mt-1 text-sm font-semibold">{{ fact.value }}</p>
                  </div>
                </div>
                <div class="mt-3 rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                  <p class="text-xs font-semibold uppercase text-land-ink-soft">{{ selectedInfrastructureFeature.layerName }}</p>
                  <p class="mt-1 text-sm leading-6 text-land-ink-muted">{{ selectedInfrastructureFeature.description || selectedInfrastructureFeature.provider }}</p>
                  <a v-if="selectedInfrastructureFeature.sourceUrl" :href="selectedInfrastructureFeature.sourceUrl" target="_blank" rel="noreferrer" class="mt-2 inline-flex text-sm font-semibold text-land-primary hover:text-land-primary-hover">
                    Open source
                  </a>
                </div>
              </section>

              <section class="mt-6 rounded-md border border-land-border-soft bg-land-surface-muted p-4">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <h3 class="text-sm font-semibold">Next actions</h3>
                    <p class="mt-1 text-xs leading-5 text-land-ink-muted">Use the clicked feature as a diligence anchor.</p>
                  </div>
                  <StateBadge label="Context" tone="blue" />
                </div>
                <div class="mt-3 grid gap-2">
                  <p v-for="hint in selectedInfrastructureActionHints" :key="hint" class="rounded-md border border-land-border-soft bg-white px-3 py-2 text-xs leading-5 text-land-ink-muted">{{ hint }}</p>
                </div>
                <div class="mt-3 grid gap-2 sm:grid-cols-2">
                  <button type="button" class="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-semibold text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-60" :disabled="!selectedInfrastructureFeature.coordinates" @click="focusInfrastructureFeature">
                    <LocateFixed class="size-4" aria-hidden="true" />
                    Focus feature
                  </button>
                  <button type="button" class="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-semibold text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-60" :disabled="!selectedInfrastructureFeature.coordinates" @click="copyFeatureCoordinates">
                    <MapPinned class="size-4" aria-hidden="true" />
                    Copy coords
                  </button>
                </div>
              </section>

              <section v-if="manualParcelSources.length > 0" class="mt-6 rounded-md border border-purple-200 bg-purple-50 p-4">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <h3 class="text-sm font-semibold text-purple-950">Parcel owner lookup</h3>
                    <p class="mt-1 text-xs leading-5 text-purple-900">Run a paid parcel query at this clicked feature only when you need owner-of-record context.</p>
                  </div>
                  <StateBadge label="Paid" tone="slate" />
                </div>
                <div class="mt-3 rounded-md border border-purple-200 bg-white/70 p-2">
                  <div class="grid grid-cols-2 gap-1 sm:grid-cols-4">
                    <button v-for="mode in manualParcelLookupModes" :key="`slideout-${mode.value}`" type="button" class="h-8 rounded px-2 text-xs font-semibold" :class="manualParcelLookupMode === mode.value ? 'bg-land-primary text-white' : 'text-land-ink-muted hover:bg-white'" @click="manualParcelLookupMode = mode.value">
                      {{ mode.label }}
                    </button>
                  </div>
                  <label v-if="manualParcelLookupMode === 'nearby'" class="mt-2 flex items-center justify-between gap-2 text-xs font-semibold text-purple-900">
                    Search radius
                    <select v-model.number="manualParcelLookupRadiusMiles" class="h-8 rounded-md border border-land-border bg-white px-2 text-xs focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                      <option v-for="radius in manualParcelRadiusOptions(manualParcelSources[0])" :key="radius" :value="radius">{{ radius }} mi</option>
                    </select>
                  </label>
                  <div v-if="manualParcelLookupMode === 'draw'" class="mt-2 rounded-md border border-purple-100 bg-white p-2">
                    <p class="text-xs leading-5 text-purple-900">{{ manualParcelDrawStatus }}</p>
                    <div class="mt-2 grid grid-cols-2 gap-1">
                      <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted" @click="startManualParcelDraw">{{ manualParcelDrawActive ? 'Drawing' : 'Start draw' }}</button>
                      <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="manualParcelDrawVertices.length === 0" @click="undoManualParcelDrawVertex">Undo point</button>
                      <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="!manualParcelDrawReady" @click="finishManualParcelDraw">Finish</button>
                      <button type="button" class="h-8 rounded-md border border-land-border bg-white px-2 text-xs font-semibold text-land-ink-muted hover:bg-land-surface-muted disabled:cursor-not-allowed disabled:opacity-50" :disabled="manualParcelDrawVertices.length === 0" @click="clearManualParcelDraw">Clear</button>
                    </div>
                  </div>
                </div>
                <div class="mt-3 grid gap-2">
                  <button type="button" class="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover disabled:cursor-not-allowed disabled:opacity-60" :disabled="manualParcelLookupDisabledForSelectedFeatureAll" @click="lookupPaidParcelsAtSelectedFeatureForAll">
                    <LoaderCircle v-if="manualParcelLookupAnyLoading" class="size-4 animate-spin" aria-hidden="true" />
                    <MapPinned v-else class="size-4" aria-hidden="true" />
                    {{ manualParcelLookupMode === 'draw' ? 'Lookup drawn area' : 'Lookup parcels near feature' }}
                  </button>
                  <p v-if="manualParcelSources.length > 1" class="text-xs leading-5 text-purple-900">
                    Runs {{ manualParcelSourceNames }}; provider-specific results appear below.
                  </p>
                </div>
                <div class="mt-3 grid gap-2">
                  <p v-for="source in manualParcelSources" :key="`${source.key}-lookup-status`" class="text-xs font-semibold" :class="parcelSourceErrors[source.key] ? 'text-red-700' : 'text-purple-900'">
                    {{ parcelSourceErrors[source.key] || manualParcelLookupNotices[source.key] }}
                  </p>
                </div>
                <div v-for="source in manualParcelSources" :key="`${source.key}-lookup-results`" class="mt-3">
                  <div v-if="parcelLookupResultsFor(source).length > 0" class="rounded-md border border-purple-200 bg-white">
                    <div class="flex items-center justify-between border-b border-purple-100 px-3 py-2">
                      <p class="text-xs font-semibold uppercase text-purple-900">Returned parcels</p>
                      <StateBadge :label="`${parcelLookupResultsFor(source).length}`" tone="slate" />
                    </div>
                    <button v-for="parcel in parcelLookupResultsFor(source).slice(0, 8)" :key="parcel.id" type="button" class="block w-full border-b border-purple-100 px-3 py-2 text-left last:border-b-0 hover:bg-land-surface-muted" @click="selectParcelLookupResult(source, parcel)">
                      <span class="block truncate text-sm font-semibold text-land-ink">{{ parcel.ownerName || parcel.label }}</span>
                      <span class="mt-0.5 block truncate text-xs text-land-ink-soft">{{ parcel.apn || 'No APN' }} · {{ parcel.situsAddress || 'No situs address' }}</span>
                    </button>
                  </div>
                </div>
              </section>

              <section class="mt-6">
                <div class="mb-3 flex items-center justify-between gap-3">
                  <h3 class="text-sm font-semibold">Attributes</h3>
                  <StateBadge :label="`${selectedInfrastructureAttributes.length}`" tone="slate" />
                </div>
                <div class="grid gap-2">
                  <div v-for="attribute in selectedInfrastructureAttributes" :key="attribute.key" class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                    <p class="text-xs font-medium uppercase text-land-ink-soft">{{ attribute.label }}</p>
                    <p class="mt-1 break-words text-sm font-semibold">{{ attribute.value }}</p>
                  </div>
                  <p v-if="selectedInfrastructureAttributes.length === 0" class="rounded-md border border-dashed border-land-border p-4 text-center text-sm text-land-ink-muted">No source attributes available.</p>
                </div>
              </section>

              <section class="mt-6">
                <div class="mb-3 flex items-center justify-between gap-3">
                  <h3 class="text-sm font-semibold">Notes</h3>
                  <StateBadge :label="`${featureNotes.length}`" tone="slate" />
                </div>

                <form class="rounded-md border border-land-border-soft bg-land-surface-muted p-3" @submit.prevent="saveFeatureNote">
                  <label class="text-xs font-semibold uppercase text-land-ink-soft" for="map-feature-note-body">Add note</label>
                  <textarea id="map-feature-note-body" v-model.trim="featureNoteBody" rows="3" class="mt-2 w-full resize-y rounded-md border border-land-border bg-white px-3 py-2 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20" placeholder="Add context, follow-up, or diligence notes." />
                  <div v-if="featureNoteErrors.length > 0" class="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    <p v-for="error in featureNoteErrors" :key="error">{{ error }}</p>
                  </div>
                  <div class="mt-3 flex justify-end">
                    <button type="submit" class="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover disabled:cursor-not-allowed disabled:opacity-60" :disabled="featureNoteSaving || featureNoteBody.length === 0">
                      <LoaderCircle v-if="featureNoteSaving" class="size-4 animate-spin" aria-hidden="true" />
                      <MessageSquarePlus v-else class="size-4" aria-hidden="true" />
                      Add note
                    </button>
                  </div>
                </form>

                <div class="mt-4 space-y-3">
                  <div v-if="featureNotesLoading" class="flex items-center gap-2 rounded-md border border-land-border-soft p-3 text-sm text-land-ink-muted">
                    <LoaderCircle class="size-4 animate-spin text-land-primary" aria-hidden="true" />
                    Loading notes
                  </div>
                  <article v-for="note in featureNotes" :key="note.id" class="rounded-md border border-land-border-soft bg-white p-3">
                    <div class="flex items-center justify-between gap-3">
                      <p class="text-sm font-semibold">{{ note.author }}</p>
                      <time class="text-xs text-land-ink-soft" :datetime="note.createdAt">{{ formatDateTime(note.createdAt) }}</time>
                    </div>
                    <p class="mt-2 whitespace-pre-wrap text-sm leading-6 text-land-ink-muted">{{ note.body }}</p>
                  </article>
                  <p v-if="!featureNotesLoading && featureNotes.length === 0" class="rounded-md border border-dashed border-land-border p-4 text-center text-sm text-land-ink-muted">{{ selectedInfrastructureEmptyMessage }}</p>
                </div>
              </section>
            </div>
          </aside>
        </div>
      </Teleport>

      <Teleport to="body">
        <div v-if="selectedExternalParcel" class="fixed inset-0 z-40 bg-land-ink/20" data-testid="external-parcel-slideout-backdrop" @click.self="closeExternalParcel">
          <aside class="ml-auto flex h-full w-full max-w-xl flex-col border-l border-land-border bg-white shadow-2xl" data-testid="external-parcel-slideout" aria-label="External parcel details">
            <header class="border-b border-land-border-soft p-5">
              <div class="flex items-start justify-between gap-4">
                <div class="min-w-0">
                  <p class="flex items-center gap-2 text-xs font-semibold uppercase text-land-primary">
                    <MapPinned class="size-4" aria-hidden="true" />
                    Public parcel
                  </p>
                  <h2 class="mt-2 truncate text-xl font-semibold tracking-normal">{{ selectedExternalParcel.displayName }}</h2>
                  <p class="mt-1 text-sm text-land-ink-muted">{{ selectedExternalParcel.sourceName }} · {{ selectedExternalParcel.state || 'State source' }}</p>
                </div>
                <button type="button" class="grid size-9 shrink-0 place-items-center rounded-md border border-land-border text-land-ink-muted hover:bg-land-surface-muted" aria-label="Close public parcel details" @click="closeExternalParcel">
                  <X class="size-4" aria-hidden="true" />
                </button>
              </div>
            </header>

            <div class="flex-1 overflow-auto p-5">
              <section>
                <div class="grid gap-2 sm:grid-cols-2">
                  <div v-for="fact in selectedExternalParcelFacts" :key="fact.label" class="rounded-md border border-land-border-soft bg-land-surface-muted p-3">
                    <p class="text-xs font-medium uppercase text-land-ink-soft">{{ fact.label }}</p>
                    <p class="mt-1 text-sm font-semibold">{{ fact.value }}</p>
                  </div>
                </div>
                <p v-if="selectedExternalParcel.licenseText" class="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900">{{ selectedExternalParcel.licenseText }}</p>
              </section>

              <section class="mt-6 rounded-md border border-land-border-soft bg-land-surface-muted p-4">
                <h3 class="text-sm font-semibold">Track in Landport</h3>
                <p class="mt-1 text-xs leading-5 text-land-ink-muted">Attach this public parcel to an opportunity as a Landport parcel with source provenance preserved.</p>
                <label class="mt-4 block text-sm font-medium">Opportunity
                  <select v-model="selectedParcelTrackOpportunityId" class="mt-2 h-10 w-full rounded-md border border-land-border bg-white px-3 text-sm focus:border-land-primary focus:outline-none focus:ring-2 focus:ring-land-primary/20">
                    <option v-for="point in trackableOpportunities" :key="point.id" :value="point.id">{{ point.name }}</option>
                  </select>
                </label>
                <div v-if="externalParcelErrors.length > 0" class="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  <p v-for="error in externalParcelErrors" :key="error">{{ error }}</p>
                </div>
                <div class="mt-4 flex justify-end">
                  <button type="button" class="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover disabled:cursor-not-allowed disabled:opacity-60" :disabled="trackingExternalParcel || !selectedParcelTrackOpportunityId" @click="trackExternalParcel">
                    <LoaderCircle v-if="trackingExternalParcel" class="size-4 animate-spin" aria-hidden="true" />
                    <Plus v-else class="size-4" aria-hidden="true" />
                    Track parcel
                  </button>
                </div>
              </section>
            </div>
          </aside>
        </div>
      </Teleport>

      <aside v-if="!rightMapPanelCollapsed" class="order-3 space-y-4 min-[1500px]:sticky min-[1500px]:top-20 min-[1500px]:max-h-[calc(100vh-6rem)] min-[1500px]:overflow-auto min-[1500px]:pr-1" data-testid="map-right-panel">
        <section class="rounded-md border border-land-border bg-land-surface shadow-sm" data-testid="site-intelligence-panel">
          <div class="border-b border-land-border-soft p-4">
            <div class="flex items-start justify-between gap-3">
              <div>
                <p class="text-xs font-semibold uppercase text-land-ink-soft">Selected Site</p>
                <h2 class="mt-1 text-lg font-semibold tracking-normal">{{ selectedRecord?.name || 'Site intelligence' }}</h2>
              </div>
              <div class="flex gap-2">
                <button v-if="selectedRecord" type="button" class="grid size-8 place-items-center rounded-md border border-land-border text-land-ink-muted hover:bg-land-surface-muted" aria-label="Clear selected site" @click="clearSelectedRecord">
                  <X class="size-4" aria-hidden="true" />
                </button>
                <button type="button" class="inline-flex h-8 items-center gap-2 rounded-md border border-land-border bg-white px-2 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted" data-testid="collapse-selected-site-panel" @click="toggleRightMapPanel">
                  <PanelRightClose class="size-4" aria-hidden="true" />
                  Collapse
                </button>
              </div>
            </div>
          </div>

          <div v-if="selectedRecord" class="p-4">
            <div class="flex items-start gap-3">
              <img v-if="selectedRecord.primaryImage" :src="selectedRecord.primaryImage.url" :alt="selectedRecord.primaryImage.filename" class="size-14 rounded-md object-cover">
              <div class="min-w-0 flex-1">
                <p class="truncate text-sm font-semibold">{{ selectedRecord.name }}</p>
                <p class="mt-1 text-xs leading-5 text-land-ink-muted">{{ selectedRecordLocation }}</p>
                <div class="mt-2 flex flex-wrap gap-1.5">
                  <StateBadge :label="selectedRecord.health?.stateLabel || selectedRecord.stageLabel || 'Unknown'" :tone="selectedRecord.health?.tone || 'slate'" />
                  <StateBadge v-if="selectedRecord.kind === 'parcel'" label="Parcel" tone="blue" />
                  <StateBadge v-else :label="selectedRecord.stageLabel" tone="slate" />
                </div>
              </div>
            </div>

            <div class="mt-4 rounded-md border border-land-border-soft bg-land-surface-muted p-3">
              <label class="flex items-center justify-between gap-3 text-xs font-semibold uppercase text-land-ink-soft">
                Search radius
                <span>{{ radiusMiles }} mi</span>
              </label>
              <input v-model.number="radiusMiles" type="range" min="1" max="50" step="1" class="mt-3 w-full accent-land-primary" aria-label="Site intelligence radius">
            </div>

            <div v-if="intelligenceLoading" class="mt-4 flex items-center gap-2 rounded-md border border-land-border-soft p-3 text-sm text-land-ink-muted">
              <LoaderCircle class="size-4 animate-spin text-land-primary" aria-hidden="true" />
              Loading site intelligence
            </div>

            <div v-else-if="intelligenceErrors.length > 0" class="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <p v-for="error in intelligenceErrors" :key="error">{{ error }}</p>
            </div>

            <div v-else-if="intelligence?.status === 'unavailable'" class="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              {{ intelligence.error }}
            </div>

            <div v-else-if="intelligence" class="mt-4 space-y-4">
              <section v-if="selectedSiteDecisionCards.length">
                <h3 class="text-sm font-semibold">Diligence next steps</h3>
                <div class="mt-2 grid gap-2">
                  <div v-for="item in selectedSiteDecisionCards" :key="item.key" class="rounded-md border border-land-border-soft bg-white p-3">
                    <div class="flex items-start justify-between gap-3">
                      <div>
                        <p class="text-sm font-semibold">{{ item.label }}</p>
                        <p class="mt-1 text-xs leading-5 text-land-ink-muted">{{ item.summary }}</p>
                      </div>
                      <StateBadge :label="item.tone === 'green' ? 'Strong' : item.tone === 'amber' ? 'Review' : 'Context'" :tone="item.tone" />
                    </div>
                    <p class="mt-2 text-xs leading-5 text-land-ink-soft">{{ item.nextAction }}</p>
                  </div>
                </div>
              </section>

              <section>
                <div class="mb-2 flex items-center justify-between gap-2">
                  <h3 class="text-sm font-semibold">Demographics</h3>
                  <StateBadge :label="demographicsStatusLabel" :tone="demographicsStatusTone" />
                </div>
                <div v-if="intelligence.demographics?.status === 'available'" class="grid grid-cols-2 gap-2">
                  <div v-for="item in intelligence.demographics.values" :key="item.key" class="rounded-md border border-land-border-soft bg-white p-3">
                    <p class="text-xs text-land-ink-soft">{{ item.label }}</p>
                    <p class="mt-1 text-sm font-semibold">{{ formatMetric(item.value, item.format) }}</p>
                  </div>
                  <div class="rounded-md border border-land-border-soft bg-white p-3">
                    <p class="text-xs text-land-ink-soft">Unemployment rate</p>
                    <p class="mt-1 text-sm font-semibold">{{ formatPercent(intelligence.demographics.metrics?.unemploymentRate) }}</p>
                  </div>
                </div>
                <p v-else class="rounded-md border border-land-border-soft bg-white p-3 text-xs leading-5 text-land-ink-muted">{{ intelligence.demographics?.error || 'Demographic data is not available.' }}</p>
              </section>

              <section>
                <h3 class="text-sm font-semibold">Infrastructure and risk</h3>
                <div class="mt-2 grid gap-2">
                  <div v-for="layer in intelligence.layers" :key="layer.key" class="rounded-md border border-land-border-soft bg-white p-3">
                    <div class="flex items-start justify-between gap-3">
                      <div>
                        <p class="text-sm font-semibold">{{ layer.name }}</p>
                        <p class="mt-1 text-xs leading-5 text-land-ink-muted">{{ layer.label }}</p>
                      </div>
                      <StateBadge :label="layer.status === 'available' ? String(layer.count ?? 0) : 'Offline'" :tone="layer.tone" />
                    </div>
                  </div>
                </div>
                <p class="mt-2 text-xs leading-5 text-land-ink-soft">{{ intelligence.bboxNote }}</p>
              </section>
            </div>

            <div class="mt-4 flex gap-2">
              <a :href="selectedRecord.path" class="inline-flex h-9 flex-1 items-center justify-center gap-2 rounded-md bg-land-primary px-3 text-sm font-semibold text-white hover:bg-land-primary-hover">
                Open record
                <ExternalLink class="size-4" aria-hidden="true" />
              </a>
              <button type="button" class="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-land-border bg-white px-3 text-sm font-medium text-land-ink-muted hover:bg-land-surface-muted" @click="focusSelectedRecord">
                <LocateFixed class="size-4" aria-hidden="true" />
                Focus
              </button>
            </div>
          </div>

          <div v-else class="p-4 text-sm leading-6 text-land-ink-muted">
            Select an opportunity or parcel marker to load live demographics, infrastructure counts, flood exposure, and source status.
          </div>
        </section>

        <section class="rounded-md border border-land-border bg-land-surface shadow-sm">
          <div class="border-b border-land-border-soft p-4">
            <div class="flex items-start justify-between gap-3">
              <div>
                <h2 class="text-lg font-semibold tracking-normal">Compare sites</h2>
                <p class="mt-1 text-xs leading-5 text-land-ink-muted">Shortlist 2-5 mapped opportunities for a quick acquisition tradeoff view.</p>
              </div>
              <StateBadge :label="`${comparisonRecords.length}/5`" :tone="comparisonRecords.length > 0 ? 'violet' : 'slate'" />
            </div>
          </div>
          <div class="p-4">
            <div v-if="comparisonTableRows.length > 0" class="space-y-2">
              <div v-for="row in comparisonTableRows" :key="row.id" class="rounded-md border border-land-border-soft bg-white p-3">
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0">
                    <p class="truncate text-sm font-semibold">{{ row.name }}</p>
                    <p class="mt-1 text-xs text-land-ink-soft">{{ row.market }} / {{ row.stage }} / {{ formatNumber(row.acreage) }} acres</p>
                    <div class="mt-2 flex flex-wrap gap-1.5">
                      <StateBadge :label="row.health" :tone="compareTone(row)" />
                      <StateBadge v-if="row.overdue > 0" :label="`${row.overdue} overdue`" tone="red" />
                      <StateBadge v-if="row.blocked > 0" :label="`${row.blocked} blocked`" tone="red" />
                    </div>
                  </div>
                  <button type="button" class="grid size-8 shrink-0 place-items-center rounded-md border border-land-border text-land-ink-muted hover:bg-land-surface-muted" aria-label="Remove from comparison" @click="toggleComparisonRecord(row.id)">
                    <X class="size-4" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <button type="button" class="inline-flex h-9 w-full items-center justify-center rounded-md border border-land-border bg-white px-3 text-sm font-semibold text-land-ink-muted hover:bg-land-surface-muted" @click="clearComparison">
                Clear comparison
              </button>
            </div>
            <p v-else class="text-sm leading-6 text-land-ink-muted">Use the Compare buttons in the opportunity list to build a side-by-side shortlist.</p>
          </div>
        </section>

        <section class="rounded-md border border-land-border bg-land-surface shadow-sm">
          <div class="border-b border-land-border-soft p-4">
            <h2 class="text-lg font-semibold tracking-normal">Opportunities</h2>
          </div>
          <div class="max-h-[360px] divide-y divide-land-border-soft overflow-auto">
            <button v-for="point in visibleOpportunityPoints" :key="point.id" type="button" class="block w-full px-4 py-3 text-left hover:bg-land-surface-muted" @click="selectRecord('opportunity', point)">
              <div class="flex gap-3">
                <img v-if="point.primaryImage" :src="point.primaryImage.url" :alt="point.primaryImage.filename" class="size-12 rounded-md object-cover">
                <div class="min-w-0 flex-1">
                  <p class="truncate text-sm font-semibold">{{ point.name }}</p>
                  <p class="mt-1 text-xs text-land-ink-soft">{{ point.market }} / {{ point.stageLabel }} / {{ formatNumber(point.acreage) }} acres</p>
                  <div class="mt-2 flex flex-wrap gap-1.5">
                    <StateBadge :label="point.health?.stateLabel || 'Unknown'" :tone="point.health?.tone || 'slate'" />
                    <StateBadge v-if="point.taskSummary?.overdue" :label="`${point.taskSummary.overdue} overdue`" tone="red" />
                    <StateBadge v-if="point.diligenceSummary?.blocked" :label="`${point.diligenceSummary.blocked} blocked`" tone="red" />
                  </div>
                </div>
                <span
                  role="button"
                  tabindex="0"
                  class="inline-flex h-8 shrink-0 items-center rounded-md border px-2 text-xs font-semibold"
                  :class="isComparisonRecordSelected(point.id) ? 'border-violet-200 bg-violet-50 text-violet-700' : 'border-land-border bg-white text-land-ink-muted'"
                  @click.stop="toggleComparisonRecord(point.id)"
                  @keydown.enter.stop.prevent="toggleComparisonRecord(point.id)"
                  @keydown.space.stop.prevent="toggleComparisonRecord(point.id)"
                >
                  {{ isComparisonRecordSelected(point.id) ? 'Added' : 'Compare' }}
                </span>
              </div>
            </button>
            <div v-if="visibleOpportunityPoints.length === 0" class="px-4 py-8 text-center text-sm text-land-ink-muted">No opportunities in this view.</div>
          </div>
        </section>

        <section class="rounded-md border border-land-border bg-land-surface shadow-sm">
          <div class="border-b border-land-border-soft p-4">
            <h2 class="text-lg font-semibold tracking-normal">Parcels</h2>
          </div>
          <div class="max-h-[280px] divide-y divide-land-border-soft overflow-auto">
            <button v-for="parcel in visibleParcels.slice(0, 12)" :key="`${parcel.opportunityId}-${parcel.id}`" type="button" class="block w-full px-4 py-3 text-left hover:bg-land-surface-muted" @click="selectRecord('parcel', parcel)">
              <p class="truncate text-sm font-semibold">{{ parcel.displayName }}</p>
              <p class="mt-1 text-xs text-land-ink-soft">{{ parcel.opportunityName }} / {{ formatNumber(parcel.acreage) }} acres</p>
              <div class="mt-2">
                <StateBadge :label="parcel.health?.stateLabel || 'Unknown'" :tone="parcel.health?.tone || 'slate'" />
              </div>
            </button>
            <div v-if="visibleParcels.length === 0" class="px-4 py-8 text-center text-sm text-land-ink-muted">No parcels in this view.</div>
          </div>
        </section>
      </aside>
    </section>

    <LayerInfoCard
      v-if="infoLayer"
      :layer="infoLayer"
      :position="infoCardPosition"
      @mouseenter="cancelCloseLayerInfo"
      @mouseleave="scheduleCloseLayerInfo"
    />
  </AppShell>
</template>

<script setup>
import { LngLatBounds, Map, Marker, NavigationControl, Popup, ScaleControl, config, geocoding } from '@maptiler/sdk'
import '@maptiler/sdk/dist/maptiler-sdk.css'
import {
  Building2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Flame,
  Layers,
  LoaderCircle,
  LocateFixed,
  MapPinned,
  MessageSquarePlus,
  PanelLeftClose,
  PanelLeftOpen,
  Info,
  PanelRightClose,
  PanelRightOpen,
  Plus,
  Satellite,
  Search,
  SlidersHorizontal,
  Star,
  Users,
  Waves,
  X,
  Zap
} from '@lucide/vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { normalizeErrors, requestJson } from '@/api/client'
import AppShell from '@/components/shared/app_shell.vue'
import StateBadge from '@/components/shared/state_badge.vue'
import { isAbortError, visibleMarkerParcels } from './map_helpers'
import { circleColorExpression, circleRadiusExpression } from './map/circle_style'
import { LAYER_RECIPES, availableRecipeLayerKeys, filterLayerCategories } from './map/layer_recipes'
import { lineColorExpression, lineWidthExpression } from './map/line_style'
import { layerUsesMarkerIcon, markerIconImageNameForLayer, markerIconSizeExpression, registerMapMarkerIcons } from './map/map_icons'
import { useRecordLayer } from './map/use_record_layer'
import { createFetchQueue } from './map/fetch_queue'
import MapLoadingOverlay from './map/map_loading_overlay.vue'
import LayerInfoCard from '@/components/opportunities/map/layer_info_card.vue'
import { cardPlacement } from '@/components/opportunities/map/layer_info_card'
import { LOCATION_SEARCH_TYPES, cameraForGpsCoordinates, cameraForLocationFeature, locationFeatureLabel, parseGpsCoordinateQuery } from './map/location_search'
import { compareTone, comparisonRows, featureActionHints, siteDecisionCards } from './map/site_decisions'
import { VOLTAGE_TIERS } from './map/voltage_tiers'

const props = defineProps({
  payload: {
    type: Object,
    required: true
  }
})

function readStoredBoolean(key, fallback) {
  const value = globalThis.localStorage?.getItem(key)
  if (value === null || value === undefined) return fallback

  return value === 'true'
}

function persistStoredBoolean(key, value) {
  globalThis.localStorage?.setItem(key, String(value))
}

function readStoredArray(key, fallback = []) {
  const value = globalThis.localStorage?.getItem(key)
  if (!value) return fallback

  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : fallback
  } catch {
    return fallback
  }
}

function persistStoredArray(key, value) {
  globalThis.localStorage?.setItem(key, JSON.stringify(value))
}

const initialSearchParams = new URLSearchParams(globalThis.location?.search || '')
const mapPayload = ref(props.payload)
const infoLayer = ref(null)
const infoLayerKey = ref(null)
const infoCardPosition = ref({ top: 0, left: 0, placement: 'right' })
let infoOpenTimer = null
let infoCloseTimer = null

const openLayerInfo = (layer, event) => {
  const trigger = event.currentTarget
  if (infoCloseTimer) { clearTimeout(infoCloseTimer); infoCloseTimer = null }
  if (infoOpenTimer) clearTimeout(infoOpenTimer)
  infoOpenTimer = setTimeout(() => {
    const rect = trigger.getBoundingClientRect()
    infoCardPosition.value = cardPlacement(
      { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height },
      { width: 320, height: 360 },
      { width: globalThis.innerWidth, height: globalThis.innerHeight }
    )
    infoLayer.value = layer
    infoLayerKey.value = layer.key
  }, 120)
}

const scheduleCloseLayerInfo = () => {
  if (infoOpenTimer) { clearTimeout(infoOpenTimer); infoOpenTimer = null }
  if (infoCloseTimer) clearTimeout(infoCloseTimer)
  infoCloseTimer = setTimeout(() => {
    infoLayer.value = null
    infoLayerKey.value = null
  }, 150)
}

const cancelCloseLayerInfo = () => {
  if (infoCloseTimer) { clearTimeout(infoCloseTimer); infoCloseTimer = null }
}

const closeLayerInfo = () => {
  if (infoOpenTimer) { clearTimeout(infoOpenTimer); infoOpenTimer = null }
  if (infoCloseTimer) { clearTimeout(infoCloseTimer); infoCloseTimer = null }
  infoLayer.value = null
  infoLayerKey.value = null
}
const notice = ref('')
const errors = ref([])
const selectedStage = ref('all')
const selectedRecordLayer = ref('all')
const needsActionOnly = ref(false)
const layerSearchQuery = ref('')
const leftMapPanelCollapsed = ref(readStoredBoolean('landport:map:left-panel-collapsed', false))
const rightMapPanelCollapsed = ref(readStoredBoolean('landport:map:right-panel-collapsed', false))
const recordHealthLegendCollapsed = ref(readStoredBoolean('landport:map:record-health-legend-collapsed', false))
const visibleLayersLegendCollapsed = ref(readStoredBoolean('landport:map:visible-layers-legend-collapsed', false))
const mapElement = ref(null)
const mapZoom = ref(0)
const styleError = ref(false)
const mapLoading = ref(true)
const showMapLoader = computed(() => mapLoading.value && Boolean(apiKey.value) && !styleError.value)
const locationSearchQuery = ref('')
const locationSearchLoading = ref(false)
const locationSearchError = ref('')
const locationSearchResult = ref(null)
const selectedRecord = ref(null)
const intelligence = ref(null)
const intelligenceLoading = ref(false)
const intelligenceErrors = ref([])
const radiusMiles = ref(Number(mapPayload.value.layers?.defaultRadiusMiles || 10))
const activeLayerKeys = ref(new Set())
const expandedContextSections = ref(new Set())
const expandedLayerCategoryKeys = ref(new Set())
const layerCategoryExpansionInitialized = ref(false)
const layerOpacity = ref({})
const layerErrors = ref({})
const layerHints = ref({})
const layerFeatureCounts = ref({})
const loadingLayerKeys = ref(new Set())
const favoriteLayerKeys = ref(new Set(readStoredArray('landport:map:favorite-layer-keys', [])))
const selectedInfrastructureFeature = ref(null)
const featureNotes = ref([])
const featureNotesLoading = ref(false)
const featureNoteSaving = ref(false)
const featureNoteBody = ref('')
const featureNoteErrors = ref([])
const activeParcelSourceKeys = ref(new Set())
const parcelSourceOpacity = ref({})
const parcelSourceErrors = ref({})
const parcelSourceHints = ref({})
const loadingParcelSourceKeys = ref(new Set())
const manualParcelLayerKeys = ref(new Set())
const manualParcelLookupNotices = ref({})
const manualParcelLookupMode = ref('nearby')
const manualParcelLookupRadiusMiles = ref(0.25)
const manualParcelLookupResults = ref({})
const manualParcelDrawActive = ref(false)
const manualParcelDrawVertices = ref([])
const selectedExternalParcel = ref(null)
const selectedParcelTrackOpportunityId = ref('')
const trackingExternalParcel = ref(false)
const externalParcelErrors = ref([])
const focusedOpportunityId = ref(initialSearchParams.get('opportunity_id') || initialSearchParams.get('opportunity') || '')
const comparisonRecordIds = ref(new Set())

const layerRecipes = LAYER_RECIPES
const layerModes = [
  { value: 'all', label: 'All' },
  { value: 'opportunities', label: 'Sites' },
  { value: 'parcels', label: 'Parcels' }
]
const manualParcelLookupModes = [
  { value: 'nearby', label: 'Nearby' },
  { value: 'point', label: 'Under pin' },
  { value: 'extent', label: 'Map view' },
  { value: 'draw', label: 'Draw area' }
]
const legendItems = [
  { label: 'Healthy', class: 'bg-emerald-500' },
  { label: 'Needs attention', class: 'bg-amber-500' },
  { label: 'Blocked / overdue', class: 'bg-red-500' },
  { label: 'Incomplete', class: 'bg-slate-400' }
]
const INFRASTRUCTURE_DETAIL_LAYERS = {
  census_tracts_acs_2024: {
    label: 'Census tract',
    icon: Users,
    emptyMessage: 'No notes for this census tract yet.'
  },
  census_block_groups_acs_2024: {
    label: 'Census block group',
    icon: Users,
    emptyMessage: 'No notes for this census block group yet.'
  },
  transmission_lines_hifld: {
    label: 'Transmission line',
    icon: Zap,
    emptyMessage: 'No notes for this transmission line yet.'
  },
  transmission_lines_cached: {
    label: 'Transmission line',
    icon: Zap,
    emptyMessage: 'No notes for this transmission line yet.'
  },
  electric_substations_hifld: {
    label: 'Substation',
    icon: Zap,
    emptyMessage: 'No notes for this substation yet.'
  },
  electric_substations_cached: {
    label: 'Substation',
    icon: Zap,
    emptyMessage: 'No notes for this substation yet.'
  },
  power_plants_hifld: {
    label: 'Power plant',
    icon: Building2,
    emptyMessage: 'No notes for this power plant yet.'
  },
  utility_hosting_capacity: {
    label: 'Hosting capacity',
    icon: Zap,
    emptyMessage: 'No notes for this hosting-capacity feature yet.'
  },
  hosting_capacity_dominion: {
    label: 'Dominion hosting capacity',
    icon: Zap,
    emptyMessage: 'No notes for this Dominion hosting-capacity feature yet.'
  },
  hosting_capacity_aep_ohio: {
    label: 'AEP Ohio hosting capacity',
    icon: Zap,
    emptyMessage: 'No notes for this AEP Ohio hosting-capacity feature yet.'
  },
  control_areas_hifld: {
    label: 'Balancing authority',
    icon: Zap,
    emptyMessage: 'No notes for this control area yet.'
  },
  egrid_subregions_epa: {
    label: 'eGRID subregion',
    icon: Waves,
    emptyMessage: 'No notes for this eGRID subregion yet.'
  },
  nietc_corridors_doe: {
    label: 'NIETC corridor',
    icon: Zap,
    emptyMessage: 'No notes for this NIETC corridor yet.'
  },
  electric_retail_service_territories_hifld: {
    label: 'Electric service territory',
    icon: Zap,
    emptyMessage: 'No notes for this service territory yet.'
  },
  interconnection_queue_projects: {
    label: 'Exact queue project',
    icon: Zap,
    emptyMessage: 'No notes for this queue project yet.'
  },
  interconnection_queue_county_summaries: {
    label: 'Queue county summary',
    icon: Zap,
    emptyMessage: 'No notes for this county queue summary yet.'
  },
  lbnl_interconnection_queue_counties: {
    label: 'LBNL queue county summary',
    icon: Zap,
    emptyMessage: 'No notes for this LBNL queue summary yet.'
  },
  data_center_sites: {
    label: 'AI data center',
    icon: Building2,
    emptyMessage: 'No notes for this data center yet.'
  },
  operational_data_centers: {
    label: 'Operational data center',
    icon: Building2,
    emptyMessage: 'No notes for this data center yet.'
  },
  internet_exchanges: {
    label: 'Internet exchange',
    icon: Layers,
    emptyMessage: 'No notes for this internet exchange yet.'
  },
  cable_landing_stations_osm: {
    label: 'Cable landing station',
    icon: Waves,
    emptyMessage: 'No notes for this cable landing station yet.'
  },
  natural_gas_pipelines_eia: {
    label: 'Natural gas pipeline',
    icon: Flame,
    emptyMessage: 'No notes for this pipeline yet.'
  },
  lng_terminals_eia: {
    label: 'LNG terminal',
    icon: Flame,
    emptyMessage: 'No notes for this LNG terminal yet.'
  },
  gas_compressor_stations_hifld: {
    label: 'Gas compressor station',
    icon: Flame,
    emptyMessage: 'No notes for this compressor station yet.'
  },
  gas_processing_plants_hifld: {
    label: 'Gas processing plant',
    icon: Flame,
    emptyMessage: 'No notes for this processing plant yet.'
  },
  gas_storage_hifld: {
    label: 'Natural gas storage',
    icon: Flame,
    emptyMessage: 'No notes for this gas storage facility yet.'
  },
  crude_oil_pipelines_eia: {
    label: 'Crude oil pipeline',
    icon: Flame,
    emptyMessage: 'No notes for this pipeline yet.'
  },
  petroleum_product_pipelines_eia: {
    label: 'Petroleum product pipeline',
    icon: Flame,
    emptyMessage: 'No notes for this pipeline yet.'
  },
  ngl_pipelines_eia: {
    label: 'HGL / NGL pipeline',
    icon: Flame,
    emptyMessage: 'No notes for this pipeline yet.'
  },
  fema_flood_hazard_zones: {
    label: 'Flood hazard zone',
    icon: Waves,
    emptyMessage: 'No notes for this flood zone yet.'
  },
  fema_national_risk_index_counties: {
    label: 'Natural hazard risk county',
    icon: Waves,
    emptyMessage: 'No notes for this hazard risk area yet.'
  },
  fema_national_risk_index_tracts: {
    label: 'Natural hazard risk tract',
    icon: Waves,
    emptyMessage: 'No notes for this hazard risk tract yet.'
  },
  epa_echo_facilities: {
    label: 'EPA regulated facility',
    icon: Building2,
    emptyMessage: 'No notes for this EPA facility yet.'
  },
  nwi_wetlands: {
    label: 'Wetland',
    icon: Waves,
    emptyMessage: 'No notes for this wetland feature yet.'
  },
  usfws_critical_habitat: {
    label: 'Critical habitat',
    icon: Users,
    emptyMessage: 'No notes for this habitat feature yet.'
  },
  padus_protected_areas: {
    label: 'Protected area',
    icon: Building2,
    emptyMessage: 'No notes for this protected area yet.'
  },
  epa_nonattainment_areas: {
    label: 'Air nonattainment area',
    icon: Waves,
    emptyMessage: 'No notes for this nonattainment area yet.'
  },
  opportunity_zones_hud: {
    label: 'Opportunity Zone',
    icon: Building2,
    emptyMessage: 'No notes for this Opportunity Zone yet.'
  },
  foreign_trade_zones_ftz: {
    label: 'Foreign Trade Zone area',
    icon: Building2,
    emptyMessage: 'No notes for this FTZ service area yet.'
  },
  foreign_trade_zone_sites_ftz: {
    label: 'Foreign Trade Zone site',
    icon: Building2,
    emptyMessage: 'No notes for this FTZ site yet.'
  },
  enterprise_zones_hud: {
    label: 'Enterprise / empowerment zone',
    icon: Building2,
    emptyMessage: 'No notes for this incentive zone yet.'
  },
  tx_groundwater_wells_twdb: {
    label: 'TX groundwater well',
    icon: Waves,
    emptyMessage: 'No notes for this groundwater well yet.'
  },
  tx_water_rights_tceq: {
    label: 'TX water right',
    icon: Waves,
    emptyMessage: 'No notes for this water-right record yet.'
  },
  regrid_zoneomics_zoning: {
    label: 'Standardized zoning',
    icon: Building2,
    emptyMessage: 'No notes for this zoning feature yet.'
  },
  usda_ssurgo_soils_wms: {
    label: 'SSURGO soil map unit',
    icon: Layers,
    emptyMessage: 'No notes for this soil map unit yet.'
  },
  ntad_national_highway_freight_network: {
    label: 'Highway freight route',
    icon: MapPinned,
    emptyMessage: 'No notes for this freight route yet.'
  },
  ntad_intermodal_rail_facilities: {
    label: 'Intermodal rail facility',
    icon: MapPinned,
    emptyMessage: 'No notes for this intermodal facility yet.'
  },
  ntad_faf_network_nodes: {
    label: 'FAF freight node',
    icon: MapPinned,
    emptyMessage: 'No notes for this freight node yet.'
  }
}

let map
let mapReady = false
let loaderStartedAt = 0
let mapLoaderTimer = null
let mapLoaderWatchdog = null
const MIN_LOADER_MS = 600
const MAX_LOADER_MS = 8000
let markerIconsReady = false
let markers = []
let layerRefreshTimer
let intelligenceTimer
let initialFocusApplied = false
let locationSearchRequestId = 0
const loadedLayerBboxes = new globalThis.Map()
const layerRequestVersions = new globalThis.Map()
const layerAbortControllers = new globalThis.Map()
const interactiveLayerHandlers = new globalThis.Map()
let pendingFeatureClickSelections = []
let pendingFeatureClickTimer
const TRACKED_PARCEL_BOUNDARY_SOURCE_ID = 'landport-tracked-parcel-boundaries'
const TRACKED_PARCEL_BOUNDARY_FILL_ID = 'landport-tracked-parcel-boundaries-fill'
const TRACKED_PARCEL_BOUNDARY_LINE_ID = 'landport-tracked-parcel-boundaries-line'
const loadedParcelSourceBboxes = new globalThis.Map()
const parcelSourceRequestVersions = new globalThis.Map()
const parcelSourceAbortControllers = new globalThis.Map()
const parcelSourceLayerHandlers = new globalThis.Map()
const MANUAL_PARCEL_DRAW_SOURCE_ID = 'landport-manual-parcel-draw'
const MANUAL_PARCEL_DRAW_FILL_ID = 'landport-manual-parcel-draw-fill'
const MANUAL_PARCEL_DRAW_LINE_ID = 'landport-manual-parcel-draw-line'
const MANUAL_PARCEL_DRAW_POINTS_ID = 'landport-manual-parcel-draw-points'
const MANUAL_PARCEL_DRAW_MAX_SQUARE_MILES = 4

const workspace = computed(() => mapPayload.value.workspace || {})
const apiKey = computed(() => mapPayload.value.maptilerKey || '')
const locationSearchDisabled = computed(() => (
  locationSearchLoading.value ||
  locationSearchQuery.value.trim().length === 0 ||
  !apiKey.value ||
  styleError.value ||
  mapLoading.value
))
const points = computed(() => mapPayload.value.points || [])
const parcels = computed(() => mapPayload.value.parcels || [])
const trackableOpportunities = computed(() => [ ...points.value, ...(mapPayload.value.unmapped || []) ])
const center = computed(() => mapPayload.value.center || { latitude: 39.5, longitude: -98.35 })
const stages = computed(() => mapPayload.value.stages || [])
const externalLayers = computed(() => mapPayload.value.layers?.layers || [])
const parcelSources = computed(() => mapPayload.value.parcelSources?.sources || [])
const renderableLayers = computed(() => externalLayers.value.filter((layer) => canRenderLayer(layer)))
const renderableParcelSources = computed(() => parcelSources.value.filter((source) => canAutoRenderParcelSource(source)))
const manualParcelSources = computed(() => parcelSources.value.filter((source) => isManualParcelSource(source) && source.enabled))
const visibleManualParcelSources = computed(() => manualParcelSources.value.filter((source) => manualParcelLayerKeys.value.has(source.key)))
const enabledParcelSources = computed(() => renderableParcelSources.value.filter((source) => source.enabled))
const activeRenderableLayers = computed(() => externalLayers.value.filter((layer) => isLayerActive(layer.key) && canRenderLayer(layer)))
const activeRenderableParcelSources = computed(() => renderableParcelSources.value.filter((source) => isParcelSourceActive(source.key) && source.enabled))
const activeMapOverlayCount = computed(() => activeRenderableLayers.value.length + activeRenderableParcelSources.value.length + visibleManualParcelSources.value.length)
const mapStatusLabel = computed(() => `${activeMapOverlayCount.value} overlay${activeMapOverlayCount.value === 1 ? '' : 's'} active`)
const isAnyLayerLoading = computed(() => loadingLayerKeys.value.size > 0 || loadingParcelSourceKeys.value.size > 0)
const favoriteLayers = computed(() => renderableLayers.value.filter((layer) => favoriteLayerKeys.value.has(layer.key)))
const manualParcelDrawReady = computed(() => manualParcelDrawVertices.value.length >= 3)
const manualParcelDrawAreaSquareMiles = computed(() => polygonBboxAreaSquareMiles(manualParcelDrawVertices.value))
const manualParcelDrawLookupReady = computed(() => manualParcelDrawReady.value && manualParcelDrawAreaSquareMiles.value <= MANUAL_PARCEL_DRAW_MAX_SQUARE_MILES)
const manualParcelDrawStatus = computed(() => {
  if (manualParcelDrawActive.value) return 'Click the map to place polygon vertices. Finish when the search area is closed.'
  if (manualParcelDrawReady.value && manualParcelDrawAreaSquareMiles.value > MANUAL_PARCEL_DRAW_MAX_SQUARE_MILES) return `Drawn area is about ${manualParcelDrawAreaSquareMiles.value.toFixed(1)} sq mi. Zoom in or draw less than ${MANUAL_PARCEL_DRAW_MAX_SQUARE_MILES} sq mi before running a paid lookup.`
  if (manualParcelDrawReady.value) return `${manualParcelDrawVertices.value.length} vertices drawn. Run the paid lookup to fetch parcel boundaries inside the shape.`
  if (manualParcelDrawVertices.value.length > 0) return `${manualParcelDrawVertices.value.length} vertices placed. Add ${3 - manualParcelDrawVertices.value.length} more to make a parcel search area.`

  return 'Draw a polygon around the substation, frontage, or land assembly area before spending a paid parcel call.'
})
const manualParcelLookupAnyLoading = computed(() => manualParcelSources.value.some((source) => loadingParcelSourceKeys.value.has(source.key)))
const manualParcelLookupDisabledForSelectedFeatureAll = computed(() => {
  if (manualParcelSources.value.length === 0 || manualParcelLookupAnyLoading.value) return true
  if (manualParcelLookupMode.value === 'draw') return !manualParcelDrawLookupReady.value

  return !selectedInfrastructureFeature.value?.coordinates
})
const manualParcelSourceNames = computed(() => formatShortList(manualParcelSources.value.map((source) => source.name).filter(Boolean)))
const voltageLegendItems = VOLTAGE_TIERS
const voltageLegendVisible = computed(() => activeRenderableLayers.value.some((layer) => [
  'transmission_lines_hifld',
  'transmission_lines_cached',
  'electric_substations_hifld',
  'electric_substations_cached'
].includes(layer.key)))
const mapGridClass = computed(() => {
  if (leftMapPanelCollapsed.value && rightMapPanelCollapsed.value) return 'min-[1500px]:grid-cols-[minmax(0,1fr)]'
  if (leftMapPanelCollapsed.value) return 'min-[1500px]:grid-cols-[minmax(560px,1fr)_340px]'
  if (rightMapPanelCollapsed.value) return 'min-[1500px]:grid-cols-[300px_minmax(560px,1fr)]'

  return 'min-[1500px]:grid-cols-[300px_minmax(560px,1fr)_340px]'
})

const layerCategories = computed(() => {
  const categories = new globalThis.Map()

  externalLayers.value.forEach((layer) => {
    if (!categories.has(layer.category)) {
      categories.set(layer.category, {
        key: layer.category,
        label: layer.categoryLabel || titleize(layer.category),
        layers: []
      })
    }
    categories.get(layer.category).layers.push(layer)
  })

  return Array.from(categories.values()).sort((left, right) => left.label.localeCompare(right.label))
})
const visibleLayerCategories = computed(() => filterLayerCategories(layerCategories.value, layerSearchQuery.value))

const visibleOpportunityPoints = computed(() => {
  if (selectedRecordLayer.value === 'parcels') return []

  return points.value.filter((point) => matchesMapFilters(point))
})

const visibleParcels = computed(() => {
  if (selectedRecordLayer.value === 'opportunities') return []

  return parcels.value.filter((parcel) => matchesMapFilters(parcel))
})
// Reuse the built Feature object for a parcel that hasn't changed (same object
// reference) so a single parcel update doesn't reallocate the whole collection.
// NOTE: globalThis.Map — the bare `Map` is shadowed by the @maptiler/sdk import.
const parcelBoundaryFeatureCache = new globalThis.Map()
const visibleParcelBoundaryFeatures = computed(() => {
  const seen = new Set()
  const features = []
  for (const parcel of visibleParcels.value) {
    if (!validParcelGeometry(parcel.geometry)) continue

    seen.add(parcel.id)
    const cached = parcelBoundaryFeatureCache.get(parcel.id)
    if (cached && cached.parcel === parcel) {
      features.push(cached.feature)
      continue
    }

    const feature = {
      type: 'Feature',
      id: parcel.id,
      geometry: parcel.geometry,
      properties: {
        id: parcel.id,
        opportunityId: parcel.opportunityId,
        displayName: parcel.displayName,
        apn: parcel.apn,
        acreage: parcel.acreage,
        primary: parcel.primary
      }
    }
    parcelBoundaryFeatureCache.set(parcel.id, { parcel, feature })
    features.push(feature)
  }

  for (const key of parcelBoundaryFeatureCache.keys()) {
    if (!seen.has(key)) parcelBoundaryFeatureCache.delete(key)
  }
  return features
})
const visibleMapMarkerParcels = computed(() => visibleMarkerParcels({
  layerMode: selectedRecordLayer.value,
  opportunities: visibleOpportunityPoints.value,
  parcels: visibleParcels.value
}))
const recordLayer = useRecordLayer({
  getMap: () => map,
  isReady: () => mapReady,
  getOpportunities: () => visibleOpportunityPoints.value,
  getParcels: () => visibleMapMarkerParcels.value,
  getSelectedRecord: () => selectedRecord.value,
  onSelectRecord: (kind, record) => selectRecord(kind, record),
  emptyFeatureCollection: () => ({ type: 'FeatureCollection', features: [] })
})

const selectedRecordLocation = computed(() => {
  if (!selectedRecord.value) return ''
  if (selectedRecord.value.kind === 'parcel') {
    return `${selectedRecord.value.county || 'Unknown county'}, ${selectedRecord.value.state || ''}`
  }

  return `${selectedRecord.value.market} / ${selectedRecord.value.county}, ${selectedRecord.value.state}`
})
const demographicsStatusLabel = computed(() => {
  if (intelligence.value?.demographics?.status === 'available') return 'Live'
  if (String(intelligence.value?.demographics?.error || '').includes('not configured')) return 'Needs key'
  if (String(intelligence.value?.demographics?.error || '').includes('rejected')) return 'Key issue'

  return 'Unavailable'
})
const demographicsStatusTone = computed(() => intelligence.value?.demographics?.status === 'available' ? 'green' : 'amber')
const selectedInfrastructureLayerConfig = computed(() => INFRASTRUCTURE_DETAIL_LAYERS[selectedInfrastructureFeature.value?.layerKey] || {})
const selectedInfrastructureLabel = computed(() => selectedInfrastructureLayerConfig.value.label || selectedInfrastructureFeature.value?.layerName || 'Map feature')
const selectedInfrastructureIcon = computed(() => selectedInfrastructureLayerConfig.value.icon || categoryIcon(selectedInfrastructureFeature.value?.layerCategory) || Layers)
const selectedInfrastructureEmptyMessage = computed(() => selectedInfrastructureLayerConfig.value.emptyMessage || `No notes for this ${selectedInfrastructureLabel.value.toLowerCase()} yet.`)
const isHostingCapacityLayerKey = (key) => key === 'utility_hosting_capacity' || key?.startsWith('hosting_capacity_')
const selectedInfrastructureFacts = computed(() => {
  const feature = selectedInfrastructureFeature.value
  if (!feature) return []

  if (feature.layerKey === 'natural_gas_pipelines_eia') return naturalGasPipelineFacts(feature)
  if (feature.layerKey === 'lng_terminals_eia') return lngTerminalFacts(feature)
  if (['gas_compressor_stations_hifld', 'gas_processing_plants_hifld', 'gas_storage_hifld'].includes(feature.layerKey)) return gasFacilityFacts(feature)
  if (['crude_oil_pipelines_eia', 'petroleum_product_pipelines_eia', 'ngl_pipelines_eia'].includes(feature.layerKey)) return liquidsPipelineFacts(feature)
  if (['transmission_lines_hifld', 'transmission_lines_cached'].includes(feature.layerKey)) return transmissionLineFacts(feature)
  if (feature.layerKey === 'power_plants_hifld') return powerPlantFacts(feature)
  if (isHostingCapacityLayerKey(feature.layerKey)) return hostingCapacityFacts(feature)
  if (feature.layerKey === 'control_areas_hifld') return controlAreaFacts(feature)
  if (feature.layerKey === 'egrid_subregions_epa') return egridSubregionFacts(feature)
  if (feature.layerKey === 'nietc_corridors_doe') return nietcCorridorFacts(feature)
  if (feature.layerKey === 'electric_retail_service_territories_hifld') return utilityTerritoryFacts(feature)
  if (feature.layerKey === 'interconnection_queue_projects') return interconnectionQueueFacts(feature)
  if (['interconnection_queue_county_summaries', 'lbnl_interconnection_queue_counties'].includes(feature.layerKey)) return interconnectionQueueCountyFacts(feature)
  if (['data_center_sites', 'operational_data_centers'].includes(feature.layerKey)) return dataCenterSiteFacts(feature)
  if (feature.layerKey === 'internet_exchanges') return internetExchangeFacts(feature)
  if (feature.layerKey === 'cable_landing_stations_osm') return cableLandingFacts(feature)
  if (feature.layerKey === 'fema_flood_hazard_zones') return floodZoneFacts(feature)
  if (['fema_national_risk_index_counties', 'fema_national_risk_index_tracts'].includes(feature.layerKey)) return naturalHazardRiskFacts(feature)
  if (feature.layerKey === 'epa_echo_facilities') return epaFacilityFacts(feature)
  if (['nwi_wetlands', 'usfws_critical_habitat', 'padus_protected_areas', 'epa_nonattainment_areas'].includes(feature.layerKey)) return environmentalConstraintFacts(feature)
  if (['opportunity_zones_hud', 'foreign_trade_zones_ftz', 'foreign_trade_zone_sites_ftz', 'enterprise_zones_hud'].includes(feature.layerKey)) return incentiveZoneFacts(feature)
  if (feature.layerKey === 'tx_groundwater_wells_twdb') return texasGroundwaterWellFacts(feature)
  if (feature.layerKey === 'tx_water_rights_tceq') return texasWaterRightFacts(feature)
  if (feature.layerKey === 'regrid_zoneomics_zoning') return zoningFacts(feature)
  if (feature.layerKey === 'usda_ssurgo_soils_wms') return ssurgoSoilFacts(feature)
  if (feature.layerKey?.startsWith('census_')) return censusFacts(feature)
  if (['electric_substations_hifld', 'electric_substations_cached'].includes(feature.layerKey)) return substationFacts(feature)

  return genericFeatureFacts(feature)
})
const selectedInfrastructureAttributes = computed(() => {
  const properties = selectedInfrastructureFeature.value?.properties || {}

  return Object.entries(properties)
    .filter(([key, value]) => key !== 'landport' && cleanValue(value))
    .slice(0, 40)
    .map(([key, value]) => ({
      key,
      label: titleize(key),
      value: formatAttribute(value)
    }))
})
const selectedSiteDecisionCards = computed(() => siteDecisionCards({
  selectedRecord: selectedRecord.value,
  intelligence: intelligence.value,
  radiusMiles: radiusMiles.value
}))
const comparisonRecords = computed(() => points.value.filter((point) => comparisonRecordIds.value.has(point.id)))
const comparisonTableRows = computed(() => comparisonRows(comparisonRecords.value))
const selectedInfrastructureActionHints = computed(() => featureActionHints(selectedInfrastructureFeature.value))

const substationFacts = (feature) => {
  const properties = feature.properties || {}
  const location = [cleanValue(propertyValue(properties, 'CITY')), cleanValue(propertyValue(properties, 'STATE'))].filter(Boolean).join(', ')

  return [
    { label: 'Max voltage', value: formatVoltage(propertyValue(properties, 'MAX_VOLTAG', 'MAX_VOLTAGE')) },
    { label: 'Min voltage', value: formatVoltage(propertyValue(properties, 'MIN_VOLTAG', 'MIN_VOLTAGE')) },
    { label: 'Connected lines', value: formatAttribute(propertyValue(properties, 'LINES')) },
    { label: 'Location', value: location || 'Not available' },
    { label: 'Coordinates', value: formatCoordinatePair(feature.coordinates) },
    { label: 'Source', value: formatAttribute(propertyValue(properties, 'SOURCE')) },
    { label: 'Method', value: formatAttribute(propertyValue(properties, 'METHOD')) },
    { label: 'Source date', value: formatAttributeDate(propertyValue(properties, 'DATE', 'SOURCEDATE', 'SOURCE_DATE')) }
  ]
}

const naturalGasPipelineFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'Operator', value: formatAttribute(propertyValue(properties, 'Operator', 'OPERATOR', 'OWNER')) },
    { label: 'Pipeline type', value: formatAttribute(propertyValue(properties, 'TYPEPIPE', 'TYPE')) },
    { label: 'Status', value: formatAttribute(propertyValue(properties, 'Status', 'STATUS')) },
    { label: 'Approx. segment length', value: formatMetersAsMiles(propertyValue(properties, 'Shape__Length')) },
    { label: 'Clicked point', value: formatCoordinatePair(feature.coordinates) },
    { label: 'Source', value: feature.provider || 'EIA / Esri National Maps' }
  ]
}

const lngTerminalFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'Terminal', value: formatAttribute(propertyValue(properties, 'NAME')) },
    { label: 'Owner', value: formatAttribute(propertyValue(properties, 'OWNER', 'OPERATOR')) },
    { label: 'Direction', value: formatAttribute(propertyValue(properties, 'TYPE')) },
    { label: 'Connection type', value: formatAttribute(propertyValue(properties, 'CONTYPE')) },
    { label: 'Status', value: formatAttribute(propertyValue(properties, 'STATUS')) },
    { label: 'Current capacity', value: formatLngCapacity(propertyValue(properties, 'CURRENTCAP')) },
    { label: 'Approved capacity', value: formatLngCapacity(propertyValue(properties, 'APPCAP')) },
    { label: 'Storage tanks', value: formatAttribute(propertyValue(properties, 'STORAGE')) },
    { label: 'Port', value: formatAttribute(propertyValue(properties, 'IE_PORT')) },
    { label: 'Coordinates', value: formatCoordinatePair(feature.coordinates) },
    { label: 'Source date', value: formatAttributeDate(propertyValue(properties, 'SOURCEDATE')) },
    { label: 'Source', value: feature.provider || 'HIFLD / EIA' }
  ]
}

const gasFacilityFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'Facility', value: formatAttribute(propertyValue(properties, 'NAME')) },
    { label: 'Operator', value: formatAttribute(propertyValue(properties, 'OPERATOR', 'OWNER', 'COMPNAME', 'PIPECO')) },
    { label: 'Type', value: formatAttribute(propertyValue(properties, 'TYPE')) },
    { label: 'Status', value: formatAttribute(propertyValue(properties, 'STATUS')) },
    { label: 'Certified horsepower', value: formatAttribute(propertyValue(properties, 'CERT_HP')) },
    { label: 'Gas capacity', value: formatAttribute(propertyValue(properties, 'GASCAP')) },
    { label: 'Plant flow', value: formatAttribute(propertyValue(properties, 'PLANTFLOW')) },
    { label: 'Working capacity', value: formatAttribute(propertyValue(properties, 'WORKCAP')) },
    { label: 'Max deliverability', value: formatAttribute(propertyValue(properties, 'MAXDEL')) },
    { label: 'Coordinates', value: formatCoordinatePair(feature.coordinates) },
    { label: 'Source date', value: formatAttributeDate(propertyValue(properties, 'SOURCEDATE')) },
    { label: 'Source', value: feature.provider || 'HIFLD' }
  ].filter((fact) => fact.value && fact.value !== 'Not available')
}

const liquidsPipelineFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'Pipeline', value: formatAttribute(propertyValue(properties, 'Pipename', 'PIPELINE', 'NAME')) },
    { label: 'Operator', value: formatAttribute(propertyValue(properties, 'Opername', 'Operator', 'OPERATOR', 'OWNER')) },
    { label: 'Commodity', value: feature.layerName || 'Pipeline' },
    { label: 'Approx. segment length', value: formatMetersAsMiles(propertyValue(properties, 'Shape__Length', 'Shape_Leng')) },
    { label: 'Clicked point', value: formatCoordinatePair(feature.coordinates) },
    { label: 'Source', value: feature.provider || 'EIA' }
  ]
}

const transmissionLineFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'Owner', value: formatAttribute(propertyValue(properties, 'OWNER')) },
    { label: 'Voltage', value: formatVoltage(propertyValue(properties, 'VOLTAGE')) },
    { label: 'Voltage class', value: formatAttribute(propertyValue(properties, 'VOLT_CLASS')) },
    { label: 'Status', value: formatAttribute(propertyValue(properties, 'STATUS')) },
    { label: 'Type', value: formatAttribute(propertyValue(properties, 'TYPE')) },
    { label: 'Clicked point', value: formatCoordinatePair(feature.coordinates) },
    { label: 'Source', value: formatAttribute(propertyValue(properties, 'SOURCE')) },
    { label: 'Source date', value: formatAttributeDate(propertyValue(properties, 'SOURCEDATE')) }
  ]
}

const powerPlantFacts = (feature) => {
  const properties = feature.properties || {}
  const location = [cleanValue(propertyValue(properties, 'CITY')), cleanValue(propertyValue(properties, 'STATE'))].filter(Boolean).join(', ')

  return [
    { label: 'Operator', value: formatAttribute(propertyValue(properties, 'OPERATOR')) },
    { label: 'Operating capacity', value: formatMegawatts(propertyValue(properties, 'OPER_CAP')) },
    { label: 'Summer capacity', value: formatMegawatts(propertyValue(properties, 'SUMMER_CAP')) },
    { label: 'Primary fuel', value: formatAttribute(propertyValue(properties, 'PRIMARY_FU')) },
    { label: 'Net generation', value: formatAttribute(propertyValue(properties, 'NET_GEN')) },
    { label: 'Location', value: location || 'Not available' },
    { label: 'Plant code', value: formatAttribute(propertyValue(properties, 'PLANT_CODE')) },
    { label: 'Coordinates', value: formatCoordinatePair(feature.coordinates) }
  ]
}

const hostingCapacityFacts = (feature) => {
  const properties = feature.properties || {}
  const directMw = propertyValue(properties, 'available_mw', 'availableMw', 'LIMIT_VAL', 'MAX_HCLOAD', 'HCLOAD', 'MaxHostingCapacity', 'MinHostingCapacity', 'HCMin', 'HCMax')
  const remainingKw = propertyValue(properties, 'Sum_Hosting_Capacity_Remaining_kW', 'Max_Hosting_Capacity_Remaining_kW', 'FeederPV', 'DistZonePV')
  const remaining = directMw === null ? formatKilowattsAsMegawatts(remainingKw) : formatMegawatts(directMw)

  return [
    { label: 'Map area', value: formatAttribute(propertyValue(properties, 'MAP_NAME', 'name', 'FEEDERNUM', 'GRID_ID', 'SUBSTATION', 'Name')) },
    { label: 'Remaining hosting capacity', value: remaining },
    { label: 'Minimum remaining capacity', value: formatKilowattsAsMegawatts(propertyValue(properties, 'Min_Hosting_Capacity_Remaining_kW')) },
    { label: 'Maximum remaining capacity', value: formatKilowattsAsMegawatts(propertyValue(properties, 'Max_Hosting_Capacity_Remaining_kW')) },
    { label: 'Feeder / circuit', value: formatAttribute(propertyValue(properties, 'FEEDERNUM', 'CIRCUITID', 'Feeder')) },
    { label: 'Substation', value: formatAttribute(propertyValue(properties, 'SUBSTATION', 'Substation')) },
    { label: 'Voltage', value: formatVoltage(propertyValue(properties, 'Line_Voltage', 'OPERATINGVOLTAGE', 'VoltageKV', 'VoltagekVLL', 'CIRCUITVOLTAGE', 'Voltage_kV')) },
    { label: 'Installed / approved DER', value: formatKilowattsAsMegawatts(propertyValue(properties, 'Sum_DER_Installed_and_approved_kW')) },
    { label: 'Source', value: feature.provider || 'Utility / DOE Hosting Capacity Atlas' }
  ]
}

const controlAreaFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'Control area', value: formatAttribute(propertyValue(properties, 'NAME')) },
    { label: 'State', value: formatAttribute(propertyValue(properties, 'STATE')) },
    { label: 'Peak load', value: formatMegawatts(propertyValue(properties, 'PEAK_LOAD')) },
    { label: 'Total capacity', value: formatMegawatts(propertyValue(properties, 'TOTAL_CAP')) },
    { label: 'Available capacity', value: formatMegawatts(propertyValue(properties, 'AVAIL_CAP')) },
    { label: 'Website', value: formatAttribute(propertyValue(properties, 'WEBSITE')) },
    { label: 'Source date', value: formatAttributeDate(propertyValue(properties, 'SOURCEDATE')) },
    { label: 'Source', value: feature.provider || 'HIFLD' }
  ]
}

const egridSubregionFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'Subregion', value: formatAttribute(propertyValue(properties, 'Subregion', 'SUBRGN')) },
    { label: 'Carbon context', value: 'Use eGRID tabular emissions data for lb/MWh by subregion.' },
    { label: 'Source', value: feature.provider || 'EPA eGRID' }
  ]
}

const nietcCorridorFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'Corridor', value: formatAttribute(propertyValue(properties, 'Name', 'NAME')) },
    { label: 'Phase', value: formatAttribute(propertyValue(properties, 'Phase', 'PHASE')) },
    { label: 'Area', value: formatAcres(propertyValue(properties, 'AreaAcres')) },
    { label: 'Context', value: 'Transmission-priority corridor; not a committed project.' },
    { label: 'Source', value: feature.provider || 'DOE Grid Deployment Office' }
  ]
}

const utilityTerritoryFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'Serving utility', value: formatAttribute(propertyValue(properties, 'NAME')) },
    { label: 'Control area', value: formatAttribute(propertyValue(properties, 'CNTRL_AREA')) },
    { label: 'Planning area', value: formatAttribute(propertyValue(properties, 'PLAN_AREA')) },
    { label: 'Holding company', value: formatAttribute(propertyValue(properties, 'HOLDING_CO')) },
    { label: 'Regulated', value: formatAttribute(propertyValue(properties, 'REGULATED')) },
    { label: 'Customers', value: formatAttribute(propertyValue(properties, 'CUSTOMERS')) },
    { label: 'Data year', value: formatAttribute(propertyValue(properties, 'YEAR')) },
    { label: 'Clicked point', value: formatCoordinatePair(feature.coordinates) }
  ]
}

const interconnectionQueueFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'Queue ID', value: formatAttribute(propertyValue(properties, 'queue_id')) },
    { label: 'ISO / RTO', value: formatAttribute(propertyValue(properties, 'iso')) },
    { label: 'Capacity', value: formatMegawatts(propertyValue(properties, 'capacity_mw')) },
    { label: 'Fuel / technology', value: formatAttribute(propertyValue(properties, 'fuel_type')) },
    { label: 'Status', value: formatAttribute(propertyValue(properties, 'status')) },
    { label: 'Entity', value: formatAttribute(propertyValue(properties, 'interconnecting_entity')) },
    { label: 'Location', value: [cleanValue(propertyValue(properties, 'county')), cleanValue(propertyValue(properties, 'state'))].filter(Boolean).join(', ') || 'Not available' },
    { label: 'Data vintage', value: formatAttribute(propertyValue(properties, 'data_vintage')) }
  ]
}

const interconnectionQueueCountyFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'County', value: [cleanValue(propertyValue(properties, 'county_name')), cleanValue(propertyValue(properties, 'state_code'))].filter(Boolean).join(', ') || 'Not available' },
    { label: 'Queued projects', value: formatAttribute(propertyValue(properties, 'project_count')) },
    { label: 'Queued capacity', value: formatMegawatts(propertyValue(properties, 'total_mw')) },
    { label: 'ISO / region mix', value: formatBreakdown(propertyValue(properties, 'iso_breakdown', 'region_breakdown')) },
    { label: 'Resource mix', value: formatBreakdown(propertyValue(properties, 'fuel_breakdown', 'resource_breakdown')) },
    { label: 'Top owners', value: formatBreakdown(propertyValue(properties, 'transmission_owners')) },
    { label: 'Largest projects', value: formatProjectList(propertyValue(properties, 'largest_projects')) },
    { label: 'Location confidence', value: formatAttribute(propertyValue(properties, 'location_confidence')) },
    { label: 'Data vintage', value: formatAttribute(propertyValue(properties, 'data_vintage')) }
  ]
}

const dataCenterSiteFacts = (feature) => {
  const properties = feature.properties || {}
  const status = cleanValue(propertyValue(properties, 'status'))
  const dataset = cleanValue(propertyValue(properties, 'source_dataset'))
  const precision = cleanValue(propertyValue(properties, 'geocode_precision'))
  const facilityType = cleanValue(propertyValue(properties, 'facility_type'))
  const duplicate = truthyFlag(propertyValue(properties, 'duplicate_candidate')) || propertyValue(properties, 'duplicate_candidate') === true
  const location = [cleanValue(propertyValue(properties, 'city')), cleanValue(propertyValue(properties, 'state'))].filter(Boolean).join(', ')

  return [
    { label: 'Operator', value: formatAttribute(propertyValue(properties, 'operator', 'company')) },
    { label: 'Owner', value: formatAttribute(propertyValue(properties, 'owner')) },
    { label: 'Facility type', value: facilityType ? titleize(facilityType) : 'Not available' },
    { label: 'Stage', value: status ? titleize(status) : 'Not available' },
    { label: 'Networks/providers', value: formatAttribute(propertyValue(properties, 'provider_count')) },
    { label: 'Location', value: location || 'Not available' },
    { label: 'Dataset', value: dataset ? titleize(dataset) : 'Not available' },
    { label: 'Likely duplicate', value: duplicate ? 'Yes' : 'No' },
    { label: 'Customers/tenants', value: formatCustomerList(propertyValue(properties, 'customers')) },
    { label: 'Coordinate precision', value: precision ? titleize(precision) : 'Not available' },
    { label: 'Data vintage', value: formatAttribute(propertyValue(properties, 'data_vintage')) },
    { label: 'Source', value: formatAttribute(propertyValue(properties, 'source')) },
    { label: 'Clicked point', value: formatCoordinatePair(feature.coordinates) }
  ].slice(0, 10)
}

const internetExchangeFacts = (feature) => {
  const properties = feature.properties || {}
  const location = [cleanValue(propertyValue(properties, 'city')), cleanValue(propertyValue(properties, 'state'))].filter(Boolean).join(', ')

  return [
    { label: 'Exchange', value: formatAttribute(propertyValue(properties, 'name')) },
    { label: 'Participants', value: formatAttribute(propertyValue(properties, 'participant_count', 'net_count')) },
    { label: 'Facilities used for geocode', value: formatAttribute(propertyValue(properties, 'facility_count')) },
    { label: 'Location', value: location || 'Not available' },
    { label: 'Website', value: formatAttribute(propertyValue(properties, 'website')) },
    { label: 'Data vintage', value: formatAttribute(propertyValue(properties, 'data_vintage')) },
    { label: 'Coordinates', value: formatCoordinatePair(feature.coordinates) },
    { label: 'Source', value: feature.provider || 'PeeringDB' }
  ]
}

const cableLandingFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'Station', value: formatAttribute(propertyValue(properties, 'name')) },
    { label: 'Operator', value: formatAttribute(propertyValue(properties, 'operator')) },
    { label: 'Cable names', value: formatAttribute(propertyValue(properties, 'cable_names')) },
    { label: 'Landing type', value: formatAttribute(propertyValue(properties, 'landing_type')) },
    { label: 'Country', value: formatAttribute(propertyValue(properties, 'country')) },
    { label: 'Data vintage', value: formatAttribute(propertyValue(properties, 'data_vintage')) },
    { label: 'Coordinates', value: formatCoordinatePair(feature.coordinates) },
    { label: 'Source', value: feature.provider || 'OpenStreetMap / Overpass' }
  ]
}

const floodZoneFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'Flood zone', value: formatAttribute(propertyValue(properties, 'FLD_ZONE')) },
    { label: 'Subtype', value: formatAttribute(propertyValue(properties, 'ZONE_SUBTY')) },
    { label: 'Special flood hazard area', value: formatAttribute(propertyValue(properties, 'SFHA_TF')) },
    { label: 'Base flood elevation', value: formatAttribute(propertyValue(properties, 'STATIC_BFE')) },
    { label: 'Vertical datum', value: formatAttribute(propertyValue(properties, 'V_DATUM')) },
    { label: 'Clicked point', value: formatCoordinatePair(feature.coordinates) },
    { label: 'Source', value: feature.provider || 'FEMA NFHL' }
  ]
}

const naturalHazardRiskFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'Area', value: [cleanValue(propertyValue(properties, 'COUNTY')), cleanValue(propertyValue(properties, 'STATEABBRV')), cleanValue(propertyValue(properties, 'TRACT')) ? `Tract ${cleanValue(propertyValue(properties, 'TRACT'))}` : null].filter(Boolean).join(', ') || 'Not available' },
    { label: 'Composite risk', value: [formatAttribute(propertyValue(properties, 'RISK_RATNG')), formatScore(propertyValue(properties, 'RISK_SCORE'))].filter((value) => value !== 'Not available').join(' / ') || 'Not available' },
    { label: 'Expected annual loss', value: formatCurrency(propertyValue(properties, 'EAL_VALT')) },
    { label: 'EAL rating', value: formatAttribute(propertyValue(properties, 'EAL_RATNG')) },
    { label: 'Social vulnerability', value: [formatAttribute(propertyValue(properties, 'SOVI_RATNG')), formatScore(propertyValue(properties, 'SOVI_SCORE'))].filter((value) => value !== 'Not available').join(' / ') || 'Not available' },
    { label: 'Community resilience', value: [formatAttribute(propertyValue(properties, 'RESL_RATNG')), formatScore(propertyValue(properties, 'RESL_SCORE'))].filter((value) => value !== 'Not available').join(' / ') || 'Not available' },
    { label: 'Wildfire', value: [formatAttribute(propertyValue(properties, 'WFIR_RISKR')), formatScore(propertyValue(properties, 'WFIR_RISKS'))].filter((value) => value !== 'Not available').join(' / ') || 'Not available' },
    { label: 'Heat wave', value: [formatAttribute(propertyValue(properties, 'HWAV_RISKR')), formatScore(propertyValue(properties, 'HWAV_RISKS'))].filter((value) => value !== 'Not available').join(' / ') || 'Not available' }
  ]
}

const epaFacilityFacts = (feature) => {
  const properties = feature.properties || {}
  const programs = ['AIR', 'NPDES', 'SDWIS', 'RCRA', 'TRI', 'GHG']
    .filter((program) => truthyFlag(propertyValue(properties, `${program}_FLAG`)))
    .join(', ')

  return [
    { label: 'Facility', value: formatAttribute(propertyValue(properties, 'FAC_NAME')) },
    { label: 'Programs', value: programs || 'Not available' },
    { label: 'Status', value: formatAttribute(propertyValue(properties, 'FAC_CURR_COMPLIANCE_STATUS')) },
    { label: 'Significant noncompliance', value: truthyFlag(propertyValue(properties, 'FAC_CURR_SNC_FLG')) ? 'Yes' : 'No' },
    { label: 'Major facility', value: truthyFlag(propertyValue(properties, 'FAC_MAJOR_FLAG')) ? 'Yes' : 'No' },
    { label: 'Inspections', value: formatAttribute(propertyValue(properties, 'FAC_INSPECTION_COUNT')) },
    { label: 'Total penalties', value: formatCurrency(propertyValue(properties, 'FAC_TOTAL_PENALTIES')) },
    { label: 'Location', value: [cleanValue(propertyValue(properties, 'FAC_CITY')), cleanValue(propertyValue(properties, 'FAC_STATE'))].filter(Boolean).join(', ') || 'Not available' }
  ]
}

const environmentalConstraintFacts = (feature) => {
  const properties = feature.properties || {}
  const fieldSets = {
    nwi_wetlands: [
      ['Wetland type', 'WETLAND_TYPE'],
      ['Attribute', 'ATTRIBUTE'],
      ['Acres', 'ACRES']
    ],
    usfws_critical_habitat: [
      ['Common name', 'comname'],
      ['Scientific name', 'sciname'],
      ['Unit', 'unitname', 'unit'],
      ['Status', 'status', 'listing_status'],
      ['Effective date', 'effectdate']
    ],
    padus_protected_areas: [
      ['Unit', 'Unit_Nm'],
      ['Manager', 'Mang_Name'],
      ['Category', 'Category'],
      ['Public access', 'Pub_Access'],
      ['GAP status', 'GAP_Sts'],
      ['GIS acres', 'GIS_Acres']
    ],
    epa_nonattainment_areas: [
      ['Pollutant', 'pollutant_name'],
      ['Area', 'area_name'],
      ['Status', 'current_status'],
      ['Classification', 'classification'],
      ['Meets NAAQS', 'meets_naaqs'],
      ['Attainment date', 'statutory_attainment_date']
    ]
  }
  const facts = (fieldSets[feature.layerKey] || []).map(([label, ...keys]) => ({
    label,
    value: formatAttribute(propertyValue(properties, ...keys))
  }))

  return [
    ...facts,
    { label: 'Clicked point', value: formatCoordinatePair(feature.coordinates) },
    { label: 'Source', value: feature.provider || 'Public environmental layer' }
  ].slice(0, 8)
}

const incentiveZoneFacts = (feature) => {
  const properties = feature.properties || {}

  if (feature.layerKey === 'opportunity_zones_hud') {
    return [
      { label: 'Tract GEOID', value: formatAttribute(propertyValue(properties, 'GEOID10')) },
      { label: 'State', value: formatAttribute(propertyValue(properties, 'STUSAB', 'STATE_NAME')) },
      { label: 'County FIPS', value: formatAttribute(propertyValue(properties, 'COUNTY')) },
      { label: 'Rural flag', value: formatAttribute(propertyValue(properties, 'Rural')) },
      { label: 'Program note', value: 'OZ eligibility is time-phased; confirm deal-specific tax treatment.' },
      { label: 'Source', value: feature.provider || 'HUD / Treasury' }
    ]
  }

  if (feature.layerKey === 'foreign_trade_zones_ftz') {
    return [
      { label: 'FTZ service area', value: formatAttribute(propertyValue(properties, 'FTZSA')) },
      { label: 'County', value: formatAttribute(propertyValue(properties, 'COUNTY')) },
      { label: 'FIPS', value: formatAttribute(propertyValue(properties, 'FIPS')) },
      { label: 'Contact', value: formatAttribute(propertyValue(properties, 'Contact')) },
      { label: 'Website', value: formatAttribute(propertyValue(properties, 'Website')) },
      { label: 'Program note', value: 'FTZ benefits require activation and operator/grantee confirmation.' }
    ]
  }

  if (feature.layerKey === 'foreign_trade_zone_sites_ftz') {
    return [
      { label: 'Zone / site', value: formatAttribute(propertyValue(properties, 'Num')) },
      { label: 'Grantee', value: formatAttribute(propertyValue(properties, 'Grantee')) },
      { label: 'Type', value: formatAttribute(propertyValue(properties, 'Type')) },
      { label: 'Address', value: formatAttribute(propertyValue(properties, 'Address')) },
      { label: 'City / state / ZIP', value: formatAttribute(propertyValue(properties, 'CityStZip')) },
      { label: 'Contact', value: formatAttribute(propertyValue(properties, 'Contact')) },
      { label: 'Website', value: formatAttribute(propertyValue(properties, 'Website')) }
    ]
  }

  return [
    { label: 'Community', value: formatAttribute(propertyValue(properties, 'FULLNAME', 'NAME')) },
    { label: 'Type', value: formatAttribute(propertyValue(properties, 'TYPE')) },
    { label: 'State', value: formatAttribute(propertyValue(properties, 'STATEABBR')) },
    { label: 'HUD link', value: formatAttribute(propertyValue(properties, 'LINK')) },
    { label: 'Program note', value: 'Legacy program benefits vary; confirm active incentives locally.' },
    { label: 'Source', value: feature.provider || 'HUD' }
  ]
}

const texasGroundwaterWellFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'State well number', value: formatAttribute(propertyValue(properties, 'StateWellNumber')) },
    { label: 'Owner', value: formatAttribute(propertyValue(properties, 'OwnerName')) },
    { label: 'Aquifer', value: formatAttribute(propertyValue(properties, 'AquiferCodeName')) },
    { label: 'Primary use', value: formatAttribute(propertyValue(properties, 'PrimaryWaterUse')) },
    { label: 'Well type', value: formatAttribute(propertyValue(properties, 'WellType')) },
    { label: 'Well depth', value: formatAttribute(propertyValue(properties, 'WellDepth')) },
    { label: 'County', value: formatAttribute(propertyValue(properties, 'CountyName')) },
    { label: 'Water quality available', value: formatAttribute(propertyValue(properties, 'WaterQualityAvailable')) }
  ]
}

const texasWaterRightFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'Holder', value: formatAttribute(propertyValue(properties, 'HOLDER_NAME')) },
    { label: 'Status', value: formatAttribute(propertyValue(properties, 'STATUS')) },
    { label: 'Water body', value: formatAttribute(propertyValue(properties, 'WATERBODY')) },
    { label: 'Main purpose', value: formatAttribute(propertyValue(properties, 'MAIN_PURPOSE')) },
    { label: 'Use type', value: formatAttribute(propertyValue(properties, 'USE_TYPE')) },
    { label: 'Source of rights', value: formatAttribute(propertyValue(properties, 'SOURCE_OF_RIGHTS')) },
    { label: 'File / document', value: [cleanValue(propertyValue(properties, 'FILE_NUM')), cleanValue(propertyValue(properties, 'DOCUMENT_NUM'))].filter(Boolean).join(' / ') || 'Not available' },
    { label: 'Region', value: formatAttribute(propertyValue(properties, 'REGION')) },
    { label: 'Expiry date', value: formatAttributeDate(propertyValue(properties, 'EXPIRY_DATE')) }
  ]
}

const zoningFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'Zoning code', value: formatAttribute(propertyValue(properties, 'zoning_code', 'zoning', 'zone_code')) },
    { label: 'Standardized type', value: formatAttribute(propertyValue(properties, 'zoning_type', 'zone_type', 'standardized_type')) },
    { label: 'Subtype', value: formatAttribute(propertyValue(properties, 'zoning_subtype', 'zone_subtype')) },
    { label: 'FAR', value: formatAttribute(propertyValue(properties, 'far')) },
    { label: 'Lot coverage', value: formatAttribute(propertyValue(properties, 'max_lot_coverage')) },
    { label: 'Building height', value: formatAttribute(propertyValue(properties, 'max_building_height')) },
    { label: 'Setbacks', value: formatSetbacks(properties) },
    { label: 'Permitted uses', value: formatAttribute(propertyValue(properties, 'permitted_uses', 'allowed_uses')) },
    { label: 'Jurisdiction', value: formatAttribute(propertyValue(properties, 'jurisdiction', 'municipality', 'city')) },
    { label: 'Source', value: feature.provider || 'Regrid + Zoneomics' }
  ].filter((fact) => fact.value && fact.value !== 'Not available').slice(0, 9)
}

const ssurgoSoilFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'Map unit', value: formatAttribute(propertyValue(properties, 'MUNAME', 'muname')) },
    { label: 'Map unit key', value: formatAttribute(propertyValue(properties, 'MUKEY', 'mukey')) },
    { label: 'Map symbol', value: formatAttribute(propertyValue(properties, 'MUSYM', 'musym')) },
    { label: 'Farmland class', value: formatAttribute(propertyValue(properties, 'FARMLNDCL', 'farmlndcl')) },
    { label: 'Survey area', value: formatAttribute(propertyValue(properties, 'AREASYMBOL', 'areasymbol')) },
    { label: 'Clicked point', value: formatCoordinatePair(feature.coordinates) },
    { label: 'Source', value: feature.provider || 'USDA NRCS SSURGO' }
  ]
}

const censusFacts = (feature) => {
  const properties = feature.properties || {}

  return [
    { label: 'GEOID', value: formatAttribute(propertyValue(properties, 'GEOID')) },
    { label: 'Name', value: formatAttribute(propertyValue(properties, 'NAME')) },
    { label: 'State', value: formatAttribute(propertyValue(properties, 'STATE')) },
    { label: 'County', value: formatAttribute(propertyValue(properties, 'COUNTY')) },
    { label: 'Tract', value: formatAttribute(propertyValue(properties, 'TRACT')) },
    { label: 'Block group', value: formatAttribute(propertyValue(properties, 'BLKGRP')) },
    { label: 'Clicked point', value: formatCoordinatePair(feature.coordinates) },
    { label: 'Source', value: feature.provider || 'U.S. Census Bureau TIGERweb' }
  ]
}

const genericFeatureFacts = (feature) => {
  const properties = feature.properties || {}
  const summaryFields = feature.summaryFields || []
  const summaryFacts = summaryFields.slice(0, 6).map((field) => ({
    label: titleize(field),
    value: formatAttribute(propertyValue(properties, field))
  }))

  return [
    ...summaryFacts,
    { label: 'Feature id', value: feature.externalId || 'Not available' },
    { label: 'Clicked point', value: formatCoordinatePair(feature.coordinates) },
    { label: 'Source', value: feature.provider || feature.layerName || 'Not available' }
  ].slice(0, 8)
}

const selectedExternalParcelFacts = computed(() => {
  const parcel = selectedExternalParcel.value
  if (!parcel) return []

  return [
    { label: 'APN / property id', value: parcel.apn || parcel.sourceFeatureId },
    { label: 'Owner', value: parcel.ownerName || 'Not available' },
    { label: 'Situs address', value: parcel.situsAddress || 'Not available' },
    { label: 'Acreage', value: parcel.acreage ? `${formatNumber(parcel.acreage)} acres` : 'Not available' },
    { label: 'Centroid', value: formatCoordinatePair(parcel.coordinates) },
    { label: 'Mailing address', value: parcel.mailingAddress || 'Not available' },
    { label: 'Legal description', value: parcel.legalDescription || 'Not available' },
    { label: 'Source feature', value: parcel.sourceFeatureId }
  ]
})

onMounted(async () => {
  loaderStartedAt = Date.now()
  mapLoaderTimer = null
  mapLoaderWatchdog = null
  initializeLayerState()
  initializeParcelSourceState()
  globalThis.addEventListener?.('landport:layout-resize', queueMapResize)

  if (!apiKey.value) return

  await nextTick()
  config.apiKey = apiKey.value
  const style = await loadMapStyle()
  if (!style) {
    styleError.value = true
    return
  }

  map = new Map({
    container: mapElement.value,
    style: style,
    // Position the camera on the data up front so the first paint is already at the
    // destination — avoids a post-load fitBounds animation that re-clusters the markers.
    ...computeInitialCamera(),
    forceNoAttributionControl: true,
    maptilerLogo: false,
    attributionControl: false,
    logo: false,
    navigationControl: false,
    geolocateControl: false
  })
  if (import.meta.env.DEV) mapElement.value.__landportMap = map
  map.addControl(new NavigationControl({ visualizePitch: true }), 'top-right')
  map.addControl(new ScaleControl(), 'bottom-left')
  map.on('load', async () => {
    mapReady = true
    mapZoom.value = map.getZoom()
    try {
      await registerMapMarkerIcons(map)
      markerIconsReady = true
    } catch {
      markerIconsReady = false
      // Fall back to circle layers if the browser refuses an inline SVG image.
    }
    syncTrackedParcelBoundaries()
    ensureManualParcelDrawLayer()
    renderMarkers()
    focusInitialOpportunity()
    syncActiveExternalLayers()
    syncActiveParcelSources()
    dismissMapLoader()
  })
  map.on('click', handleManualParcelDrawClick)
  map.on('moveend', () => {
    mapZoom.value = map.getZoom()
    scheduleLayerRefresh()
  })
  // Failsafe: if the GL map never fires 'load' (e.g. a runtime/WebGL error), don't strand the loader.
  mapLoaderWatchdog = globalThis.setTimeout(dismissMapLoader, MAX_LOADER_MS)
})

const loadMapStyle = async () => {
  try {
    const response = await fetch(`https://api.maptiler.com/maps/hybrid-v4/style.json?key=${encodeURIComponent(apiKey.value)}`)

    if (!response.ok) return null

    return await response.json()
  } catch {
    return null
  }
}

// Hide the loader once the map is ready, but keep it visible at least MIN_LOADER_MS
// so fast loads do not flash a half-drawn frame. The <Transition> handles the fade-out.
const dismissMapLoader = () => {
  if (mapLoaderTimer) return
  clearTimeout(mapLoaderWatchdog)
  const remaining = Math.max(0, MIN_LOADER_MS - (Date.now() - loaderStartedAt))
  mapLoaderTimer = globalThis.setTimeout(() => {
    mapLoading.value = false
  }, remaining)
}

onBeforeUnmount(() => {
  closeLayerInfo()
  clearTimeout(layerRefreshTimer)
  clearTimeout(intelligenceTimer)
  clearTimeout(mapLoaderTimer)
  clearTimeout(mapLoaderWatchdog)
  globalThis.removeEventListener?.('landport:layout-resize', queueMapResize)
  cancelAllMapRequests()
  clearMarkers()
  parcelSourceLayerHandlers.forEach((handlers, id) => unbindParcelSourceInteractions(id))
  map?.off?.('click', handleManualParcelDrawClick)
  try {
    map?.remove()
  } catch {
    // The MapTiler SDK can throw during Vite hot reload after its GL resources are already gone.
  } finally {
    map = null
    mapReady = false
    markerIconsReady = false
  }
})

watch([visibleOpportunityPoints, visibleMapMarkerParcels], () => {
  renderMarkers()
})

watch(visibleParcelBoundaryFeatures, () => {
  syncTrackedParcelBoundaries()
})

watch(activeLayerKeys, () => {
  syncActiveExternalLayers()
})

watch(activeParcelSourceKeys, () => {
  syncActiveParcelSources()
})

watch(manualParcelLookupMode, (mode) => {
  if (mode === 'draw') {
    startManualParcelDraw()
  } else {
    manualParcelDrawActive.value = false
    if (mapReady) map.getCanvas().style.cursor = ''
  }
})

// Apply opacity only to layers whose slider actually changed — reapplying
// every active layer per tick made slider drags jank with many layers on.
let lastAppliedLayerOpacity = {}
watch(layerOpacity, (next) => {
  activeRenderableLayers.value.forEach((layer) => {
    const opacity = Number(next[layer.key] || layer.defaultOpacity || 0.7)
    if (lastAppliedLayerOpacity[layer.key] === opacity) return

    applyLayerOpacityFor(layer, opacity)
  })
  lastAppliedLayerOpacity = { ...next }
}, { deep: true })

watch(parcelSourceOpacity, () => {
  applyParcelSourceOpacity()
}, { deep: true })

watch(radiusMiles, () => {
  if (!selectedRecord.value) return

  clearTimeout(intelligenceTimer)
  intelligenceTimer = setTimeout(() => fetchIntelligence(), 350)
})

watch(leftMapPanelCollapsed, (collapsed) => {
  persistStoredBoolean('landport:map:left-panel-collapsed', collapsed)
  queueMapResize()
})

watch(rightMapPanelCollapsed, (collapsed) => {
  persistStoredBoolean('landport:map:right-panel-collapsed', collapsed)
  queueMapResize()
})

watch(recordHealthLegendCollapsed, (collapsed) => {
  persistStoredBoolean('landport:map:record-health-legend-collapsed', collapsed)
})

watch(visibleLayersLegendCollapsed, (collapsed) => {
  persistStoredBoolean('landport:map:visible-layers-legend-collapsed', collapsed)
})

watch(favoriteLayerKeys, (keys) => {
  persistStoredArray('landport:map:favorite-layer-keys', Array.from(keys))
}, { deep: true })

const initializeLayerState = () => {
  const activeKeys = new Set()
  const opacity = {}

  externalLayers.value.forEach((layer) => {
    opacity[layer.key] = Number(layer.defaultOpacity || 0.7)
    if (layer.defaultVisible && canRenderLayer(layer)) activeKeys.add(layer.key)
  })

  activeLayerKeys.value = activeKeys
  layerOpacity.value = opacity
  initializeLayerCategoryExpansion()
}

const initializeParcelSourceState = () => {
  const activeKeys = new Set()
  const opacity = {}

  renderableParcelSources.value.forEach((source) => {
    opacity[source.key] = Number(source.defaultOpacity || 0.22)
    if (source.defaultVisible && source.enabled) activeKeys.add(source.key)
  })

  activeParcelSourceKeys.value = activeKeys
  parcelSourceOpacity.value = opacity
}

const renderMarkers = () => {
  if (!mapReady) return

  clearMarkers()
  syncRecordLayer()
}

const createMarker = (longitude, latitude, element, popupHtml) => new Marker({ element })
  .setLngLat([longitude, latitude])
  .setPopup(new Popup({ offset: 18 }).setHTML(popupHtml))
  .addTo(map)

const clearMarkers = () => {
  markers.forEach((marker) => marker.remove())
  markers = []
}

const syncRecordLayer = () => {
  if (!mapReady) return

  recordLayer.sync()
}

const syncTrackedParcelBoundaries = () => {
  if (!mapReady) return

  if (!map.getSource(TRACKED_PARCEL_BOUNDARY_SOURCE_ID)) {
    map.addSource(TRACKED_PARCEL_BOUNDARY_SOURCE_ID, {
      type: 'geojson',
      data: emptyFeatureCollection()
    })
  }
  if (!map.getLayer(TRACKED_PARCEL_BOUNDARY_FILL_ID)) {
    map.addLayer({
      id: TRACKED_PARCEL_BOUNDARY_FILL_ID,
      type: 'fill',
      source: TRACKED_PARCEL_BOUNDARY_SOURCE_ID,
      paint: {
        'fill-color': '#f59e0b',
        'fill-opacity': 0.16
      }
    })
    map.on('click', TRACKED_PARCEL_BOUNDARY_FILL_ID, handleTrackedParcelBoundaryClick)
    map.on('mouseenter', TRACKED_PARCEL_BOUNDARY_FILL_ID, () => { map.getCanvas().style.cursor = 'pointer' })
    map.on('mouseleave', TRACKED_PARCEL_BOUNDARY_FILL_ID, () => { map.getCanvas().style.cursor = '' })
  }
  if (!map.getLayer(TRACKED_PARCEL_BOUNDARY_LINE_ID)) {
    map.addLayer({
      id: TRACKED_PARCEL_BOUNDARY_LINE_ID,
      type: 'line',
      source: TRACKED_PARCEL_BOUNDARY_SOURCE_ID,
      paint: {
        'line-color': '#d97706',
        'line-width': 2,
        'line-opacity': 0.9
      }
    })
  }

  map.getSource(TRACKED_PARCEL_BOUNDARY_SOURCE_ID).setData({
    type: 'FeatureCollection',
    features: visibleParcelBoundaryFeatures.value
  })
}

const handleTrackedParcelBoundaryClick = (event) => {
  const parcelId = event.features?.[0]?.properties?.id
  const parcel = visibleParcels.value.find((item) => String(item.id) === String(parcelId))
  if (parcel) selectRecord('parcel', parcel)
}

const opportunityMarker = (point) => {
  const element = document.createElement('button')
  element.type = 'button'
  element.className = `landport-map-marker landport-map-marker--opportunity ${markerToneClass(point.health?.tone)} ${isSelectedMarker('opportunity', point) ? 'landport-map-marker--selected' : ''}`
  element.title = `${point.name} / ${point.health?.stateLabel || point.stageLabel}`
  element.textContent = initials(point.name)
  element.addEventListener('click', () => selectRecord('opportunity', point))

  return element
}

const parcelMarker = (parcel) => {
  const element = document.createElement('button')
  element.type = 'button'
  element.className = `landport-map-marker landport-map-marker--parcel ${markerToneClass(parcel.health?.tone)} ${isSelectedMarker('parcel', parcel) ? 'landport-map-marker--selected' : ''}`
  element.title = `${parcel.displayName} / ${parcel.health?.stateLabel || parcel.opportunityName}`
  element.textContent = parcel.primary ? 'P' : ''
  element.addEventListener('click', () => selectRecord('parcel', parcel))

  return element
}

const opportunityPopup = (point) => `
  <div class="landport-map-popup">
    ${point.primaryImage ? `<img src="${escapeHtml(point.primaryImage.url)}" alt="" class="landport-map-popup__image">` : ''}
    <strong>${escapeHtml(point.name)}</strong>
    <span>${escapeHtml(point.market)} / ${escapeHtml(point.stageLabel)} / ${formatNumber(point.acreage)} acres</span>
    <span>${escapeHtml(point.health?.stateLabel || 'Unknown')} / ${escapeHtml(point.health?.nextAction || '')}</span>
    <a href="${escapeHtml(point.path)}">Open opportunity</a>
  </div>
`

const parcelPopup = (parcel) => `
  <div class="landport-map-popup">
    ${parcel.primaryImage ? `<img src="${escapeHtml(parcel.primaryImage.url)}" alt="" class="landport-map-popup__image">` : ''}
    <strong>${escapeHtml(parcel.displayName)}</strong>
    <span>${escapeHtml(parcel.opportunityName)} / ${formatNumber(parcel.acreage)} acres</span>
    <a href="${escapeHtml(parcel.opportunityPath)}">Open opportunity</a>
  </div>
`

const visibleRecordCoordinates = () => [
  ...visibleOpportunityPoints.value.map((point) => [point.longitude, point.latitude]),
  ...visibleParcels.value.map((parcel) => [parcel.longitude, parcel.latitude])
].filter((coordinate) => validPoint(coordinate))

// Computes the camera the map should open at, mirroring fitToVisibleRecords but applied
// in the Map constructor so there is no visible animation or marker re-clustering on load.
const computeInitialCamera = () => {
  const fallback = {
    center: [center.value.longitude, center.value.latitude],
    zoom: points.value.length > 0 ? 6 : 3
  }

  // A deep-linked opportunity always wins so the page opens directly on it.
  const focusPoint = focusedOpportunityId.value
    ? points.value.find((candidate) => candidate.id === focusedOpportunityId.value)
    : null
  if (focusPoint && validPoint([focusPoint.longitude, focusPoint.latitude])) {
    return { center: [focusPoint.longitude, focusPoint.latitude], zoom: 13 }
  }

  const coordinates = visibleRecordCoordinates()
  if (coordinates.length === 0) return fallback
  if (coordinates.length === 1) return { center: coordinates[0], zoom: 11 }

  const bounds = coordinates.reduce(
    (mapBounds, coordinate) => mapBounds.extend(coordinate),
    new LngLatBounds(coordinates[0], coordinates[0])
  )
  return { bounds, fitBoundsOptions: { padding: 80, maxZoom: 12 } }
}

// Animated fit, triggered by the "Fit records" button after the map is already live.
const fitToVisibleRecords = () => {
  if (!mapReady) return
  if (selectedRecord.value) return

  const coordinates = visibleRecordCoordinates()
  if (coordinates.length === 0) return

  if (coordinates.length === 1) {
    map.easeTo({ center: coordinates[0], zoom: 11, duration: 250 })
    return
  }

  const bounds = coordinates.reduce((mapBounds, coordinate) => mapBounds.extend(coordinate), new LngLatBounds(coordinates[0], coordinates[0]))
  map.fitBounds(bounds, { padding: 80, maxZoom: 12, duration: 250 })
}

const searchMapLocation = async () => {
  const query = locationSearchQuery.value.trim()
  if (!query || locationSearchLoading.value) return

  if (!apiKey.value || styleError.value) {
    locationSearchError.value = 'Map search is unavailable until map tiles are configured.'
    return
  }

  if (mapLoading.value || !mapReady) {
    locationSearchError.value = 'Map search will be available when the map finishes loading.'
    return
  }

  const gpsCoordinates = parseGpsCoordinateQuery(query)
  const gpsCamera = cameraForGpsCoordinates(gpsCoordinates)
  if (gpsCamera) {
    locationSearchRequestId += 1
    locationSearchLoading.value = false
    locationSearchError.value = ''
    locationSearchResult.value = { label: gpsCoordinates.label }
    if (selectedRecord.value) clearSelectedRecord()
    moveToLocationCamera(gpsCamera)
    return
  }

  const requestId = ++locationSearchRequestId
  locationSearchLoading.value = true
  locationSearchError.value = ''
  locationSearchResult.value = { label: 'Searching...' }

  try {
    const result = await geocoding.forward(query, locationSearchOptions())
    if (requestId !== locationSearchRequestId) return

    const feature = result?.features?.[0]
    const camera = cameraForLocationFeature(feature)
    if (!camera) {
      locationSearchError.value = `No map result found for "${query}".`
      return
    }

    locationSearchResult.value = { label: locationFeatureLabel(feature) }
    if (selectedRecord.value) clearSelectedRecord()
    moveToLocationCamera(camera)
  } catch (error) {
    if (requestId !== locationSearchRequestId) return

    locationSearchError.value = locationSearchErrorMessage(error)
  } finally {
    if (requestId === locationSearchRequestId) locationSearchLoading.value = false
  }
}

const clearLocationSearch = () => {
  locationSearchRequestId += 1
  locationSearchQuery.value = ''
  locationSearchError.value = ''
  locationSearchResult.value = null
  locationSearchLoading.value = false
}

const locationSearchOptions = () => {
  const options = {
    country: ['US'],
    limit: 1,
    types: LOCATION_SEARCH_TYPES
  }

  if (mapReady && map) {
    const currentCenter = map.getCenter()
    options.proximity = [currentCenter.lng, currentCenter.lat]
  }

  return options
}

const moveToLocationCamera = (camera) => {
  if (!mapReady || !map) return

  if (camera.kind === 'bounds') {
    map.fitBounds(camera.bounds, camera.fitBoundsOptions)
    return
  }

  map.easeTo(camera.easeOptions)
}

const locationSearchErrorMessage = (error) => {
  const message = String(error?.message || '')
  if (message.includes('403')) return 'Map search is unavailable for the current MapTiler key.'
  if (message.toLowerCase().includes('api key')) return 'Map search needs a valid MapTiler API key.'

  return 'Location search is unavailable. Try again in a moment.'
}

const syncActiveExternalLayers = () => {
  if (!mapReady) return

  externalLayers.value.forEach((layer) => {
    if (isLayerActive(layer.key) && canRenderLayer(layer)) {
      ensureExternalLayer(layer)
      fetchExternalLayer(layer)
    } else {
      removeExternalLayer(layer)
    }
  })
}

const syncActiveParcelSources = () => {
  if (!mapReady) return

  renderableParcelSources.value.forEach((source) => {
    if (isParcelSourceActive(source.key) && source.enabled) {
      ensureParcelSourceLayer(source)
      fetchParcelSource(source)
    } else {
      removeParcelSourceLayer(source)
    }
  })
}

const scheduleLayerRefresh = () => {
  clearTimeout(layerRefreshTimer)
  layerRefreshTimer = setTimeout(() => {
    activeRenderableLayers.value.forEach((layer) => fetchExternalLayer(layer))
    activeRenderableParcelSources.value.forEach((source) => fetchParcelSource(source))
  }, 450)
}

const ensureExternalLayer = (layer) => {
  if (!map.getSource(sourceId(layer))) {
    map.addSource(sourceId(layer), sourceDefinition(layer))
  }

  layerIds(layer).forEach((id) => {
    if (map.getLayer(id)) {
      bindFeatureLayerInteractions(layer, id)
      return
    }

    map.addLayer(layerDefinition(layer, id))
    bindFeatureLayerInteractions(layer, id)
  })
  applyLayerOpacity()
}

const removeExternalLayer = (layer) => {
  cancelLayerRequest(layer.key)
  layerIds(layer).forEach((id) => {
    unbindFeatureLayerInteractions(id)
    if (map.getLayer(id)) map.removeLayer(id)
  })

  if (map.getSource(sourceId(layer))) map.removeSource(sourceId(layer))
  loadedLayerBboxes.delete(layer.key)
  layerRequestVersions.delete(layer.key)
  setLayerFeatureCount(layer.key, null)
  layerHints.value = { ...layerHints.value, [layer.key]: '' }
  if (selectedInfrastructureFeature.value?.layerKey === layer.key) closeInfrastructureFeature()
}

const cancelLayerRequest = (key) => {
  const controller = layerAbortControllers.get(key)
  if (!controller) return

  controller.abort()
  layerAbortControllers.delete(key)
}

const cancelParcelSourceRequest = (key) => {
  const controller = parcelSourceAbortControllers.get(key)
  if (!controller) return

  controller.abort()
  parcelSourceAbortControllers.delete(key)
}

const cancelAllMapRequests = () => {
  layerAbortControllers.forEach((controller) => controller.abort())
  parcelSourceAbortControllers.forEach((controller) => controller.abort())
  layerAbortControllers.clear()
  parcelSourceAbortControllers.clear()
}

// At most 4 layer requests in flight; the rest queue. Protects the server's
// thread pool when a user enables many proxied layers at once.
const enqueueLayerFetch = createFetchQueue(4)

const fetchExternalLayer = async (layer, options = {}) => {
  if (isRasterLayer(layer)) {
    setLayerFeatureCount(layer.key, null)
    layerErrors.value = { ...layerErrors.value, [layer.key]: '' }
    layerHints.value = { ...layerHints.value, [layer.key]: '' }
    setLayerLoading(layer.key, false)
    return
  }

  const source = map.getSource(sourceId(layer))
  if (!source) return

  if (layerBelowMinZoom(layer)) {
    layerRequestVersions.set(layer.key, (layerRequestVersions.get(layer.key) || 0) + 1)
    cancelLayerRequest(layer.key)
    source.setData(emptyFeatureCollection())
    loadedLayerBboxes.delete(layer.key)
    layerErrors.value = { ...layerErrors.value, [layer.key]: '' }
    layerHints.value = { ...layerHints.value, [layer.key]: '' }
    setLayerFeatureCount(layer.key, 0)
    setLayerLoading(layer.key, false)
    return
  }

  const viewportBbox = currentBboxArray()
  const loadedState = loadedLayerBboxes.get(layer.key)
  if (!options.force && loadedState && bboxContains(loadedState.bbox, viewportBbox) && loadedStateCanSatisfyViewport(loadedState, viewportBbox)) return

  const requestBbox = paddedBbox(viewportBbox, layer)
  const requestVersion = (layerRequestVersions.get(layer.key) || 0) + 1
  layerRequestVersions.set(layer.key, requestVersion)
  cancelLayerRequest(layer.key)
  const abortController = new AbortController()
  layerAbortControllers.set(layer.key, abortController)

  setLayerLoading(layer.key, true)
  try {
    const data = await enqueueLayerFetch(() => requestJson(externalLayerRequestUrl(layer, requestBbox), { signal: abortController.signal }))
    if (layerRequestVersions.get(layer.key) !== requestVersion) return

    const sourceError = data?.properties?.landportError
    const sourceHint = data?.properties?.landportHint
    source.setData(data)
    setLayerFeatureCount(layer.key, data?.properties?.landportReturnedFeatures || data?.features?.length || 0)
    if (sourceError) {
      loadedLayerBboxes.delete(layer.key)
      layerErrors.value = { ...layerErrors.value, [layer.key]: sourceError }
      layerHints.value = { ...layerHints.value, [layer.key]: '' }
      return
    }

    loadedLayerBboxes.set(layer.key, {
      bbox: requestBbox,
      viewportBbox,
      complete: !featureCollectionLimited(data, layer.query?.maxFeatures || 500)
    })
    layerErrors.value = { ...layerErrors.value, [layer.key]: '' }
    layerHints.value = { ...layerHints.value, [layer.key]: sourceHint || '' }
  } catch (error) {
    if (isAbortError(error)) return
    if (layerRequestVersions.get(layer.key) !== requestVersion) return

    layerErrors.value = { ...layerErrors.value, [layer.key]: normalizeErrors(error).join(', ') }
    layerHints.value = { ...layerHints.value, [layer.key]: '' }
  } finally {
    if (layerAbortControllers.get(layer.key) === abortController) layerAbortControllers.delete(layer.key)
    if (layerRequestVersions.get(layer.key) === requestVersion) setLayerLoading(layer.key, false)
  }
}

const externalLayerRequestUrl = (layer, requestBbox) => {
  const params = new URLSearchParams({
    bbox: formatBbox(requestBbox),
    limit: String(layer.query?.maxFeatures || 500)
  })
  if (layer.query?.status) params.set('status', layer.query.status)
  if (layer.query?.sourceDataset) params.set('source_dataset', layer.query.sourceDataset)
  if (layer.query?.dedupe === false) params.set('include_duplicates', '1')

  const separator = String(layer.queryUrl || '').includes('?') ? '&' : '?'
  return `${layer.queryUrl}${separator}${params.toString()}`
}

const sourceDefinition = (layer) => {
  if (isRasterLayer(layer)) {
    return {
      type: 'raster',
      tiles: [arcgisImageTileUrl(layer)],
      tileSize: Number(layer.query?.tileSize || 512),
      attribution: layer.attribution
    }
  }

  return {
    type: 'geojson',
    data: emptyFeatureCollection()
  }
}

const arcgisImageTileUrl = (layer) => {
  const tileSize = Number(layer.query?.tileSize || 512)
  const params = new URLSearchParams({
    bboxSR: '3857',
    imageSR: '3857',
    size: `${tileSize},${tileSize}`,
    format: 'png32',
    transparent: 'true',
    f: 'image'
  })
  if (layer.query?.renderingRule) params.set('renderingRule', JSON.stringify(layer.query.renderingRule))

  return `${String(layer.sourceUrl || '').replace(/\/$/, '')}/exportImage?bbox={bbox-epsg-3857}&${params.toString()}`
}

const fillColorExpression = (paint) => {
  if (paint.fillColorField && paint.fillColorStops) {
    const stops = paint.fillColorStops.flat()
    return ['interpolate', ['linear'], ['coalesce', ['to-number', ['get', paint.fillColorField]], 0], ...stops]
  }

  if (paint.statusColorField && paint.statusColors) {
    const matchPairs = Object.entries(paint.statusColors).flatMap(([status, color]) => [status, color])
    return ['match', ['get', paint.statusColorField], ...matchPairs, paint.fillColor || '#0f766e']
  }

  return paint.fillColor || '#0f766e'
}

const layerDefinition = (layer, id) => {
  const paint = layer.style || {}
  const opacity = Number(layerOpacity.value[layer.key] || layer.defaultOpacity || 0.7)

  if (id.endsWith('-raster')) {
    return {
      id,
      type: 'raster',
      source: sourceId(layer),
      paint: {
        'raster-opacity': opacity,
        'raster-fade-duration': 0
      }
    }
  }

  if (id.endsWith('-fill')) {
    return {
      id,
      type: 'fill',
      source: sourceId(layer),
      paint: {
        'fill-color': fillColorExpression(paint),
        'fill-opacity': opacity
      }
    }
  }

  if (id.endsWith('-circle')) {
    return {
      id,
      type: 'circle',
      source: sourceId(layer),
      paint: {
        'circle-color': circleColorExpression(paint),
        'circle-radius': circleRadiusExpression(paint),
        'circle-stroke-color': paint.circleStrokeColor || '#ffffff',
        'circle-stroke-width': 1,
        'circle-opacity': opacity
      }
    }
  }

  if (id.endsWith('-symbol')) {
    return {
      id,
      type: 'symbol',
      source: sourceId(layer),
      layout: {
        'icon-image': markerIconImageNameForLayer(layer),
        'icon-size': markerIconSizeExpression(layer),
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        'icon-ignore-placement': true
      },
      paint: {
        'icon-opacity': opacity
      }
    }
  }

  if (id.endsWith('-label')) {
    return {
      id,
      type: 'symbol',
      source: sourceId(layer),
      layout: {
        'text-field': ['coalesce', ['get', 'operator_initials'], 'DC'],
        'text-offset': pointLayerUsesMarkerIcon(layer) ? [0, 1.05] : [0, 0],
        'text-anchor': pointLayerUsesMarkerIcon(layer) ? 'top' : 'center',
        'text-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          4,
          9,
          9,
          11,
          13,
          13
        ],
        'text-allow-overlap': true,
        'text-ignore-placement': true
      },
      paint: {
        'text-color': '#0f172a',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1,
        'text-opacity': opacity
      }
    }
  }

  return {
    id,
    type: 'line',
    source: sourceId(layer),
    paint: {
      'line-color': lineColorExpression(paint),
      'line-width': lineWidthExpression(paint),
      'line-opacity': opacity
    }
  }
}

const applyLayerOpacity = () => {
  if (!mapReady) return

  activeRenderableLayers.value.forEach((layer) => {
    const opacity = Number(layerOpacity.value[layer.key] || layer.defaultOpacity || 0.7)
    applyLayerOpacityFor(layer, opacity)
  })
}

const applyLayerOpacityFor = (layer, opacity) => {
  if (!mapReady) return

  layerIds(layer).forEach((id) => {
    if (!map.getLayer(id)) return
    if (id.endsWith('-fill')) map.setPaintProperty(id, 'fill-opacity', opacity)
    if (id.endsWith('-line')) map.setPaintProperty(id, 'line-opacity', opacity)
    if (id.endsWith('-circle')) map.setPaintProperty(id, 'circle-opacity', opacity)
    if (id.endsWith('-symbol')) map.setPaintProperty(id, 'icon-opacity', opacity)
    if (id.endsWith('-label')) map.setPaintProperty(id, 'text-opacity', opacity)
    if (id.endsWith('-raster')) map.setPaintProperty(id, 'raster-opacity', opacity)
  })
}

const ensureParcelSourceLayer = (parcelSource) => {
  if (!map.getSource(parcelSourceSourceId(parcelSource))) {
    map.addSource(parcelSourceSourceId(parcelSource), {
      type: 'geojson',
      data: emptyFeatureCollection()
    })
  }

  parcelSourceLayerIds(parcelSource).forEach((id) => {
    if (map.getLayer(id)) {
      bindParcelSourceInteractions(parcelSource, id)
      return
    }

    map.addLayer(parcelSourceLayerDefinition(parcelSource, id))
    bindParcelSourceInteractions(parcelSource, id)
  })
  applyParcelSourceOpacity()
}

const removeParcelSourceLayer = (parcelSource) => {
  cancelParcelSourceRequest(parcelSource.key)
  parcelSourceLayerIds(parcelSource).forEach((id) => {
    unbindParcelSourceInteractions(id)
    if (map.getLayer(id)) map.removeLayer(id)
  })

  if (map.getSource(parcelSourceSourceId(parcelSource))) map.removeSource(parcelSourceSourceId(parcelSource))
  loadedParcelSourceBboxes.delete(parcelSource.key)
  parcelSourceRequestVersions.delete(parcelSource.key)
  parcelSourceHints.value = { ...parcelSourceHints.value, [parcelSource.key]: '' }
  if (selectedExternalParcel.value?.sourceKey === parcelSource.key) closeExternalParcel()
}

const fetchParcelSource = async (parcelSource, options = {}) => {
  if (isManualParcelSource(parcelSource)) return

  const source = map.getSource(parcelSourceSourceId(parcelSource))
  if (!source) return

  if (map.getZoom() < Number(parcelSource.minZoom || 11)) {
    parcelSourceRequestVersions.set(parcelSource.key, (parcelSourceRequestVersions.get(parcelSource.key) || 0) + 1)
    cancelParcelSourceRequest(parcelSource.key)
    source.setData(emptyFeatureCollection())
    loadedParcelSourceBboxes.delete(parcelSource.key)
    parcelSourceErrors.value = { ...parcelSourceErrors.value, [parcelSource.key]: '' }
    parcelSourceHints.value = { ...parcelSourceHints.value, [parcelSource.key]: `Zoom to ${parcelSource.minZoom}+ to load parcels` }
    setParcelSourceLoading(parcelSource.key, false)
    return
  }

  const viewportBbox = currentBboxArray()
  if (parcelSourceBboxTooWide(viewportBbox)) {
    parcelSourceRequestVersions.set(parcelSource.key, (parcelSourceRequestVersions.get(parcelSource.key) || 0) + 1)
    cancelParcelSourceRequest(parcelSource.key)
    source.setData(emptyFeatureCollection())
    loadedParcelSourceBboxes.delete(parcelSource.key)
    parcelSourceErrors.value = { ...parcelSourceErrors.value, [parcelSource.key]: '' }
    parcelSourceHints.value = { ...parcelSourceHints.value, [parcelSource.key]: 'Zoom closer to load parcel polygons' }
    setParcelSourceLoading(parcelSource.key, false)
    return
  }

  const loadedBbox = loadedParcelSourceBboxes.get(parcelSource.key)
  if (!options.force && loadedBbox && bboxContains(loadedBbox, viewportBbox)) return

  const requestBbox = paddedParcelSourceBbox(viewportBbox)
  const requestVersion = (parcelSourceRequestVersions.get(parcelSource.key) || 0) + 1
  parcelSourceRequestVersions.set(parcelSource.key, requestVersion)
  cancelParcelSourceRequest(parcelSource.key)
  const abortController = new AbortController()
  parcelSourceAbortControllers.set(parcelSource.key, abortController)

  setParcelSourceLoading(parcelSource.key, true)
  parcelSourceHints.value = { ...parcelSourceHints.value, [parcelSource.key]: '' }
  try {
    const data = await requestJson(`${parcelSource.queryUrl}?bbox=${encodeURIComponent(formatBbox(requestBbox))}&limit=${parcelSource.query?.maxFeatures || 250}`, { signal: abortController.signal })
    if (parcelSourceRequestVersions.get(parcelSource.key) !== requestVersion) return

    const sourceError = data?.properties?.landportError
    source.setData(data)
    if (sourceError) {
      loadedParcelSourceBboxes.delete(parcelSource.key)
      parcelSourceErrors.value = { ...parcelSourceErrors.value, [parcelSource.key]: sourceError }
      return
    }

    loadedParcelSourceBboxes.set(parcelSource.key, requestBbox)
    parcelSourceErrors.value = { ...parcelSourceErrors.value, [parcelSource.key]: '' }
  } catch (error) {
    if (isAbortError(error)) return
    if (parcelSourceRequestVersions.get(parcelSource.key) !== requestVersion) return

    parcelSourceErrors.value = { ...parcelSourceErrors.value, [parcelSource.key]: normalizeErrors(error).join(', ') }
  } finally {
    if (parcelSourceAbortControllers.get(parcelSource.key) === abortController) parcelSourceAbortControllers.delete(parcelSource.key)
    if (parcelSourceRequestVersions.get(parcelSource.key) === requestVersion) setParcelSourceLoading(parcelSource.key, false)
  }
}

const lookupPaidParcelsAtSelectedFeature = (parcelSource) => {
  if (manualParcelLookupMode.value === 'draw') {
    lookupPaidParcels(parcelSource, { label: 'drawn area' })
    return
  }

  const coordinates = selectedInfrastructureFeature.value?.coordinates
  if (!validPoint(coordinates)) {
    parcelSourceErrors.value = { ...parcelSourceErrors.value, [parcelSource.key]: 'Selected feature does not have usable coordinates.' }
    return
  }

  lookupPaidParcels(parcelSource, {
    coordinates,
    label: selectedInfrastructureFeature.value.name || 'selected feature'
  })
}

const lookupPaidParcelsAtSelectedFeatureForAll = () => {
  if (manualParcelLookupDisabledForSelectedFeatureAll.value) return

  manualParcelSources.value.forEach((source) => lookupPaidParcelsAtSelectedFeature(source))
}

const lookupPaidParcelsAtMapCenter = (parcelSource) => {
  if (!mapReady) return

  if (manualParcelLookupMode.value === 'draw') {
    lookupPaidParcels(parcelSource, { label: 'drawn area' })
    return
  }

  lookupPaidParcels(parcelSource, {
    coordinates: currentMapCenterPoint(),
    label: 'map center'
  })
}

const lookupPaidParcels = async (parcelSource, { coordinates = null, label = 'selected area' } = {}) => {
  if (!mapReady) return
  if (!['extent', 'draw'].includes(manualParcelLookupMode.value) && !validPoint(coordinates)) return
  if (manualParcelLookupMode.value === 'draw' && !manualParcelDrawLookupReady.value) {
    parcelSourceErrors.value = {
      ...parcelSourceErrors.value,
      [parcelSource.key]: manualParcelDrawReady.value ? `Drawn parcel search area is too large (${manualParcelDrawAreaSquareMiles.value.toFixed(1)} sq mi). Zoom in or draw less than ${MANUAL_PARCEL_DRAW_MAX_SQUARE_MILES} sq mi.` : 'Draw at least three points before running a parcel lookup.'
    }
    return
  }

  const requestVersion = (parcelSourceRequestVersions.get(parcelSource.key) || 0) + 1
  parcelSourceRequestVersions.set(parcelSource.key, requestVersion)
  cancelParcelSourceRequest(parcelSource.key)
  const abortController = new AbortController()
  parcelSourceAbortControllers.set(parcelSource.key, abortController)
  setParcelSourceLoading(parcelSource.key, true)
  parcelSourceErrors.value = { ...parcelSourceErrors.value, [parcelSource.key]: '' }
  parcelSourceHints.value = { ...parcelSourceHints.value, [parcelSource.key]: '' }
  manualParcelLookupNotices.value = { ...manualParcelLookupNotices.value, [parcelSource.key]: '' }

  try {
    const lookup = manualParcelLookupRequest(parcelSource, coordinates)
    const data = await requestJson(`${parcelSource.queryUrl}?${lookup.params.toString()}`, { signal: abortController.signal })
    if (parcelSourceRequestVersions.get(parcelSource.key) !== requestVersion) return

    if (data?.properties?.landportError) {
      parcelSourceErrors.value = { ...parcelSourceErrors.value, [parcelSource.key]: data.properties.landportError }
      return
    }
    if (data?.type !== 'FeatureCollection') {
      parcelSourceErrors.value = { ...parcelSourceErrors.value, [parcelSource.key]: 'Parcel lookup returned an unexpected response.' }
      return
    }

    const featureCount = data?.features?.length || 0
    if (featureCount === 0) {
      removeManualParcelLayer(parcelSource)
      manualParcelLookupResults.value = { ...manualParcelLookupResults.value, [parcelSource.key]: [] }
      manualParcelLookupNotices.value = {
        ...manualParcelLookupNotices.value,
        [parcelSource.key]: `No parcels returned ${lookup.description} ${label}.`
      }
      return
    }

    ensureParcelSourceLayer(parcelSource)
    const source = map.getSource(parcelSourceSourceId(parcelSource))
    if (!source) return

    source.setData(data)
    fitToFeatureCollection(data)

    const next = new Set(manualParcelLayerKeys.value)
    next.add(parcelSource.key)
    manualParcelLayerKeys.value = next
    manualParcelLookupResults.value = {
      ...manualParcelLookupResults.value,
      [parcelSource.key]: summarizeParcelLookupFeatures(parcelSource, data.features || [])
    }
    manualParcelLookupNotices.value = {
      ...manualParcelLookupNotices.value,
      [parcelSource.key]: `${featureCount} parcel${featureCount === 1 ? '' : 's'} returned ${lookup.description} ${label}.`
    }
  } catch (error) {
    if (isAbortError(error)) return
    if (parcelSourceRequestVersions.get(parcelSource.key) !== requestVersion) return

    parcelSourceErrors.value = { ...parcelSourceErrors.value, [parcelSource.key]: normalizeErrors(error).join(', ') }
  } finally {
    if (parcelSourceAbortControllers.get(parcelSource.key) === abortController) parcelSourceAbortControllers.delete(parcelSource.key)
    if (parcelSourceRequestVersions.get(parcelSource.key) === requestVersion) setParcelSourceLoading(parcelSource.key, false)
  }
}

const manualParcelLookupRequest = (parcelSource, coordinates) => {
  const params = new URLSearchParams()
  const limit = manualParcelLookupLimit(parcelSource)
  params.set('manual', 'true')
  params.set('limit', String(limit))

  if (manualParcelLookupMode.value === 'extent') {
    params.set('bbox', formatBbox(currentBboxArray()))
    return { params, description: 'in the current map view around' }
  }

  if (manualParcelLookupMode.value === 'draw') {
    params.set('polygon', manualParcelDrawPolygonParam())
    return { params, description: 'inside' }
  }

  params.set('point', formatPoint(coordinates))
  if (manualParcelLookupMode.value === 'nearby') {
    params.set('radius_miles', String(manualParcelLookupRadiusMiles.value || defaultManualParcelRadius(parcelSource)))
    return { params, description: `within ${manualParcelLookupRadiusMiles.value || defaultManualParcelRadius(parcelSource)} mi of` }
  }

  return { params, description: 'under' }
}

const manualParcelLookupLimit = (parcelSource) => Math.max(Number(parcelSource.metadata?.maxManualFeatures || 0), Number(parcelSource.query?.maxFeatures || 0), 25)
const defaultManualParcelRadius = (parcelSource) => Number(parcelSource.metadata?.defaultRadiusMiles || 0.25)
const manualParcelRadiusOptions = (parcelSource) => (parcelSource?.metadata?.radiusOptionsMiles || [0.1, 0.25, 0.5, 1]).map((radius) => Number(radius))
const parcelLookupResultsFor = (parcelSource) => manualParcelLookupResults.value[parcelSource.key] || []
const manualParcelLookupDisabledForSelectedFeature = (parcelSource) => loadingParcelSourceKeys.value.has(parcelSource.key) || (manualParcelLookupMode.value === 'draw' ? !manualParcelDrawLookupReady.value : !selectedInfrastructureFeature.value?.coordinates)
const manualParcelLookupDisabledForMap = (parcelSource) => loadingParcelSourceKeys.value.has(parcelSource.key) || (manualParcelLookupMode.value === 'draw' && !manualParcelDrawLookupReady.value)

const summarizeParcelLookupFeatures = (parcelSource, features) => features.map((feature, index) => {
  const properties = feature.properties || {}
  const normalized = normalizedLandportProperties(properties)
  const fieldMap = parcelSource.query?.fieldMap || {}
  const apn = cleanValue(normalized.apn) || cleanValue(propertyValue(properties, fieldMap.apn, 'Prop_ID', 'APN', 'parcel_id'))
  const ownerName = cleanValue(normalized.ownerName) || cleanValue(propertyValue(properties, fieldMap.ownerName, 'OWNER_NAME', 'OWNER', 'owner'))
  const situsAddress = cleanValue(normalized.situsAddress) || cleanValue(propertyValue(properties, fieldMap.situsAddress, 'SITUS_ADDR', 'SITUS_ADDRESS', 'address'))
  const acreage = normalized.acreage || Number(propertyValue(properties, fieldMap.acreage, 'GIS_AREA', 'LEGAL_AREA', 'acreage_calc'))

  return {
    id: String(normalized.sourceFeatureId || feature.id || index),
    label: apn || situsAddress || ownerName || `Parcel ${index + 1}`,
    apn,
    ownerName,
    situsAddress,
    acreage: Number.isFinite(Number(acreage)) ? Number(acreage) : null,
    coordinates: parcelFeatureCenter(feature),
    feature
  }
})

const selectParcelLookupResult = (parcelSource, parcel) => {
  const [lng, lat] = parcel.coordinates || []
  selectExternalParcel(parcelSource, parcel.feature, Number.isFinite(lng) && Number.isFinite(lat) ? { lng, lat } : null)
}

const startManualParcelDraw = () => {
  if (!mapReady) return

  manualParcelLookupMode.value = 'draw'
  manualParcelDrawActive.value = true
  ensureManualParcelDrawLayer()
  map.getCanvas().style.cursor = 'crosshair'
}

const activateManualParcelDraw = () => {
  manualParcelLookupMode.value = 'draw'
  startManualParcelDraw()
}

const finishManualParcelDraw = () => {
  if (!manualParcelDrawReady.value) return

  manualParcelDrawActive.value = false
  if (mapReady) map.getCanvas().style.cursor = ''
}

const clearManualParcelDraw = () => {
  manualParcelDrawVertices.value = []
  manualParcelDrawActive.value = false
  if (mapReady) map.getCanvas().style.cursor = ''
  syncManualParcelDrawLayer()
}

const undoManualParcelDrawVertex = () => {
  manualParcelDrawVertices.value = manualParcelDrawVertices.value.slice(0, -1)
  syncManualParcelDrawLayer()
}

const handleManualParcelDrawClick = (event) => {
  if (!manualParcelDrawActive.value) return
  if (event.originalEvent?.target !== map?.getCanvas()) return

  event.preventDefault?.()
  manualParcelDrawVertices.value = [
    ...manualParcelDrawVertices.value,
    [Number(event.lngLat.lng.toFixed(6)), Number(event.lngLat.lat.toFixed(6))]
  ]
  syncManualParcelDrawLayer()
}

const ensureManualParcelDrawLayer = () => {
  if (!mapReady || map.getSource(MANUAL_PARCEL_DRAW_SOURCE_ID)) return

  map.addSource(MANUAL_PARCEL_DRAW_SOURCE_ID, {
    type: 'geojson',
    data: manualParcelDrawFeatureCollection()
  })
  map.addLayer({
    id: MANUAL_PARCEL_DRAW_FILL_ID,
    type: 'fill',
    source: MANUAL_PARCEL_DRAW_SOURCE_ID,
    paint: {
      'fill-color': '#a855f7',
      'fill-opacity': 0.14
    }
  })
  map.addLayer({
    id: MANUAL_PARCEL_DRAW_LINE_ID,
    type: 'line',
    source: MANUAL_PARCEL_DRAW_SOURCE_ID,
    paint: {
      'line-color': '#7e22ce',
      'line-width': 2.5,
      'line-dasharray': [2, 1]
    }
  })
  map.addLayer({
    id: MANUAL_PARCEL_DRAW_POINTS_ID,
    type: 'circle',
    source: MANUAL_PARCEL_DRAW_SOURCE_ID,
    paint: {
      'circle-color': '#7e22ce',
      'circle-radius': 5,
      'circle-stroke-color': '#ffffff',
      'circle-stroke-width': 1.5
    }
  })
}

const syncManualParcelDrawLayer = () => {
  if (!mapReady) return

  ensureManualParcelDrawLayer()
  const source = map.getSource(MANUAL_PARCEL_DRAW_SOURCE_ID)
  source?.setData(manualParcelDrawFeatureCollection())
}

const manualParcelDrawFeatureCollection = () => {
  const features = manualParcelDrawVertices.value.map((point, index) => ({
    type: 'Feature',
    properties: { kind: 'vertex', index },
    geometry: { type: 'Point', coordinates: point }
  }))

  if (manualParcelDrawVertices.value.length >= 2) {
    features.push({
      type: 'Feature',
      properties: { kind: 'edge' },
      geometry: { type: 'LineString', coordinates: manualParcelDrawVertices.value }
    })
  }

  if (manualParcelDrawReady.value) {
    features.push({
      type: 'Feature',
      properties: { kind: 'polygon' },
      geometry: { type: 'Polygon', coordinates: [closedManualParcelDrawRing()] }
    })
  }

  return { type: 'FeatureCollection', features }
}

const closedManualParcelDrawRing = () => {
  const ring = manualParcelDrawVertices.value.map((point) => [point[0], point[1]])
  if (ring.length > 0 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
    ring.push([ring[0][0], ring[0][1]])
  }

  return ring
}

const manualParcelDrawPolygonParam = () => JSON.stringify({
  type: 'Polygon',
  coordinates: [closedManualParcelDrawRing()]
})

const polygonBboxAreaSquareMiles = (points) => {
  if (!Array.isArray(points) || points.length < 3) return 0

  const longitudes = points.map((point) => Number(point[0]))
  const latitudes = points.map((point) => Number(point[1]))
  const midLatitude = (Math.min(...latitudes) + Math.max(...latitudes)) / 2
  const longitudeScale = Math.max(Math.abs(Math.cos(midLatitude * Math.PI / 180)), 0.2)
  const widthMiles = Math.abs(Math.max(...longitudes) - Math.min(...longitudes)) * 69 * longitudeScale
  const heightMiles = Math.abs(Math.max(...latitudes) - Math.min(...latitudes)) * 69

  return widthMiles * heightMiles
}

const removeManualParcelLayer = (parcelSource) => {
  removeParcelSourceLayer(parcelSource)
  const next = new Set(manualParcelLayerKeys.value)
  next.delete(parcelSource.key)
  manualParcelLayerKeys.value = next
  manualParcelLookupResults.value = { ...manualParcelLookupResults.value, [parcelSource.key]: [] }
}

const clearManualParcelLookup = (parcelSource) => {
  removeManualParcelLayer(parcelSource)
  manualParcelLookupNotices.value = { ...manualParcelLookupNotices.value, [parcelSource.key]: '' }
  parcelSourceErrors.value = { ...parcelSourceErrors.value, [parcelSource.key]: '' }
  parcelSourceHints.value = { ...parcelSourceHints.value, [parcelSource.key]: '' }
}

const parcelSourceLayerDefinition = (parcelSource, id) => {
  const paint = parcelSource.style || {}
  const opacity = Number(parcelSourceOpacity.value[parcelSource.key] || parcelSource.defaultOpacity || 0.22)

  if (id.endsWith('-fill')) {
    return {
      id,
      type: 'fill',
      source: parcelSourceSourceId(parcelSource),
      paint: {
        'fill-color': paint.fillColor || '#f59e0b',
        'fill-opacity': opacity
      }
    }
  }

  if (id.endsWith('-circle')) {
    return {
      id,
      type: 'circle',
      source: parcelSourceSourceId(parcelSource),
      paint: {
        'circle-color': paint.lineColor || '#7e22ce',
        'circle-radius': 5,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 1.5,
        'circle-opacity': Math.min(opacity + 0.25, 0.9),
        'circle-stroke-opacity': Math.min(opacity + 0.35, 0.95)
      }
    }
  }

  return {
    id,
    type: 'line',
    source: parcelSourceSourceId(parcelSource),
    paint: {
      'line-color': paint.lineColor || '#d97706',
      'line-width': paint.lineWidth || 1,
      'line-opacity': Math.min(opacity + 0.35, 0.9)
    }
  }
}

const applyParcelSourceOpacity = () => {
  if (!mapReady) return

  ;[...activeRenderableParcelSources.value, ...visibleManualParcelSources.value].forEach((parcelSource) => {
    const opacity = Number(parcelSourceOpacity.value[parcelSource.key] || parcelSource.defaultOpacity || 0.22)
    parcelSourceLayerIds(parcelSource).forEach((id) => {
      if (!map.getLayer(id)) return
      if (id.endsWith('-fill')) map.setPaintProperty(id, 'fill-opacity', opacity)
      if (id.endsWith('-line')) map.setPaintProperty(id, 'line-opacity', Math.min(opacity + 0.35, 0.9))
      if (id.endsWith('-circle')) {
        map.setPaintProperty(id, 'circle-opacity', Math.min(opacity + 0.25, 0.9))
        map.setPaintProperty(id, 'circle-stroke-opacity', Math.min(opacity + 0.35, 0.95))
      }
    })
  })
}

const bindParcelSourceInteractions = (parcelSource, id) => {
  if ((!id.endsWith('-fill') && !id.endsWith('-circle')) || parcelSourceLayerHandlers.has(id)) return

  const click = (event) => {
    const feature = event.features?.[0]
    if (!feature) return

    selectExternalParcel(parcelSource, feature, event.lngLat)
  }
  const mouseenter = () => {
    map.getCanvas().style.cursor = 'pointer'
  }
  const mouseleave = () => {
    map.getCanvas().style.cursor = ''
  }

  map.on('click', id, click)
  map.on('mouseenter', id, mouseenter)
  map.on('mouseleave', id, mouseleave)
  parcelSourceLayerHandlers.set(id, { click, mouseenter, mouseleave })
}

const unbindParcelSourceInteractions = (id) => {
  const handlers = parcelSourceLayerHandlers.get(id)
  if (!handlers) return

  map.off('click', id, handlers.click)
  map.off('mouseenter', id, handlers.mouseenter)
  map.off('mouseleave', id, handlers.mouseleave)
  parcelSourceLayerHandlers.delete(id)
}

const bindFeatureLayerInteractions = (layer, id) => {
  if (!isFeatureLayerInteractive(layer, id) || interactiveLayerHandlers.has(id)) return

  const click = (event) => {
    const feature = event.features?.[0]
    if (!feature) return

    queueInfrastructureFeatureSelection(layer, feature, event.lngLat)
  }
  const mouseenter = () => {
    map.getCanvas().style.cursor = 'pointer'
  }
  const mouseleave = () => {
    map.getCanvas().style.cursor = ''
  }

  map.on('click', id, click)
  map.on('mouseenter', id, mouseenter)
  map.on('mouseleave', id, mouseleave)
  interactiveLayerHandlers.set(id, { click, mouseenter, mouseleave })
}

const unbindFeatureLayerInteractions = (id) => {
  const handlers = interactiveLayerHandlers.get(id)
  if (!handlers) return

  map.off('click', id, handlers.click)
  map.off('mouseenter', id, handlers.mouseenter)
  map.off('mouseleave', id, handlers.mouseleave)
  interactiveLayerHandlers.delete(id)
}

const isFeatureLayerInteractive = (layer, id) => id.endsWith(interactiveLayerSuffix(layer))
const interactiveLayerSuffix = (layer) => {
  if (layer.geometryType === 'polygon') return '-fill'
  if (pointLayerUsesMarkerIcon(layer)) return '-symbol'
  if (layer.geometryType === 'point') return '-circle'

  return '-line'
}
const queueInfrastructureFeatureSelection = (layer, feature, lngLat) => {
  if (manualParcelDrawActive.value) return

  pendingFeatureClickSelections.push({ layer, feature, lngLat })
  if (pendingFeatureClickTimer) globalThis.clearTimeout(pendingFeatureClickTimer)

  pendingFeatureClickTimer = globalThis.setTimeout(() => {
    const selected = pendingFeatureClickSelections
      .slice()
      .sort((left, right) => featureSelectionRank(right.layer) - featureSelectionRank(left.layer))[0]

    pendingFeatureClickSelections = []
    pendingFeatureClickTimer = null
    if (selected) selectInfrastructureFeature(selected.layer, selected.feature, selected.lngLat)
  }, 0)
}
const featureSelectionRank = (layer) => {
  if (layer.geometryType === 'point') return 30
  if (layer.geometryType === 'line') return 20

  return 10
}

const selectInfrastructureFeature = (layer, feature, lngLat) => {
  const properties = feature.properties || {}
  const coordinates = featureCoordinates(feature, lngLat)
  const externalId = externalFeatureId(layer, feature, coordinates)
  const name = infrastructureFeatureName(layer, properties)
  const subtitle = infrastructureSubtitle(layer, properties)

  selectedInfrastructureFeature.value = {
    layerKey: layer.key,
    layerName: layer.name,
    layerCategory: layer.category,
    description: layer.description,
    attribution: layer.attribution,
    sourceUrl: layer.sourceUrl,
    geometryType: layer.geometryType,
    summaryFields: layer.query?.summaryFields || [],
    provider: layer.provider,
    externalId,
    name,
    subtitle,
    coordinates,
    properties,
    notesUrl: `/map/features/${encodeURIComponent(layer.key)}/${encodeURIComponent(externalId)}/notes`
  }
  featureNotes.value = []
  featureNoteBody.value = ''
  featureNoteErrors.value = []
  fetchFeatureNotes()
}

const closeInfrastructureFeature = () => {
  selectedInfrastructureFeature.value = null
  featureNotes.value = []
  featureNoteBody.value = ''
  featureNoteErrors.value = []
  featureNotesLoading.value = false
  featureNoteSaving.value = false
}

const fetchFeatureNotes = async () => {
  const feature = selectedInfrastructureFeature.value
  if (!feature) return

  featureNotesLoading.value = true
  featureNoteErrors.value = []
  try {
    const data = await requestJson(feature.notesUrl)
    if (selectedInfrastructureFeature.value?.layerKey !== feature.layerKey || selectedInfrastructureFeature.value?.externalId !== feature.externalId) return

    featureNotes.value = data.notes || []
  } catch (error) {
    featureNoteErrors.value = normalizeErrors(error)
  } finally {
    if (selectedInfrastructureFeature.value?.layerKey === feature.layerKey && selectedInfrastructureFeature.value?.externalId === feature.externalId) featureNotesLoading.value = false
  }
}

const saveFeatureNote = async () => {
  const feature = selectedInfrastructureFeature.value
  if (!feature || featureNoteBody.value.length === 0) return

  featureNoteSaving.value = true
  featureNoteErrors.value = []
  try {
    const data = await requestJson(feature.notesUrl, {
      method: 'POST',
      body: JSON.stringify({
        note: {
          body: featureNoteBody.value,
          feature_name: feature.name,
          metadata: {
            layerName: feature.layerName,
            coordinates: feature.coordinates,
            properties: feature.properties
          }
        }
      })
    })
    featureNotes.value = data.notes || []
    featureNoteBody.value = ''
  } catch (error) {
    featureNoteErrors.value = normalizeErrors(error)
  } finally {
    featureNoteSaving.value = false
  }
}

const focusInfrastructureFeature = () => {
  const coordinates = selectedInfrastructureFeature.value?.coordinates
  if (!mapReady || !validPoint(coordinates)) return

  map.easeTo({ center: coordinates, zoom: Math.max(map.getZoom(), 12), duration: 250 })
}

const copyFeatureCoordinates = async () => {
  const coordinates = selectedInfrastructureFeature.value?.coordinates
  if (!validPoint(coordinates)) return

  const text = formatPoint(coordinates)
  try {
    await globalThis.navigator?.clipboard?.writeText(text)
    notice.value = `Copied ${text}.`
  } catch {
    notice.value = text
  }
}

const selectExternalParcel = (parcelSource, feature, lngLat) => {
  const properties = feature.properties || {}
  const normalized = normalizedLandportProperties(properties)
  const coordinates = featureCoordinates(feature, lngLat)
  const fieldMap = parcelSource.query?.fieldMap || {}
  const apn = cleanValue(normalized.apn) || cleanValue(propertyValue(properties, fieldMap.apn, 'Prop_ID', 'APN'))
  const ownerName = cleanValue(normalized.ownerName) || cleanValue(propertyValue(properties, fieldMap.ownerName, 'OWNER_NAME', 'OWNER'))
  const situsAddress = cleanValue(normalized.situsAddress) || cleanValue(propertyValue(properties, fieldMap.situsAddress, 'SITUS_ADDR', 'SITUS_ADDRESS'))
  const mailingAddress = cleanValue(normalized.mailingAddress) || cleanValue(propertyValue(properties, fieldMap.mailingAddress, 'MAIL_ADDR', 'MAILING_ADDRESS'))
  const acreage = normalized.acreage || Number(propertyValue(properties, fieldMap.acreage, 'GIS_AREA', 'LEGAL_AREA'))

  selectedExternalParcel.value = {
    sourceKey: parcelSource.key,
    sourceName: parcelSource.name,
    sourceFeatureId: String(normalized.sourceFeatureId || feature.id || propertyValue(properties, parcelSource.query?.idField, 'OBJECTID') || stableFeatureKey(feature, coordinates)),
    displayName: apn || situsAddress || ownerName || 'Public parcel',
    apn,
    ownerName,
    situsAddress,
    mailingAddress,
    county: normalized.county,
    state: normalized.state || parcelSource.state,
    acreage: Number.isFinite(Number(acreage)) ? Number(acreage) : null,
    legalDescription: cleanValue(normalized.legalDescription) || cleanValue(propertyValue(properties, fieldMap.legalDescription, 'LEGAL_DESC')),
    licenseText: normalized.licenseText || parcelSource.licenseText,
    coordinates,
    feature
  }
  selectedParcelTrackOpportunityId.value = selectedRecord.value?.kind === 'opportunity' ? selectedRecord.value.id : (trackableOpportunities.value[0]?.id || '')
  externalParcelErrors.value = []
}

const closeExternalParcel = () => {
  selectedExternalParcel.value = null
  selectedParcelTrackOpportunityId.value = ''
  externalParcelErrors.value = []
  trackingExternalParcel.value = false
}

const trackExternalParcel = async () => {
  const parcel = selectedExternalParcel.value
  if (!parcel || !selectedParcelTrackOpportunityId.value) return

  trackingExternalParcel.value = true
  externalParcelErrors.value = []
  errors.value = []
  try {
    const data = await requestJson(`/opportunities/${selectedParcelTrackOpportunityId.value}/external_parcels`, {
      method: 'POST',
      body: JSON.stringify({
        source_key: parcel.sourceKey,
        feature: parcel.feature
      })
    })
    mapPayload.value = data.map
    notice.value = `${parcel.displayName} added to ${opportunityName(selectedParcelTrackOpportunityId.value)}.`
    closeExternalParcel()
    await nextTick()
    renderMarkers()
  } catch (error) {
    externalParcelErrors.value = normalizeErrors(error)
  } finally {
    trackingExternalParcel.value = false
  }
}

const selectRecord = (kind, record, focusOptions = {}) => {
  selectedRecord.value = {
    ...record,
    kind,
    name: kind === 'parcel' ? record.displayName : record.name,
    path: kind === 'parcel' ? record.opportunityPath : record.path
  }
  renderMarkers()
  focusSelectedRecord(focusOptions)
  fetchIntelligence()
}

const clearSelectedRecord = () => {
  selectedRecord.value = null
  intelligence.value = null
  intelligenceErrors.value = []
  renderMarkers()
}

const focusSelectedRecord = ({ zoom = 11, duration = 250 } = {}) => {
  if (!mapReady || !selectedRecord.value) return

  map.easeTo({
    center: [selectedRecord.value.longitude, selectedRecord.value.latitude],
    zoom: Math.max(map.getZoom(), zoom),
    duration
  })
}

const focusInitialOpportunity = () => {
  if (initialFocusApplied || !focusedOpportunityId.value) return

  initialFocusApplied = true
  const point = points.value.find((candidate) => candidate.id === focusedOpportunityId.value)
  if (!point) {
    notice.value = 'That opportunity is not plotted yet. Add parcel coordinates to focus it on the map.'
    return
  }

  selectedStage.value = 'all'
  selectedRecordLayer.value = 'all'
  rightMapPanelCollapsed.value = false
  selectRecord('opportunity', point, { zoom: 13, duration: 500 })
  notice.value = `${point.name} focused on the map.`
}

const fetchIntelligence = async () => {
  if (!selectedRecord.value?.intelligenceUrl) return

  intelligenceLoading.value = true
  intelligenceErrors.value = []
  try {
    const data = await requestJson(`${selectedRecord.value.intelligenceUrl}?radius_miles=${radiusMiles.value}`)
    intelligence.value = data.intelligence
  } catch (error) {
    intelligence.value = null
    intelligenceErrors.value = normalizeErrors(error)
  } finally {
    intelligenceLoading.value = false
  }
}

const toggleLeftMapPanel = () => {
  leftMapPanelCollapsed.value = !leftMapPanelCollapsed.value
}

const toggleRightMapPanel = () => {
  rightMapPanelCollapsed.value = !rightMapPanelCollapsed.value
}

const isContextSectionExpanded = (key) => expandedContextSections.value.has(key)
const toggleContextSection = (key) => {
  const next = new Set(expandedContextSections.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  expandedContextSections.value = next
}

const initializeLayerCategoryExpansion = () => {
  if (layerCategoryExpansionInitialized.value) return

  const expanded = new Set()
  layerCategories.value.forEach((category) => {
    if (category.layers.some((layer) => layer.defaultVisible || isLayerActive(layer.key))) expanded.add(category.key)
  })
  if (expanded.size === 0 && layerCategories.value[0]) expanded.add(layerCategories.value[0].key)

  expandedLayerCategoryKeys.value = expanded
  layerCategoryExpansionInitialized.value = true
}

const isLayerCategoryExpanded = (key) => expandedLayerCategoryKeys.value.has(key)
const layerCategoryExpandedForDisplay = (key) => layerSearchQuery.value.length > 0 || isLayerCategoryExpanded(key)
const toggleLayerCategory = (key) => {
  const next = new Set(expandedLayerCategoryKeys.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  expandedLayerCategoryKeys.value = next
}

const setLayerCategoryExpansion = (expanded) => {
  expandedLayerCategoryKeys.value = expanded ? new Set(layerCategories.value.map((category) => category.key)) : new Set()
}

const queueMapResize = () => {
  if (!mapReady) return

  nextTick(() => {
    map?.resize()
    scheduleLayerRefresh()
  })
}

const toggleLayer = (key) => {
  const next = new Set(activeLayerKeys.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  activeLayerKeys.value = next
}

const toggleRecordHealthLegend = () => {
  recordHealthLegendCollapsed.value = !recordHealthLegendCollapsed.value
}

const toggleVisibleLayersLegend = () => {
  visibleLayersLegendCollapsed.value = !visibleLayersLegendCollapsed.value
}

const hideVisibleMapOverlays = () => {
  const nextLayerKeys = new Set(activeLayerKeys.value)
  activeRenderableLayers.value.forEach((layer) => nextLayerKeys.delete(layer.key))
  activeLayerKeys.value = nextLayerKeys

  const nextParcelSourceKeys = new Set(activeParcelSourceKeys.value)
  activeRenderableParcelSources.value.forEach((source) => nextParcelSourceKeys.delete(source.key))
  activeParcelSourceKeys.value = nextParcelSourceKeys

  visibleManualParcelSources.value.forEach((source) => clearManualParcelLookup(source))
}

const isFavoriteLayer = (key) => favoriteLayerKeys.value.has(key)
const toggleFavoriteLayer = (key) => {
  const next = new Set(favoriteLayerKeys.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  favoriteLayerKeys.value = next
}

const applyLayerRecipe = (recipe) => {
  const layerKeys = availableRecipeLayerKeys(recipe, renderableLayers.value)
  activeLayerKeys.value = new Set(layerKeys)
  if (recipe.parcelSourceMode === 'public') {
    activeParcelSourceKeys.value = new Set(enabledParcelSources.value.map((source) => source.key))
  } else if (recipe.parcelSourceMode === 'none') {
    activeParcelSourceKeys.value = new Set()
  }

  expandedLayerCategoryKeys.value = new Set(layerCategories.value
    .filter((category) => category.layers.some((layer) => layerKeys.includes(layer.key)))
    .map((category) => category.key))
  layerSearchQuery.value = ''
  notice.value = `${recipe.label} layers applied.`
}

const setAllLayersVisible = (visible) => {
  activeLayerKeys.value = visible ? new Set(renderableLayers.value.map((layer) => layer.key)) : new Set()
}

const setDefaultLayersVisible = () => {
  activeLayerKeys.value = new Set(renderableLayers.value.filter((layer) => layer.defaultVisible).map((layer) => layer.key))
}

// "Show all" minus the full-coverage flood: replace the selection with line and
// point layers only. Polygons AND rasters stay off — a national raster blankets
// the map just like a choropleth does.
const setNonPolygonLayersVisible = () => {
  activeLayerKeys.value = new Set(renderableLayers.value.filter((layer) => [ 'line', 'point' ].includes(layer.geometryType)).map((layer) => layer.key))
}

const setCategoryLayersVisible = (category, visible) => {
  const categoryKeys = category.layers.filter((layer) => canRenderLayer(layer)).map((layer) => layer.key)
  const next = new Set(activeLayerKeys.value)
  categoryKeys.forEach((key) => {
    if (visible) next.add(key)
    else next.delete(key)
  })
  activeLayerKeys.value = next
  if (visible && categoryKeys.length > 0 && !isLayerCategoryExpanded(category.key)) toggleLayerCategory(category.key)
}

const isComparisonRecordSelected = (id) => comparisonRecordIds.value.has(id)
const toggleComparisonRecord = (recordOrId) => {
  const id = typeof recordOrId === 'object' ? recordOrId.id : recordOrId
  const next = new Set(comparisonRecordIds.value)
  if (next.has(id)) next.delete(id)
  else if (next.size < 5) next.add(id)
  else notice.value = 'Compare up to five opportunities at a time.'
  comparisonRecordIds.value = next
}
const clearComparison = () => {
  comparisonRecordIds.value = new Set()
}

const toggleParcelSource = (key) => {
  const next = new Set(activeParcelSourceKeys.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  activeParcelSourceKeys.value = next
}

const setAllParcelSourcesVisible = (visible) => {
  activeParcelSourceKeys.value = visible ? new Set(enabledParcelSources.value.map((source) => source.key)) : new Set()
}

const setLayerOpacity = (key, value) => {
  layerOpacity.value = {
    ...layerOpacity.value,
    [key]: Number(value)
  }
}

const setParcelSourceOpacity = (key, value) => {
  parcelSourceOpacity.value = {
    ...parcelSourceOpacity.value,
    [key]: Number(value)
  }
}

const setLayerLoading = (key, loading) => {
  const next = new Set(loadingLayerKeys.value)
  if (loading) next.add(key)
  else next.delete(key)
  loadingLayerKeys.value = next
}

const setLayerFeatureCount = (key, value) => {
  const next = { ...layerFeatureCounts.value }
  if (value === null || value === undefined) delete next[key]
  else next[key] = Number(value)
  layerFeatureCounts.value = next
}

const setParcelSourceLoading = (key, loading) => {
  const next = new Set(loadingParcelSourceKeys.value)
  if (loading) next.add(key)
  else next.delete(key)
  loadingParcelSourceKeys.value = next
}

const currentBboxArray = () => {
  const bounds = map.getBounds()

  return [
    bounds.getWest(),
    bounds.getSouth(),
    bounds.getEast(),
    bounds.getNorth()
  ]
}

const formatBbox = (bbox) => bbox.map((coordinate) => coordinate.toFixed(6)).join(',')
const currentMapCenterPoint = () => {
  const center = map.getCenter()

  return [center.lng, center.lat]
}
const validPoint = (point) => Array.isArray(point) && point.length === 2 && point.every((coordinate) => Number.isFinite(Number(coordinate)))
function validParcelGeometry(geometry) {
  if (!geometry || typeof geometry !== 'object') return false
  if (!['Polygon', 'MultiPolygon'].includes(geometry.type)) return false

  return Array.isArray(geometry.coordinates) && geometry.coordinates.length > 0
}
const formatPoint = (point) => point.map((coordinate) => Number(coordinate).toFixed(6)).join(',')
const fitToFeatureCollection = (data) => {
  if (!mapReady) return

  const bounds = featureCollectionBounds(data)
  if (!bounds) return

  map.fitBounds([[bounds[0], bounds[1]], [bounds[2], bounds[3]]], {
    padding: { top: 80, right: 80, bottom: 80, left: 80 },
    maxZoom: 16,
    duration: 500
  })
}

const featureCollectionBounds = (data) => {
  const points = (data?.features || []).flatMap((feature) => coordinatePairs(feature.geometry))
  if (points.length === 0) return null

  const longitudes = points.map((point) => point[0])
  const latitudes = points.map((point) => point[1])
  let west = Math.min(...longitudes)
  let east = Math.max(...longitudes)
  let south = Math.min(...latitudes)
  let north = Math.max(...latitudes)
  if (west === east) {
    west -= 0.002
    east += 0.002
  }
  if (south === north) {
    south -= 0.002
    north += 0.002
  }

  return [west, south, east, north]
}

const parcelFeatureCenter = (feature) => {
  const normalized = normalizedLandportProperties(feature.properties || {})
  if (Number.isFinite(Number(normalized.centroidLongitude)) && Number.isFinite(Number(normalized.centroidLatitude))) {
    return [Number(normalized.centroidLongitude), Number(normalized.centroidLatitude)]
  }

  const points = coordinatePairs(feature.geometry)
  if (points.length === 0) return null

  const longitude = points.reduce((sum, point) => sum + point[0], 0) / points.length
  const latitude = points.reduce((sum, point) => sum + point[1], 0) / points.length

  return [longitude, latitude]
}

const coordinatePairs = (geometry) => {
  if (!geometry || typeof geometry !== 'object') return []

  const collect = (value) => {
    if (!Array.isArray(value)) return []
    if (value.length >= 2 && Number.isFinite(Number(value[0])) && Number.isFinite(Number(value[1]))) {
      return [[Number(value[0]), Number(value[1])]]
    }

    return value.flatMap((entry) => collect(entry))
  }

  return collect(geometry.coordinates)
}

const paddedBbox = (bbox, layer) => {
  const [west, south, east, north] = bbox
  const minimumPadding = layerMinimumPadding(layer)
  const longitudePadding = Math.max((east - west) * layerPaddingRatio(layer), minimumPadding)
  const latitudePadding = Math.max((north - south) * layerPaddingRatio(layer), minimumPadding)

  return [
    Math.max(-180, west - longitudePadding),
    Math.max(-90, south - latitudePadding),
    Math.min(180, east + longitudePadding),
    Math.min(90, north + latitudePadding)
  ]
}

const layerPaddingRatio = (layer) => layer.geometryType === 'point' ? 1.25 : 0.65
const layerMinimumPadding = (layer) => {
  if (layer.key === 'fema_flood_hazard_zones') return 0.02
  if (layer.geometryType === 'polygon') return 0.03
  if (layer.geometryType === 'line') return 0.05

  return 0.05
}
const paddedParcelSourceBbox = (bbox) => {
  const [west, south, east, north] = bbox
  const longitudePadding = Math.min(Math.max((east - west) * 0.2, 0.002), 0.03)
  const latitudePadding = Math.min(Math.max((north - south) * 0.2, 0.002), 0.03)

  return [
    Math.max(-180, west - longitudePadding),
    Math.max(-90, south - latitudePadding),
    Math.min(180, east + longitudePadding),
    Math.min(90, north + latitudePadding)
  ]
}
const loadedStateCanSatisfyViewport = (loadedState, viewportBbox) => {
  if (loadedState.complete) return true

  return bboxArea(viewportBbox) >= bboxArea(loadedState.viewportBbox) * 0.65
}
const featureCollectionLimited = (data, fallbackLimit) => {
  const returned = Number(data?.properties?.landportReturnedFeatures || data?.features?.length || 0)
  const limit = Number(data?.properties?.landportFeatureLimit || fallbackLimit || 0)

  return limit > 0 ? returned >= limit : false
}
const bboxArea = (bbox) => {
  const [west, south, east, north] = bbox

  return Math.max(east - west, 0) * Math.max(north - south, 0)
}
const parcelSourceBboxTooWide = (bbox) => {
  const [west, south, east, north] = bbox

  return (east - west) > 0.25 || (north - south) > 0.25
}

const bboxContains = (outer, inner) => (
  outer[0] <= inner[0] &&
  outer[1] <= inner[1] &&
  outer[2] >= inner[2] &&
  outer[3] >= inner[3]
)

const sourceId = (layer) => `landport-source-${layer.key}`
const pointLayerUsesMarkerIcon = (layer) => markerIconsReady && layer.geometryType === 'point' && layerUsesMarkerIcon(layer)
const layerIds = (layer) => {
  if (isRasterLayer(layer)) return [ `landport-layer-${layer.key}-raster` ]
  if (layer.geometryType === 'polygon') return [ `landport-layer-${layer.key}-fill`, `landport-layer-${layer.key}-line` ]
  if (pointLayerUsesMarkerIcon(layer) && isDataCenterLayer(layer)) return [ `landport-layer-${layer.key}-symbol`, `landport-layer-${layer.key}-label` ]
  if (pointLayerUsesMarkerIcon(layer)) return [ `landport-layer-${layer.key}-symbol` ]
  if (layer.geometryType === 'point' && isDataCenterLayer(layer)) return [ `landport-layer-${layer.key}-circle`, `landport-layer-${layer.key}-label` ]
  if (layer.geometryType === 'point') return [ `landport-layer-${layer.key}-circle` ]

  return [ `landport-layer-${layer.key}-line` ]
}
const parcelSourceSourceId = (parcelSource) => `landport-parcel-source-${parcelSource.key}`
const parcelSourceLayerIds = (parcelSource) => [ `landport-parcel-layer-${parcelSource.key}-fill`, `landport-parcel-layer-${parcelSource.key}-line`, `landport-parcel-layer-${parcelSource.key}-circle` ]
const emptyFeatureCollection = () => ({ type: 'FeatureCollection', features: [] })
const isLayerActive = (key) => activeLayerKeys.value.has(key)
const isParcelSourceActive = (key) => activeParcelSourceKeys.value.has(key)
const canRenderLayer = (layer) => layer.enabled !== false && layer.sourceType !== 'paid_provider'
const isRasterLayer = (layer) => layer.sourceType === 'arcgis_image' || layer.geometryType === 'raster'
const isDataCenterLayer = (layer) => ['data_center_sites', 'operational_data_centers'].includes(layer.key)
const isManualParcelSource = (source) => source.sourceType === 'paid_provider' || source.query?.paidManualOnly || source.metadata?.manualOnly
const canAutoRenderParcelSource = (source) => source.enabled !== false && !isManualParcelSource(source)
function layerBelowMinZoom(layer) {
  return mapReady && isLayerActive(layer.key) && Number(layer.minZoom || 0) > 0 && mapZoom.value < Number(layer.minZoom)
}
const layerStatusLabel = (layer) => {
  if (!canRenderLayer(layer)) return 'Planned'
  if (layerBelowMinZoom(layer)) return 'Zoom in'
  if (loadingLayerKeys.value.has(layer.key)) return 'Loading'
  if (layerErrors.value[layer.key]) return 'Issue'
  if (layerHints.value[layer.key]) return 'Limited'

  return isLayerActive(layer.key) ? 'Live' : 'Ready'
}
const layerStatusTone = (layer) => {
  if (!canRenderLayer(layer)) return 'slate'
  if (layerBelowMinZoom(layer)) return 'amber'
  if (layerErrors.value[layer.key]) return 'amber'
  if (layerHints.value[layer.key]) return 'amber'

  return isLayerActive(layer.key) ? 'green' : 'blue'
}
const parcelSourceStatusLabel = (source) => {
  if (isManualParcelSource(source)) return 'Manual'
  if (!source.enabled) return 'Planned'
  if (loadingParcelSourceKeys.value.has(source.key)) return 'Loading'
  if (parcelSourceErrors.value[source.key]) return 'Issue'
  if (parcelSourceHints.value[source.key]) return 'Zoom in'

  return isParcelSourceActive(source.key) ? 'Live' : 'Ready'
}
const parcelSourceStatusTone = (source) => {
  if (isManualParcelSource(source)) return 'slate'
  if (!source.enabled) return 'slate'
  if (parcelSourceErrors.value[source.key]) return 'amber'
  if (parcelSourceHints.value[source.key]) return 'amber'

  return isParcelSourceActive(source.key) ? 'green' : 'blue'
}
const categoryIcon = (category) => ({
  demographics: Users,
  power: Zap,
  gas: Flame,
  pipelines: Flame,
  grid: Zap,
  environmental: Waves,
  water: Waves,
  transportation: MapPinned,
  fiber: Satellite,
  land: Layers,
  terrain: MapPinned,
  paid: Building2,
  zoning: Building2
})[category] || Layers
const categoryRenderableCount = (category) => category.layers.filter((layer) => canRenderLayer(layer)).length
const categoryActiveCount = (category) => category.layers.filter((layer) => canRenderLayer(layer) && isLayerActive(layer.key)).length
const layerColor = (layer) => layer.style?.lineColor || layer.style?.circleColor || layer.style?.fillColor || '#0f766e'
const layerDataSummary = (layer) => {
  const details = [layer.provider]
  if (Number(layer.minZoom || 0) > 0) details.push(`zoom ${layer.minZoom}+`)
  if (layer.health?.label && !['Source managed'].includes(layer.health.label)) details.push(layer.health.label)
  if (layer.metadata?.status === 'planned') details.push('planned')
  if (layer.metadata?.coverage) details.push(layer.metadata.coverage)
  if (layer.metadata?.locationConfidence) details.push(String(layer.metadata.locationConfidence).replace(/_/g, ' '))
  if (layer.health?.coverage && layer.health.coverage !== layer.metadata?.coverage) details.push(layer.health.coverage)
  if (layer.health?.featureCount) details.push(`${formatLayerCount(layer.health.featureCount)} cached`)
  if (isLayerActive(layer.key) && layerFeatureCounts.value[layer.key] !== undefined) details.push(`${formatLayerCount(layerFeatureCounts.value[layer.key])} loaded`)

  return details.filter(Boolean).join(' · ')
}
const formatLayerCount = (value) => `${new Intl.NumberFormat().format(Number(value || 0))} feature${Number(value) === 1 ? '' : 's'}`
const initials = (name) => name.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase()
const formatNumber = (value) => new Intl.NumberFormat().format(Number(value || 0))
const formatShortList = (values) => {
  if (values.length <= 2) return values.join(' and ')

  return `${values.slice(0, 2).join(', ')} and ${values.length - 2} more`
}
const formatScore = (value) => {
  const number = Number(value)
  if (!Number.isFinite(number)) return 'Not available'

  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(number)} score`
}
const formatCurrency = (value) => {
  const number = Number(value)
  if (!Number.isFinite(number)) return 'Not available'

  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(number)
}
const formatMetric = (value, format) => {
  if (value === null || value === undefined) return 'Not available'
  if (format === 'currency') return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(value))

  return formatNumber(value)
}
const formatPercent = (value) => value === null || value === undefined ? 'Not available' : `${value}%`
const formatDateTime = (value) => {
  if (!value) return ''

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value))
}
const formatAttribute = (value) => cleanValue(value) || 'Not available'
const truthyFlag = (value) => ['Y', 'YES', 'TRUE', '1'].includes(String(value || '').toUpperCase())
const formatVoltage = (value) => {
  const formatted = formatAttribute(value)
  if (formatted === 'Not available' || /kv/i.test(formatted)) return formatted

  return `${formatted} kV`
}
const formatMetersAsMiles = (value) => {
  const number = Number(value)
  if (!Number.isFinite(number) || number <= 0) return 'Not available'

  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(number / 1609.344)} mi`
}
const formatMegawatts = (value) => {
  const number = Number(value)
  if (!Number.isFinite(number)) return 'Not available'

  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(number)} MW`
}
const formatKilowattsAsMegawatts = (value) => {
  const number = Number(value)
  if (!Number.isFinite(number)) return 'Not available'
  if (String(value).toLowerCase().includes('mw')) return formatMegawatts(number)

  return formatMegawatts(number / 1000)
}
const formatAcres = (value) => {
  const number = Number(value)
  if (!Number.isFinite(number)) return 'Not available'

  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(number)} acres`
}
const formatLngCapacity = (value) => {
  const number = Number(value)
  if (!Number.isFinite(number) || number < 0) return 'Not available'

  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(number)} Bcf/d`
}
const formatSetbacks = (properties) => {
  const setbacks = [
    ['front', propertyValue(properties, 'min_setback_front')],
    ['side', propertyValue(properties, 'min_setback_side')],
    ['rear', propertyValue(properties, 'min_setback_rear')]
  ].map(([label, value]) => cleanValue(value) ? `${label}: ${cleanValue(value)}` : null).filter(Boolean)

  return setbacks.length > 0 ? setbacks.join(', ') : 'Not available'
}
const formatBreakdown = (value) => {
  const parsed = parseStructuredValue(value)
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return 'Not available'

  const entries = Object.entries(parsed)
    .filter(([key, entryValue]) => cleanValue(key) && cleanValue(entryValue))
    .slice(0, 4)

  if (entries.length === 0) return 'Not available'

  return entries.map(([key, entryValue]) => `${key}: ${formatNumber(entryValue)}`).join(', ')
}
const formatProjectList = (value) => {
  const parsed = parseStructuredValue(value)
  if (!Array.isArray(parsed)) return 'Not available'

  const projects = parsed
    .map((project) => {
      const name = cleanValue(project.projectName) || cleanValue(project.queueId)
      const capacity = formatMegawatts(project.capacityMw)
      const fuel = cleanValue(project.fuelType)

      return [name, capacity !== 'Not available' ? capacity : null, fuel].filter(Boolean).join(' / ')
    })
    .filter(Boolean)
    .slice(0, 3)

  return projects.length > 0 ? projects.join('; ') : 'Not available'
}
const formatCustomerList = (value) => {
  const parsed = parseStructuredValue(value)
  if (Array.isArray(parsed)) {
    const customers = parsed.map((item) => cleanValue(item)).filter(Boolean).slice(0, 5)
    return customers.length > 0 ? customers.join(', ') : 'Not available'
  }
  if (parsed && typeof parsed === 'object') {
    const customers = Object.values(parsed).map((item) => cleanValue(item)).filter(Boolean).slice(0, 5)
    return customers.length > 0 ? customers.join(', ') : 'Not available'
  }

  return cleanValue(parsed) || 'Not available'
}
const parseStructuredValue = (value) => {
  if (!value || typeof value !== 'string') return value

  const trimmed = value.trim()
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value

  try {
    return JSON.parse(trimmed)
  } catch (_error) {
    return value
  }
}
const formatAttributeDate = (value) => {
  const formatted = cleanValue(value)
  if (!formatted) return 'Not available'

  const date = Number.isFinite(Number(formatted)) && Number(formatted) > 10_000_000_000 ? new Date(Number(formatted)) : new Date(formatted)
  if (Number.isNaN(date.getTime())) return formatted

  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' }).format(date)
}
const titleize = (value) => String(value || '')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase())
const propertyValue = (properties, ...keys) => {
  const entries = Object.entries(properties || {})
  const normalized = new globalThis.Map(entries.map(([key, value]) => [key.toLowerCase(), value]))

  for (const key of keys) {
    const value = normalized.get(String(key).toLowerCase())
    if (cleanValue(value)) return value
  }

  return null
}
const normalizedLandportProperties = (properties) => {
  const raw = properties?.landport
  if (!raw) return {}
  if (typeof raw === 'object') return raw

  try {
    return JSON.parse(raw)
  } catch {
    return {}
  }
}
const cleanValue = (value) => {
  if (value === null || value === undefined) return ''

  const text = String(value).trim()
  if (text.length === 0) return ''
  if (['null', 'undefined', 'nan', '-999999'].includes(text.toLowerCase())) return ''

  return text
}
const featureCoordinates = (feature, lngLat) => {
  const geometryCoordinates = feature.geometry?.coordinates
  if (Array.isArray(geometryCoordinates) && Number.isFinite(Number(geometryCoordinates[0])) && Number.isFinite(Number(geometryCoordinates[1]))) {
    return [Number(geometryCoordinates[0]), Number(geometryCoordinates[1])]
  }
  if (lngLat && Number.isFinite(Number(lngLat.lng)) && Number.isFinite(Number(lngLat.lat))) {
    return [Number(lngLat.lng), Number(lngLat.lat)]
  }

  return null
}
const formatCoordinatePair = (coordinates) => (
  coordinates?.length === 2 ? `${coordinates[1].toFixed(5)}, ${coordinates[0].toFixed(5)}` : 'Not available'
)
const externalFeatureId = (layer, feature, coordinates) => {
  const properties = feature.properties || {}
  const explicitId = [
    feature.id,
    propertyValue(properties, 'OBJECTID'),
    propertyValue(properties, 'FID'),
    propertyValue(properties, 'ID'),
    propertyValue(properties, 'GLOBALID')
  ].find((value) => cleanValue(value))

  if (explicitId) return cleanValue(explicitId)

  const name = cleanValue(propertyValue(properties, 'NAME')) || layer.key
  const coordinateKey = coordinates?.length === 2 ? coordinates.map((coordinate) => Number(coordinate).toFixed(5)).join(',') : 'unknown'

  return `${name}:${coordinateKey}`
}
const stableFeatureKey = (feature, coordinates) => {
  const properties = feature.properties || {}
  const propertyKey = cleanValue(propertyValue(properties, 'OBJECTID', 'FID', 'ID')) || JSON.stringify(properties).slice(0, 80)
  const coordinateKey = coordinates?.length === 2 ? coordinates.map((coordinate) => Number(coordinate).toFixed(5)).join(',') : 'unknown'

  return `${propertyKey}:${coordinateKey}`
}
const opportunityName = (id) => trackableOpportunities.value.find((point) => point.id === id)?.name || 'selected opportunity'
const infrastructureFeatureName = (layer, properties) => {
  if (layer.key === 'natural_gas_pipelines_eia') {
    const operator = cleanValue(propertyValue(properties, 'Operator', 'OPERATOR', 'OWNER'))
    if (operator) return operator

    const type = cleanValue(propertyValue(properties, 'TYPEPIPE', 'TYPE'))
    return type ? `${type} pipeline` : 'Natural gas pipeline'
  }
  if (layer.key === 'lng_terminals_eia') {
    return cleanValue(propertyValue(properties, 'NAME')) || cleanValue(propertyValue(properties, 'OWNER')) || 'LNG terminal'
  }
  if (['gas_compressor_stations_hifld', 'gas_processing_plants_hifld', 'gas_storage_hifld'].includes(layer.key)) {
    return cleanValue(propertyValue(properties, 'NAME')) || cleanValue(propertyValue(properties, 'OPERATOR', 'OWNER', 'COMPNAME', 'PIPECO')) || layer.name
  }
  if (['crude_oil_pipelines_eia', 'petroleum_product_pipelines_eia', 'ngl_pipelines_eia'].includes(layer.key)) {
    return cleanValue(propertyValue(properties, 'Pipename', 'PIPELINE', 'NAME')) || cleanValue(propertyValue(properties, 'Opername', 'OPERATOR')) || layer.name
  }
  if (['transmission_lines_hifld', 'transmission_lines_cached'].includes(layer.key)) {
    const owner = cleanValue(propertyValue(properties, 'OWNER'))
    const voltage = formatVoltage(propertyValue(properties, 'VOLTAGE'))
    if (owner && voltage !== 'Not available') return `${owner} ${voltage} line`
    if (owner) return `${owner} transmission line`

    return 'Transmission line'
  }
  if (layer.key === 'power_plants_hifld') {
    return cleanValue(propertyValue(properties, 'NAME', 'PLANT_NAME')) || 'Power plant'
  }
  if (['electric_substations_hifld', 'electric_substations_cached'].includes(layer.key)) {
    return cleanValue(propertyValue(properties, 'NAME')) || 'Unnamed substation'
  }
  if (isHostingCapacityLayerKey(layer.key)) {
    return cleanValue(propertyValue(properties, 'MAP_NAME', 'FEEDERNUM', 'GRID_ID', 'SUBSTATION', 'Name')) || 'Hosting capacity feature'
  }
  if (layer.key === 'control_areas_hifld') {
    return cleanValue(propertyValue(properties, 'NAME')) || 'Balancing authority'
  }
  if (layer.key === 'egrid_subregions_epa') {
    const subregion = cleanValue(propertyValue(properties, 'Subregion'))
    return subregion ? `eGRID ${subregion}` : 'eGRID subregion'
  }
  if (layer.key === 'nietc_corridors_doe') {
    return cleanValue(propertyValue(properties, 'Name', 'NAME')) || 'NIETC corridor'
  }
  if (layer.key === 'electric_retail_service_territories_hifld') {
    return cleanValue(propertyValue(properties, 'NAME', 'HOLDING_CO')) || 'Electric service territory'
  }
  if (layer.key === 'interconnection_queue_projects') {
    return cleanValue(propertyValue(properties, 'project_name')) || cleanValue(propertyValue(properties, 'queue_id')) || 'Interconnection queue project'
  }
  if (['interconnection_queue_county_summaries', 'lbnl_interconnection_queue_counties'].includes(layer.key)) {
    const county = cleanValue(propertyValue(properties, 'county_name'))
    const state = cleanValue(propertyValue(properties, 'state_code'))

    return [county, state].filter(Boolean).join(', ') || 'Queue county summary'
  }
  if (['data_center_sites', 'operational_data_centers'].includes(layer.key)) {
    return cleanValue(propertyValue(properties, 'name')) || cleanValue(propertyValue(properties, 'operator', 'company')) || 'Data center'
  }
  if (layer.key === 'internet_exchanges') {
    return cleanValue(propertyValue(properties, 'name')) || 'Internet exchange'
  }
  if (layer.key === 'cable_landing_stations_osm') {
    return cleanValue(propertyValue(properties, 'name')) || cleanValue(propertyValue(properties, 'cable_names')) || 'Cable landing station'
  }
  if (layer.key === 'fema_flood_hazard_zones') {
    const zone = cleanValue(propertyValue(properties, 'FLD_ZONE'))
    const subtype = cleanValue(propertyValue(properties, 'ZONE_SUBTY'))
    if (zone && subtype) return `Flood zone ${zone} - ${subtype}`
    if (zone) return `Flood zone ${zone}`

    return 'Flood hazard zone'
  }
  if (['fema_national_risk_index_counties', 'fema_national_risk_index_tracts'].includes(layer.key)) {
    const county = cleanValue(propertyValue(properties, 'COUNTY'))
    const state = cleanValue(propertyValue(properties, 'STATEABBRV'))
    const tract = cleanValue(propertyValue(properties, 'TRACT'))

    return [county, state, tract ? `Tract ${tract}` : null].filter(Boolean).join(', ') || 'Natural hazard risk'
  }
  if (layer.key === 'epa_echo_facilities') {
    return cleanValue(propertyValue(properties, 'FAC_NAME')) || 'EPA regulated facility'
  }
  if (layer.key === 'nwi_wetlands') return cleanValue(propertyValue(properties, 'WETLAND_TYPE', 'ATTRIBUTE')) || 'Wetland feature'
  if (layer.key === 'usfws_critical_habitat') return cleanValue(propertyValue(properties, 'comname', 'sciname', 'unitname')) || 'Critical habitat'
  if (layer.key === 'padus_protected_areas') return cleanValue(propertyValue(properties, 'Unit_Nm', 'Mang_Name', 'Category')) || 'Protected area'
  if (layer.key === 'epa_nonattainment_areas') return cleanValue(propertyValue(properties, 'area_name', 'pollutant_name')) || 'Air nonattainment area'
  if (layer.key === 'opportunity_zones_hud') return cleanValue(propertyValue(properties, 'GEOID10')) || 'Opportunity Zone'
  if (layer.key === 'foreign_trade_zones_ftz') return cleanValue(propertyValue(properties, 'FTZSA')) || cleanValue(propertyValue(properties, 'COUNTY')) || 'Foreign Trade Zone service area'
  if (layer.key === 'foreign_trade_zone_sites_ftz') return cleanValue(propertyValue(properties, 'Grantee')) || cleanValue(propertyValue(properties, 'Num')) || 'Foreign Trade Zone site'
  if (layer.key === 'enterprise_zones_hud') return cleanValue(propertyValue(properties, 'FULLNAME', 'NAME')) || 'Enterprise / empowerment zone'
  if (layer.key === 'tx_groundwater_wells_twdb') return cleanValue(propertyValue(properties, 'StateWellNumber')) || cleanValue(propertyValue(properties, 'OwnerName')) || 'TX groundwater well'
  if (layer.key === 'tx_water_rights_tceq') return cleanValue(propertyValue(properties, 'HOLDER_NAME')) || cleanValue(propertyValue(properties, 'DOCUMENT_NUM')) || 'TX water right'
  if (layer.key?.startsWith('census_')) {
    return cleanValue(propertyValue(properties, 'NAME')) || cleanValue(propertyValue(properties, 'GEOID')) || INFRASTRUCTURE_DETAIL_LAYERS[layer.key]?.label || 'Census geography'
  }

  return cleanValue(propertyValue(properties, 'NAME', 'TITLE', 'LABEL')) || layer.name || 'Map feature'
}
const infrastructureSubtitle = (layer, properties) => {
  if (layer.key === 'natural_gas_pipelines_eia') {
    const type = cleanValue(propertyValue(properties, 'TYPEPIPE', 'TYPE'))
    const status = cleanValue(propertyValue(properties, 'Status', 'STATUS'))

    return [type, status, layer.provider].filter(Boolean).join(' / ') || layer.name
  }
  if (layer.key === 'lng_terminals_eia') {
    const direction = cleanValue(propertyValue(properties, 'TYPE'))
    const status = cleanValue(propertyValue(properties, 'STATUS'))
    const capacity = formatLngCapacity(propertyValue(properties, 'CURRENTCAP'))

    return [direction, status, capacity !== 'Not available' ? capacity : null, layer.provider].filter(Boolean).join(' / ') || layer.name
  }
  if (['gas_compressor_stations_hifld', 'gas_processing_plants_hifld', 'gas_storage_hifld'].includes(layer.key)) {
    const operator = cleanValue(propertyValue(properties, 'OPERATOR', 'OWNER', 'COMPNAME', 'PIPECO'))
    const status = cleanValue(propertyValue(properties, 'STATUS'))

    return [operator, status, layer.provider].filter(Boolean).join(' / ') || layer.name
  }
  if (['crude_oil_pipelines_eia', 'petroleum_product_pipelines_eia', 'ngl_pipelines_eia'].includes(layer.key)) {
    const operator = cleanValue(propertyValue(properties, 'Opername', 'OPERATOR'))
    const length = formatMetersAsMiles(propertyValue(properties, 'Shape__Length', 'Shape_Leng'))

    return [operator, length !== 'Not available' ? length : null, layer.provider].filter(Boolean).join(' / ') || layer.name
  }
  if (['transmission_lines_hifld', 'transmission_lines_cached'].includes(layer.key)) {
    const voltage = formatVoltage(propertyValue(properties, 'VOLTAGE'))
    const status = cleanValue(propertyValue(properties, 'STATUS'))
    const type = cleanValue(propertyValue(properties, 'TYPE', 'VOLT_CLASS'))

    return [voltage !== 'Not available' ? voltage : null, type, status, layer.provider].filter(Boolean).join(' / ') || layer.name
  }
  if (isHostingCapacityLayerKey(layer.key)) {
    const remaining = propertyValue(properties, 'available_mw', 'availableMw', 'LIMIT_VAL', 'MAX_HCLOAD', 'HCLOAD', 'MaxHostingCapacity', 'MinHostingCapacity', 'HCMin', 'HCMax')
    const capacity = remaining === null ? formatKilowattsAsMegawatts(propertyValue(properties, 'Sum_Hosting_Capacity_Remaining_kW', 'Max_Hosting_Capacity_Remaining_kW', 'FeederPV', 'DistZonePV')) : formatMegawatts(remaining)

    return [capacity !== 'Not available' ? `${capacity} remaining` : null, layer.provider].filter(Boolean).join(' / ') || layer.name
  }
  if (layer.key === 'control_areas_hifld') {
    const peak = formatMegawatts(propertyValue(properties, 'PEAK_LOAD'))
    return [peak !== 'Not available' ? `Peak ${peak}` : null, layer.provider].filter(Boolean).join(' / ') || layer.name
  }
  if (layer.key === 'egrid_subregions_epa') {
    return [cleanValue(propertyValue(properties, 'Subregion')), layer.provider].filter(Boolean).join(' / ') || layer.name
  }
  if (layer.key === 'nietc_corridors_doe') {
    const phase = cleanValue(propertyValue(properties, 'Phase', 'PHASE'))
    const area = formatAcres(propertyValue(properties, 'AreaAcres'))

    return [phase, area !== 'Not available' ? area : null, layer.provider].filter(Boolean).join(' / ') || layer.name
  }
  if (layer.key === 'electric_retail_service_territories_hifld') {
    const controlArea = cleanValue(propertyValue(properties, 'CNTRL_AREA'))
    const planArea = cleanValue(propertyValue(properties, 'PLAN_AREA'))

    return [controlArea, planArea, layer.provider].filter(Boolean).join(' / ') || layer.name
  }
  if (layer.key === 'interconnection_queue_projects') {
    const capacity = formatMegawatts(propertyValue(properties, 'capacity_mw'))
    const fuel = cleanValue(propertyValue(properties, 'fuel_type'))
    const status = cleanValue(propertyValue(properties, 'status'))

    return [capacity !== 'Not available' ? capacity : null, fuel, status, layer.provider].filter(Boolean).join(' / ') || layer.name
  }
  if (['interconnection_queue_county_summaries', 'lbnl_interconnection_queue_counties'].includes(layer.key)) {
    const count = cleanValue(propertyValue(properties, 'project_count'))
    const capacity = formatMegawatts(propertyValue(properties, 'total_mw'))

    return [`${count || 0} project${Number(count) === 1 ? '' : 's'}`, capacity !== 'Not available' ? capacity : null, 'county-level'].filter(Boolean).join(' / ')
  }
  if (['data_center_sites', 'operational_data_centers'].includes(layer.key)) {
    const status = cleanValue(propertyValue(properties, 'status'))
    const operator = cleanValue(propertyValue(properties, 'operator', 'company'))
    const facilityType = cleanValue(propertyValue(properties, 'facility_type'))
    const city = cleanValue(propertyValue(properties, 'city'))

    return [status ? titleize(status) : null, facilityType ? titleize(facilityType) : null, operator, city].filter(Boolean).join(' / ') || layer.name
  }
  if (layer.key === 'internet_exchanges') {
    const participants = cleanValue(propertyValue(properties, 'participant_count', 'net_count'))
    const location = [cleanValue(propertyValue(properties, 'city')), cleanValue(propertyValue(properties, 'state'))].filter(Boolean).join(', ')

    return [participants ? `${formatNumber(participants)} participants` : null, location, layer.provider].filter(Boolean).join(' / ') || layer.name
  }
  if (layer.key === 'cable_landing_stations_osm') {
    const operator = cleanValue(propertyValue(properties, 'operator'))
    const cables = cleanValue(propertyValue(properties, 'cable_names'))

    return [operator, cables, layer.provider].filter(Boolean).join(' / ') || layer.name
  }
  if (layer.key === 'fema_flood_hazard_zones') {
    const sfha = cleanValue(propertyValue(properties, 'SFHA_TF'))
    const subtype = cleanValue(propertyValue(properties, 'ZONE_SUBTY'))

    return [sfha ? `SFHA ${sfha}` : null, subtype, layer.provider].filter(Boolean).join(' / ') || layer.name
  }
  if (['fema_national_risk_index_counties', 'fema_national_risk_index_tracts'].includes(layer.key)) {
    const rating = cleanValue(propertyValue(properties, 'RISK_RATNG'))
    const score = formatScore(propertyValue(properties, 'RISK_SCORE'))

    return [rating, score !== 'Not available' ? score : null, layer.provider].filter(Boolean).join(' / ') || layer.name
  }
  if (layer.key === 'epa_echo_facilities') {
    const status = cleanValue(propertyValue(properties, 'FAC_CURR_COMPLIANCE_STATUS'))
    const programs = ['AIR', 'NPDES', 'SDWIS', 'RCRA', 'TRI', 'GHG'].filter((program) => truthyFlag(propertyValue(properties, `${program}_FLAG`))).join(', ')

    return [programs, status, layer.provider].filter(Boolean).join(' / ') || layer.name
  }
  if (layer.key === 'nwi_wetlands') return [cleanValue(propertyValue(properties, 'ATTRIBUTE')), layer.provider].filter(Boolean).join(' / ') || layer.name
  if (layer.key === 'usfws_critical_habitat') return [cleanValue(propertyValue(properties, 'status', 'listing_status')), cleanValue(propertyValue(properties, 'unitname', 'unit')), layer.provider].filter(Boolean).join(' / ') || layer.name
  if (layer.key === 'padus_protected_areas') return [cleanValue(propertyValue(properties, 'Pub_Access')), cleanValue(propertyValue(properties, 'GAP_Sts')), layer.provider].filter(Boolean).join(' / ') || layer.name
  if (layer.key === 'epa_nonattainment_areas') return [cleanValue(propertyValue(properties, 'pollutant_name')), cleanValue(propertyValue(properties, 'current_status')), cleanValue(propertyValue(properties, 'classification')), layer.provider].filter(Boolean).join(' / ') || layer.name
  if (layer.key === 'opportunity_zones_hud') return [cleanValue(propertyValue(properties, 'STUSAB', 'STATE_NAME')), cleanValue(propertyValue(properties, 'Rural')) ? `Rural ${cleanValue(propertyValue(properties, 'Rural'))}` : null, layer.provider].filter(Boolean).join(' / ') || layer.name
  if (layer.key === 'foreign_trade_zones_ftz') return [cleanValue(propertyValue(properties, 'COUNTY')), cleanValue(propertyValue(properties, 'Contact')), layer.provider].filter(Boolean).join(' / ') || layer.name
  if (layer.key === 'foreign_trade_zone_sites_ftz') return [cleanValue(propertyValue(properties, 'Type')), cleanValue(propertyValue(properties, 'CityStZip')), layer.provider].filter(Boolean).join(' / ') || layer.name
  if (layer.key === 'enterprise_zones_hud') return [cleanValue(propertyValue(properties, 'TYPE')), cleanValue(propertyValue(properties, 'STATEABBR')), layer.provider].filter(Boolean).join(' / ') || layer.name
  if (layer.key === 'tx_groundwater_wells_twdb') return [cleanValue(propertyValue(properties, 'AquiferCodeName')), cleanValue(propertyValue(properties, 'PrimaryWaterUse')), cleanValue(propertyValue(properties, 'CountyName'))].filter(Boolean).join(' / ') || layer.name
  if (layer.key === 'tx_water_rights_tceq') return [cleanValue(propertyValue(properties, 'STATUS')), cleanValue(propertyValue(properties, 'WATERBODY')), cleanValue(propertyValue(properties, 'MAIN_PURPOSE'))].filter(Boolean).join(' / ') || layer.name
  if (layer.key?.startsWith('census_')) {
    const geoid = cleanValue(propertyValue(properties, 'GEOID'))
    const county = cleanValue(propertyValue(properties, 'COUNTY'))

    return [geoid ? `GEOID ${geoid}` : null, county ? `County ${county}` : null, layer.provider].filter(Boolean).join(' / ') || layer.name
  }

  const city = cleanValue(propertyValue(properties, 'CITY'))
  const state = cleanValue(propertyValue(properties, 'STATE'))
  const location = [city, state].filter(Boolean).join(', ')

  return [location, layer.provider].filter(Boolean).join(' / ') || layer.name
}

function matchesMapFilters(record) {
  const matchesStage = selectedStage.value === 'all' || record.stage === selectedStage.value
  const matchesAction = !needsActionOnly.value || ['attention', 'blocked', 'overdue', 'stale', 'incomplete'].includes(record.health?.state)

  return matchesStage && matchesAction
}

const markerToneClass = (tone) => ({
  green: 'landport-map-marker--green',
  blue: 'landport-map-marker--blue',
  amber: 'landport-map-marker--amber',
  red: 'landport-map-marker--red',
  violet: 'landport-map-marker--violet',
  slate: 'landport-map-marker--slate'
})[tone] || 'landport-map-marker--slate'
const isSelectedMarker = (kind, record) => selectedRecord.value?.kind === kind && selectedRecord.value?.id === record.id
const escapeHtml = (value) => String(value || '').replace(/[&<>"']/g, (character) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#039;'
})[character])
</script>

<style>
.map-loader-fade-leave-active {
  transition: opacity 0.4s ease;
}

.map-loader-fade-leave-from {
  opacity: 1;
}

.map-loader-fade-leave-to {
  opacity: 0;
}
</style>
